import React, { useEffect, useRef, useState, useMemo } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import ReactSlider from "react-slider";
import Header from "./Header";
import PropertyList from "./PropertyListCaroline";
import ParcelFilters from "./ParcelFilters";
import { jsPDF } from "jspdf";
import * as XLSX from "xlsx";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import * as turf from "@turf/turf";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import "mapbox-gl/dist/mapbox-gl.css";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";

mapboxgl.accessToken =
    "pk.eyJ1IjoiYWJoaWppdGgxMjc5IiwiYSI6ImNtNGNqdzE4NDA2MGoyaXFwdXh6cjVqMWQifQ.OpRKi-Zt3NIOjXFz-hd-DA";

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        console.error("NCComponent Error:", error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return <div style={{ padding: "20px", color: "red" }}>Error: {this.state.error?.message}</div>;
        }
        return this.props.children;
    }
}

const NCComponent = () => {
    const navigate = useNavigate();
    // const authToken = localStorage.getItem("authToken");
    const [authToken, setAuthToken] = useState(() => localStorage.getItem("authToken"));
    const mapContainerRef = useRef(null);
    const mapRef = useRef(null);
    const legendControlRef = useRef(null);
    const boundsInitialized = useRef(false);  // Track if bounds have been set for current county
    const vacantFilterTouched = useRef(false);  // Track if vacant filter has ever been changed
    const zoneFilterTouched = useRef(false);  // Track if zone filter has ever been changed
    const listedSaleFilterTouched = useRef(false);  // Track if listed sale filter has ever been changed

    const [mapLoaded, setMapLoaded] = useState(false);
    const [selectedCounty, setSelectedCounty] = useState("all");
    const [countiesList, setCountiesList] = useState([]);
    const [countyCenters, setCountyCenters] = useState({});
    const [filteredParcels, setFilteredParcels] = useState([]);
    const [currentZoom, setCurrentZoom] = useState(6.5);
    const [isLoadingParcel, setIsLoadingParcel] = useState(false);
    const [selectedParcelId, setSelectedParcelId] = useState(null);
    const [selectionMode, setSelectionMode] = useState(false);
    const [selectedRows, setSelectedRows] = useState(() => {
        try {
            const stored = localStorage.getItem("ncSelectedRows");
            return stored ? JSON.parse(stored) : [];
        } catch (e) {
            console.warn("Failed to parse selected rows from localStorage", e);
            return [];
        }
    });
    const [isSubscribed, setIsSubscribed] = useState(
        localStorage.getItem("isSubscribed") === "true"
    );
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    // API data state
    const [apiParcels, setApiParcels] = useState([]);
    const [filteredApiParcels, setFilteredApiParcels] = useState([]);
    const [isLoadingApi, setIsLoadingApi] = useState(false);
    const [totalParcelCount, setTotalParcelCount] = useState(0); // Total count from API

    // Filter states
    const [showPriceFilter, setShowPriceFilter] = useState(false);
    const [showSizeFilter, setShowSizeFilter] = useState(false);
    const [showFloodFilter, setShowFloodFilter] = useState(false);
    const [showZoneFilter, setShowZoneFilter] = useState(false);
    const [showMunicipalityFilter, setShowMunicipalityFilter] = useState(false);
    const [showZestimateFilter, setShowZestimateFilter] = useState(false);
    const [showSpreadFilter, setShowSpreadFilter] = useState(false);
    const [showListedSaleFilter, setShowListedSaleFilter] = useState(false);
    const [showAdjacentParcelFilter, setShowAdjacentParcelFilter] = useState(false);

    // Filter values
    const [priceRange, setPriceRange] = useState([0, 1000000]);
    const [sizeRange, setSizeRange] = useState([0, 100]);
    const [floodRiskFilter, setFloodRiskFilter] = useState("All");
    const [zoneCodeFilter, setZoneCodeFilter] = useState([]);
    const [zestimateRange, setZestimateRange] = useState([0, 1000000]);
    const [spreadRange, setSpreadRange] = useState([-100, 100]);
    const [listedSaleFilter, setListedSaleFilter] = useState("All");
    const [adjacentParcelFilter, setAdjacentParcelFilter] = useState("Suitable Parcels");
    // Sorting state
    const [sortBy, setSortBy] = useState('spreadpercentage');
    const [sortOrder, setSortOrder] = useState('desc');
    const [vacantFilter, setVacantFilter] = useState("All");

    // Slider bounds
    const [sliderMinPrice, setSliderMinPrice] = useState(0);
    const [sliderMaxPrice, setSliderMaxPrice] = useState(1000000);
    const [sliderMinSize, setSliderMinSize] = useState(0);
    const [sliderMaxSize, setSliderMaxSize] = useState(100);
    const [sliderMinZestimate, setSliderMinZestimate] = useState(0);
    const [sliderMaxZestimate, setSliderMaxZestimate] = useState(1000000);
    const [sliderMinSpread, setSliderMinSpread] = useState(-100);
    const [sliderMaxSpread, setSliderMaxSpread] = useState(100);

    // Filter options
    const [floodOptions, setFloodOptions] = useState(["All"]);
    const [listedSaleOptions, setListedSaleOptions] = useState([]);

    // Municipality 
    const [municipalityFilter, setMunicipalityFilter] = useState("All");
    const [municipalityOptions, setMunicipalityOptions] = useState(["All"]);
    const municipalityFilterTouched = useRef(false);

    // Zoning Type (only in parcel mode, not adjacent mode)
    const [zoningTypFilter, setZoningTypFilter] = useState([]);
    const [zoningTypOptions, setZoningTypOptions] = useState([]);
    const [showZoningTypFilter, setShowZoningTypFilter] = useState(false);

    const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
    const [favoriteParcelIds, setFavoriteParcelIds] = useState([]);
    const [favoriteParcels, setFavoriteParcels] = useState([]);
    const [isLoadingFavorites, setIsLoadingFavorites] = useState(false);
    const [favoriteOffset, setFavoriteOffset] = useState(0);
    const [favoriteHasMore, setFavoriteHasMore] = useState(false);
    const [favoriteTotalCount, setFavoriteTotalCount] = useState();
    const [favoritePageCount, setFavoritePageCount] = useState(null);
    const favoriteParcelIdSet = useMemo(() => new Set(favoriteParcelIds), [favoriteParcelIds]);
    const favoriteParcelIdsLower = useMemo(
        () => Array.from(favoriteParcelIdSet).map((id) => String(id).trim().toLowerCase()),
        [favoriteParcelIdSet]
    );
    const favoriteParcelIdsRef = useRef([]);

    useEffect(() => {
        favoriteParcelIdsRef.current = favoriteParcelIds;
    }, [favoriteParcelIds]);

    // Active tab for header
    const [activeTab, setActiveTab] = useState("parcels");

    // Track when slider drag is complete
    const [priceRangeDragComplete, setPriceRangeDragComplete] = useState(0);
    const [zestimateRangeDragComplete, setZestimateRangeDragComplete] = useState(0);
    const [sizeRangeDragComplete, setSizeRangeDragComplete] = useState(0);
    const [spreadRangeDragComplete, setSpreadRangeDragComplete] = useState(0);

    const [zoomBoundaryTrigger, setZoomBoundaryTrigger] = useState(0);
    const prevZoomRef = useRef(currentZoom);
    const minMaxInitialized = useRef(false);
    const [zoneCodes, setZoneCodes] = useState([]);
    const clickPopupRef = useRef(null);
    const clickPopupRequestRef = useRef(0);
    const lastParcelLayerClickRef = useRef({ parcelId: null, time: 0 });
    const hoverPopupRef = useRef(null);

    const wasAboveZoomThresholdRef = useRef(false);
    const selectedCountyRef = useRef("all");  // Track current selected county for event handlers

    const [listWidth, setListWidth] = useState(60); // percentage
    const [isParcelSearchActive, setIsParcelSearchActive] = useState(false);
    // Pagination state for Load More functionality (record-based offset)
    const [currentOffset, setCurrentOffset] = useState(0);
    const [hasMoreData, setHasMoreData] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    // Map style state (light or satellite)
    const [mapStyle, setMapStyle] = useState('light'); // 'light' or 'satellite'
    const [resetParcelSearchSignal, setResetParcelSearchSignal] = useState(0);
    const [adjacentMode, setAdjacentMode] = useState(false);
    const [showDownloadButton, setShowDownloadButton] = useState(false);
    const [favoritesRefreshKey, setFavoritesRefreshKey] = useState(0);

    const latestFetchIdRef = useRef(0);
    const [mapInitError, setMapInitError] = useState(null);
    const filtersClearedRef = useRef(false); // Track when filters are being cleared
    const adjacentModeRef = useRef(adjacentMode);
    const floodRiskFilterTouched = useRef(false);

    // STATE
    const [showSewageMap, setShowSewageMap] = useState(false);
    const contourHoverPopupRef = useRef(null);
    const floodHoverPopupRef = useRef(null);
    const builderProjectsPopupRef = useRef(null);
    const builderOngoingProjectsPopupRef = useRef(null);
    const builderProjectsControlRef = useRef(null);
    const [drawnCoordinates, setDrawnCoordinates] = useState("");
    const [drawnParcelIds, setDrawnParcelIds] = useState([]);
    const [drawnArea, setDrawnArea] = useState(null);
    const [showFemaLayer, setShowFemaLayer] = useState(true);
    const [floodCLickedParcel, setFloodClickedParcel] = useState(false);
    const isDrawingParcelSelectionRef = useRef(false);
    const isBuilderLayerActiveRef = useRef(false);
    const previousBuilderLayerActiveRef = useRef(false);
    const showFavoritesOnlyRef = useRef(showFavoritesOnly);
    const skipBuilderFeatureReloadRef = useRef(false);
    const drawRef = useRef(null);
    const drawControlRef = useRef(null);
    const drawnPolygonRef = useRef(null);

    const [nearbyParcels, setNearbyParcels] = useState([]);
    const [isLoadingNearby, setIsLoadingNearby] = useState(false);
    const [builderLayerActive, setBuilderLayerActive] = useState(false);
    const [builderLayerFeatures, setBuilderLayerFeatures] = useState([]);
    const [isLoadingBuilderLayerFeatures, setIsLoadingBuilderLayerFeatures] = useState(false);
    const [isLoadingMoreNearby, setIsLoadingMoreNearby] = useState(false);
    const [nearbyError, setNearbyError] = useState(null);
    const [nearbyTotalCount, setNearbyTotalCount] = useState(0);
    const [, setNearbyOffset] = useState(0);
    const [nearbyHasMore, setNearbyHasMore] = useState(false);
    const nearbyPolygonRef = useRef(null);
    const nearbyCountyRef = useRef(null);
    const nearbyLoadMoreRef = useRef(null);
    const nearbyOffsetRef = useRef(0);
    const nearbyHasMoreRef = useRef(false);
    const nearbyLoadingRef = useRef(false);
    const NEARBY_PAGE_LIMIT = 250;








    const NC_CENTER = [-78.6443, 35.7877];
    const WAKE_CENTER = [-78.35, 35.79];

    const addOngoingBuilderMarkerImage = (map) => {
        if (!map || map.hasImage("ongoing-builder-marker-icon")) return;

        map.loadImage("/assets/builder.svg", (error, image) => {
            if (error) {
                console.error(error);
                return;
            }

            map.addImage("ongoing-builder-marker-icon", image);
        });
    };

 

    const addBuilderMarkerImage = (map, iconName, assetPath) => {
    if (!map || map.hasImage(iconName)) return;

    const img = new Image();
    
    img.onload = () => {
        const size = 42; 
        const radius = size / 2;
        const iconSize = 20; 
        const offset = (size - iconSize) / 2;

        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');

        // 1. Dynamic Color Configuration
        let backgroundColor, borderColor, glowColor, glowBlur;

        if (iconName === 'ongoing-builder-marker-icon') {
            // Enlightened Builder Style
            // backgroundColor = '#96dbcd'; // Deep industrial slate
            backgroundColor = '#ff8126'; // Deep industrial slate
            borderColor = '#ff8126';     // Bright construction amber
            glowColor = 'transparent';       // Vivid gold glow
            glowBlur = 0;
        } else {
            // Standard Fallback Style (Clean White)
            backgroundColor = '#f0eded'; // Crisp white
            borderColor = '#64748B';     // Neutral gray border
            glowColor = 'transparent';   // No glow
            glowBlur = 0;
        }

        // 2. Apply Glow Settings
        // if (glowBlur > 0) {
        //     ctx.shadowColor = glowColor;
        //     ctx.shadowBlur = glowBlur;
        // }

        // 3. Draw Base Background Circle
        ctx.beginPath();
        ctx.arc(radius, radius, radius - 4, 0, Math.PI * 2);
        ctx.fillStyle = backgroundColor;
        ctx.fill();

        // 4. Turn off shadow for crisp borders/icons
        ctx.shadowBlur = 0; 

        // 5. Draw Border Stroke
        ctx.strokeStyle = borderColor;
        ctx.lineWidth = 1;
        ctx.stroke();

        // 6. Center and render the icon image
        ctx.drawImage(img, offset, offset, iconSize, iconSize);

        // 7. Add to Mapbox Map
        const imageData = ctx.getImageData(0, 0, size, size);
        map.addImage(iconName, imageData);
    };

    img.onerror = (err) => {
        console.error(`Failed to load marker image: ${iconName}`, err);
    };

    img.src = assetPath;
};







 

    const fetchParcelMinMax = async (county) => {
        try {
            const token = localStorage.getItem("authToken");
            if (!token) { handleLogout(); return null; }
            const url = adjacentMode
                ? `https://beta.flowlogic.com/api/assemblage/assemblage-minmax?county=${county}`
                : `https://beta.flowlogic.com/api/parcels/parcel-minmax/?county=${county}`;
            const res = await fetch(url, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            if (res.status === 401) { handleLogout(); return null; }
            if (!res.ok) throw new Error('Failed to fetch min/max');

            return await res.json();
        } catch (err) {
            console.error(' MinMax API error:', err);
            return null;
        }
    };

    useEffect(() => {
        if (!mapRef.current || !mapRef.current.getLayer("parcel-points")) return;

        if (showFemaLayer) {
            // 1. Make the layer visible
            mapRef.current.setLayoutProperty("parcel-points", "visibility", "visible");

            // 2. Apply your dynamic FEMA matching color configuration
            mapRef.current.setPaintProperty("parcel-points", "fill-color", [
                "match",
                ["get", "parcelcustomfemaflood"],
                "X", "#4CAF50",
                "AE", "#FF9800",
                "VE", "#E91E63",
                "A", "#2196F3",
                "#9fa2e7", // Default fallback color
            ]);
            mapRef.current.setPaintProperty("parcel-points", "fill-opacity", 0.45);

        } else {
            // Choose one of two behaviors for when the button is off:

            // Alternative 1: Simply hide it completely
            mapRef.current.setLayoutProperty("parcel-points", "visibility", "none");

            /* 
            // Alternative 2: Instead of hiding it, "refill" it to a generic styling scheme
            mapRef.current.setPaintProperty("parcel-points", "fill-color", "#cccccc"); 
            mapRef.current.setPaintProperty("parcel-points", "fill-opacity", 0.2);
            */
        }
    }, [showFemaLayer]);
    const reloadTilesSafely = (mode) => {
        const map = mapRef.current;
        if (!map) return;

        if (!map.isStyleLoaded()) {
            map.once("styledata", () => reloadTilesSafely(mode));
            return;
        }

        reloadParcelTiles(mode);
    };
    useEffect(() => {
        if (!mapRef.current) return;

        console.log(" Adjacent mode:", adjacentMode);

        reloadTilesSafely(adjacentMode);

        // Restore parcel highlighting after tiles are reloaded
        setTimeout(() => {
            if (selectedCounty && selectedCounty !== "all") {
                updateLeftPanelParcels(selectedCounty);
                console.log(' Restored parcel highlighting after adjacent mode change');
            }
        }, 500);
    }, [adjacentMode]);

    useEffect(() => {
        adjacentModeRef.current = adjacentMode;
    }, [adjacentMode]);

    useEffect(() => {
        showFavoritesOnlyRef.current = showFavoritesOnly;
    }, [showFavoritesOnly]);

    useEffect(() => {
        if (!showFavoritesOnly && !adjacentMode) return;

        builderProjectsControlRef.current?.forceOff();
        setBuilderLayerActive(false);
        setBuilderLayerFeatures([]);
        isBuilderLayerActiveRef.current = false;
    }, [showFavoritesOnly, adjacentMode]);

    // Handle adjacent parcel filter changes
    useEffect(() => {
        if (adjacentParcelFilter !== "All" && adjacentMode && selectedCounty !== "all") {
            // Reload API with adjacent parcel filter
            // fetchApiParcels();

            const isBest = adjacentParcelFilter === "Suitable Parcels" ? 1 : 0;
            // fetchApiParcels(selectedCounty, null, null, null, null, null, null, null, null, null, null, null, null, null, null, null, null, 0, null);
            //  NOW refetch the parcels using the new mode
            fetchApiParcels(
                selectedCounty,
                null, null,   // landValue
                null, null,   // zestimate
                null, null,   // parcelsize
                null, null,   // spread
                null,         // zone
                null,         // isVacant
                null,         // listedSale
                null,         // floodRisk
                sortBy,
                sortOrder,
                null,         // municipality
                0, isBest,
                null          // zoningTyp
            );
            // Reload tiles based on filter type
            // reloadTilesSafely(adjacentMode);  // ✅ Only pass mode
            reloadParcelTiles(adjacentMode);
            setBuilderLayerActive(false); // Ensure builder layer is turned off when switching to adjacent mode


            // Preserve and reapply county filter after tile reload
            setTimeout(() => {
                if (mapRef.current && selectedCounty) {
                    mapRef.current.setLayoutProperty("parcel-points", "visibility", "visible");
                    const filterExpr = ["==", ["downcase", ["get", "county"]], selectedCounty.toLowerCase()];
                    mapRef.current.setFilter("parcel-points", filterExpr);
                    if (mapRef.current.getLayer("parcel-outline")) {
                        mapRef.current.setLayoutProperty("parcel-outline", "visibility", "visible");
                        mapRef.current.setFilter("parcel-outline", filterExpr);
                    }



                    if (mapRef.current.getLayer("parcel-selected-outline")) {
                        mapRef.current.setFilter("parcel-selected-outline", ["==", ["get", "parcel"], ""]);
                    }

                }
            }, 1000);
            console.log(" Adjacent parcel filter changed:", adjacentParcelFilter);
        }
    }, [adjacentParcelFilter, adjacentMode, selectedCounty, selectedCountyRef.current]);


    // Swap parcel tiles between base and adjacent without navigation
    const reloadParcelTiles = (mode) => {
        if (!mapRef.current) return;

        // const parcelsLayers = ["parcel-selected-outline", "parcel-selected-fill", "parcel-hover", "parcel-outline", "parcel-points"];
        const parcelsLayers = ["parcel-selected-outline", "parcel-hover", "parcel-outline", "parcel-hitbox", "parcel-points"];
        parcelsLayers.forEach((id) => {
            if (mapRef.current.getLayer(id)) {
                try { mapRef.current.removeLayer(id); } catch (e) { }
            }
        });

        if (mapRef.current.getSource("parcels")) {
            try { mapRef.current.removeSource("parcels"); } catch (e) { }
        }

        const tilesUrl = mode
            // ? "http://localhost:8008/data/adjparcel1s/{z}/{x}/{y}.pbf"
            // ? "https://demo.flowlogic.com/tilesgl/data/adjparcels/{z}/{x}/{y}.pbf"
            ? adjacentParcelFilter === "Suitable Parcels"
                // ? "http://localhost:8009/data/adjparcelsfinal/{z}/{x}/{y}.pbf"
                // : "http://localhost:8009/data/adjparcelsrejected/{z}/{x}/{y}.pbf"

                ? "https://demo.flowlogic.com/tilesgl/data/adjparcelsfinal/{z}/{x}/{y}.pbf"
                : "https://demo.flowlogic.com/tilesgl/data/adjparcelsrejected/{z}/{x}/{y}.pbf"

            // : "https://demo.flowlogic.com/tilesgl/data/adjparcels/{z}/{x}/{y}.pbf"
            // : "http://localhost:8007/data/ncarolina/{z}/{x}/{y}.pbf"

            : "https://demo.flowlogic.com/tilesgl/data/ncarolina/{z}/{x}/{y}.pbf";

        console.log(" tilesUrl", tilesUrl);

        mapRef.current.addSource("parcels", {
            type: "vector",
            tiles: [tilesUrl],
        });

        // Re-add layers with same styling
        mapRef.current.addLayer({
            id: "parcel-points",
            type: "fill",
            source: "parcels",
            "source-layer": "fema",
            layout: { visibility: "none" },
            paint: {
                "fill-color": [
                    "match",
                    ["get", "parcelcustomfemaflood"],
                    "X", "#4CAF50",
                    "AE", "#FF9800",
                    "VE", "#E91E63",
                    "A", "#2196F3",
                    "#9fa2e7",
                ],
                "fill-opacity": 0.45,
            },
        });

        mapRef.current.addLayer({
            id: "parcel-hitbox",
            type: "fill",
            source: "parcels",
            "source-layer": "fema",
            layout: { visibility: "visible" },
            paint: {
                "fill-color": "rgba(0,0,0,0)",
                "fill-opacity": 0.01,
            },
        });

        mapRef.current.addLayer({
            id: "parcel-outline",
            type: "line",
            source: "parcels",
            "source-layer": "fema",
            layout: { visibility: "none" },
            paint: {
                // "line-color": [
                //     "step",
                //     ["zoom"],
                //     "#333",
                //     13, "#ebeaea",
                // ],
                "line-color": [
                    "step",
                    ["zoom"],

                    // Below zoom 13
                    // [
                    //     "match",
                    //     ["get", "parcelcustomfemaflood"],
                    //     "X", "#81C784",
                    //     "AE", "#FFB74D",
                    //     "VE", "#F06292",
                    //     "A", "#64B5F6",
                    //     "#C5CAE9"
                    // ],
                    [
                        "match",
                        ["get", "parcelcustomfemaflood"],
                        "X", "#31f03b",
                        "AE", "#f5f25b",
                        "VE", "#f07fa5",
                        "A", "#64B5F6",
                        "#C5CAE9"
                    ],

                    // Zoom 13 and above
                    // 13, "#ffffff"
                    13, "#bebd6b"
                ],
                // "line-width": [
                //     "interpolate",
                //     ["linear"],
                //     ["zoom"],
                //     0, 0.5,
                //     13, 1.0,
                //     18, 2.0,
                // ],
                "line-width": [
                    "interpolate",
                    ["linear"],
                    ["zoom"],
                    0, 0.5,      // Thin at low zoom
                    13, .9,     // Medium at zoom 13
                    18, 1.5      // Thicker at high zoom
                ],
                // "line-opacity": [
                //     "step",
                //     ["zoom"],
                //     0,
                //     13, 0.9,
                // ],
                "line-opacity": [
                    "interpolate",
                    ["linear"],
                    ["zoom"],
                    0, 0.65,   // faint below zoom 13
                    13, 0.9
                ]
            },
        });

        mapRef.current.addLayer({
            id: "parcel-hover",
            type: "line",
            source: "parcels",
            "source-layer": "fema",
            layout: { visibility: "none" },
            paint: {
                "line-color": "#ff7300",
                "line-width": 3,
                "line-opacity": 0,
            },
            filter: ["==", ["get", "parcel"], ""],
        });

        // Selected parcels fill layer (for row click highlight)
        // adjacentMode && mapRef.current.addLayer({
        //     id: "parcel-selected-fill",
        //     type: "fill",
        //     source: "parcels",
        //     "source-layer": "fema",
        //     layout: { visibility: "none" },
        //     paint: {
        //         "fill-color": "#1c8f7d",
        //         "fill-opacity": 0.4,
        //     },
        //     filter: ["in", ["get", "parcel"], ["literal", []]],
        // });

        // Selected parcels outline layer
        adjacentMode && mapRef.current.addLayer({
            id: "parcel-selected-outline",
            type: "line",
            source: "parcels",
            "source-layer": "fema",
            layout: { visibility: "none" },
            paint: {
                // "line-color": "#0f5d51",
                "line-color": "#eaff4a",
                "line-width": 3,
                "line-opacity": 0.95,
            },
            filter: ["in", ["get", "parcel"], ["literal", []]],
        });

        // Ensure layers reflect current app state immediately
        const currentZoomNow = mapRef.current.getZoom();
        const hasActiveFilters =
            (vacantFilter && vacantFilter !== "All") ||
            (zoneCodeFilter && zoneCodeFilter.length > 0) ||
            (listedSaleFilter && listedSaleFilter !== "All") ||
            (floodRiskFilter && floodRiskFilter !== "All") ||
            (municipalityFilter && municipalityFilter !== "All") ||
            (Array.isArray(zoningTypFilter) ? zoningTypFilter.length > 0 : zoningTypFilter && zoningTypFilter !== "All") ||
            (priceRange[0] !== sliderMinPrice || priceRange[1] !== sliderMaxPrice) ||
            (zestimateRange[0] !== sliderMinZestimate || zestimateRange[1] !== sliderMaxZestimate) ||
            (sizeRange[0] !== sliderMinSize || sizeRange[1] !== sliderMaxSize) ||
            (spreadRange[0] !== sliderMinSpread || spreadRange[1] !== sliderMaxSpread);

        mapRef.current.once('idle', () => {
            try {
                // Show parcel layers only when zoomed in (and optionally hide when filters active)
                const showTiles = currentZoomNow >= 13.1 && !hasActiveFilters;
                mapRef.current.setLayoutProperty('parcel-points', 'visibility', showTiles ? 'visible' : 'none');
                mapRef.current.setLayoutProperty('parcel-outline', 'visibility', showTiles ? 'visible' : 'none');

                // Reapply county filter after tile reload (prevents momentary hidden state)
                const county = selectedCountyRef.current;
                if (county && county !== 'all') {
                    const filterExpr = ["==", ["downcase", ["get", "county"]], county.toLowerCase()];
                    mapRef.current.setFilter('parcel-points', filterExpr);
                    if (mapRef.current.getLayer('parcel-outline')) {
                        mapRef.current.setFilter('parcel-outline', filterExpr);
                    }
                    // Ensure layer is visible when a county is selected
                    mapRef.current.setLayoutProperty('parcel-points', 'visibility', 'visible');
                    mapRef.current.setLayoutProperty('parcel-outline', 'visibility', 'visible');
                }

                // Keep counties above parcels
                mapRef.current.moveLayer('counties-fill', 'parcel-points');
                mapRef.current.moveLayer('counties-outline', 'parcel-points');
            } catch (e) { }

            try { mapRef.current.triggerRepaint && mapRef.current.triggerRepaint(); } catch (e) { }
        });

        // Sewage Gravity Main Source
        // if (!mapRef.current.getSource("water-ssgravitymain")) {
        //     mapRef.current.addSource("water-ssgravitymain", {
        //         type: "vector",
        //         tiles: [
        //             "http://demo.flowlogic.com:8080/data/water_ssgravitymain/{z}/{x}/{y}.pbf"
        //         ],
        //         minzoom: 10,
        //         maxzoom: 17,
        //     });
        // }

        // Sewage Layer
        // if (!mapRef.current.getLayer("water-ssgravitymain-layer")) {
        //     mapRef.current.addLayer({
        //         id: "water-ssgravitymain-layer",
        //         type: "line",
        //         source: "water-ssgravitymain",
        //         "source-layer": "water_ssgravitymain",
        //         layout: {
        //             visibility: "none",
        //             // "line-join": "round",
        //             // "line-cap": "round",
        //         },

        //                paint: {
        //             "line-color": "#00BCD4",
        //             "line-width": 2,
        //         },
        //     });
        // }

    };


    //     useEffect(() => {
    //     if (!mapRef.current?.getLayer("water-ssgravitymain-layer")) return;

    //     mapRef.current.setLayoutProperty(
    //         "water-ssgravitymain-layer",
    //         "visibility",
    //         showSewageMap ? "visible" : "none"
    //     );
    // }, [showSewageMap]);

    useEffect(() => {
        minMaxInitialized.current = false;
        selectedCountyRef.current = selectedCounty;  // Keep ref in sync with state

        if (!selectedCounty || selectedCounty === "all") return;
        setShowFavoritesOnly(false);
        // setBuilderLayerActive(false); // Ensure builder layer is turned off when county changes

        //  Reset municipality filter when county changes
        setMunicipalityFilter("All");

        //  Reset zoning type filter when county changes
        setZoningTypFilter([]);

        // If you track touched state
        municipalityFilterTouched.current = false;
        setShowFemaLayer(true); // Reset FEMA layer visibility when county changes
    }, [selectedCounty]);

     useEffect(() => {
        setBuilderLayerActive(false); // Ensure builder layer is turned off when county changes
 }, [showFavoritesOnly]);

    // Reinitialize min/max when adjacent mode toggles

    // useEffect(() => {
    //     minMaxInitialized.current = false;
    //     if (!selectedCounty || selectedCounty === 'all') return;
    //     // Trigger the init effect below by calling fetchParcelMinMax directly
    //     (async () => {
    //         const data = await fetchParcelMinMax(selectedCounty);
    //         if (!data) return;
    //         // ---- Price ----
    //         setSliderMinPrice(data.totallandvalue_min);
    //         setSliderMaxPrice(data.totallandvalue_max);
    //         setPriceRange([data.totallandvalue_min, data.totallandvalue_max]);

    //         // ---- Size ----
    //         setSliderMinSize(Number(data.parcelsize_min));
    //         setSliderMaxSize(Number(data.parcelsize_max));
    //         setSizeRange([Number(data.parcelsize_min), Number(data.parcelsize_max)]);

    //         // ---- Zestimate ----
    //         setSliderMinZestimate(data.zestimate_min);
    //         setSliderMaxZestimate(data.zestimate_max);
    //         setZestimateRange([data.zestimate_min, data.zestimate_max]);

    //         // ---- Zone codes ----
    //         const zoneCodes = (data.zonedcodelocal_values || [])
    //             .filter(Boolean)
    //             .sort();
    //         setZoneCodes(zoneCodes);

    //         // ---- Spread Percentage ----
    //         const minSpread = (data.spreadpercentage_min);
    //         const maxSpread = (data.spreadpercentage_max);
    //         setSliderMinSpread(minSpread);
    //         setSliderMaxSpread(maxSpread);
    //         setSpreadRange([minSpread, maxSpread]);

    //          // ---- Flood risk ----
    //         const floodRisks = (data.parcelcustomfemaflood_values || [])
    //             .filter(Boolean)
    //             .sort();

    //         // Municipality 
    //         setMunicipalityOptions([
    //             "All",
    //             ...(data.municipality_values || []).filter(Boolean).sort(),
    //         ]);

    //         minMaxInitialized.current = true;
    //     })();
    // }, [adjacentMode]);

    useEffect(() => {
        if (!selectedCounty || selectedCounty === "all") return;

        console.log("@@@@@*****selcted county ", selectedCounty);


        console.log(" Adjacent mode changed:", adjacentMode);

        // Reset min/max cache
        minMaxInitialized.current = false;

        //  CRITICAL FIX: Reset pagination offset when mode changes
        setCurrentOffset(0);
        setHasMoreData(true);
        setIsLoadingMore(false);

        //  FIX: Clear API parcels IMMEDIATELY to prevent old data from showing
        setApiParcels([]);
        setFilteredApiParcels([]);

        (async () => {
            // 1 Reload min/max for the new mode
            const data = await fetchParcelMinMax(selectedCounty);
            if (!data) return;

            // ---- Price ----
            setSliderMinPrice(data.totallandvalue_min);
            setSliderMaxPrice(data.totallandvalue_max);
            setPriceRange([data.totallandvalue_min, data.totallandvalue_max]);

            // ---- Size ----
            setSliderMinSize(Number(data.parcelsize_min));
            setSliderMaxSize(Number(data.parcelsize_max));
            setSizeRange([Number(data.parcelsize_min), Number(data.parcelsize_max)]);

            // ---- Zestimate ----
            setSliderMinZestimate(data.zestimate_min);
            setSliderMaxZestimate(data.zestimate_max);
            setZestimateRange([data.zestimate_min, data.zestimate_max]);

            // ---- Spread ----
            setSliderMinSpread(data.spreadpercentage_min);
            setSliderMaxSpread(data.spreadpercentage_max);
            setSpreadRange([data.spreadpercentage_min, data.spreadpercentage_max]);

            // ---- Zone ----
            setZoneCodes((data.zonedcodelocal_values || []).filter(Boolean).sort());

            // ---- Zoning Type (only in parcel mode) ----
            setZoningTypOptions((data.zoning_typ_values || []).filter(Boolean).sort());

            // ---- Flood ----
            setFloodOptions(["All", ...(data.parcelcustomfemaflood_values || []).filter(Boolean).sort()]);

            // ---- Municipality ----
            setMunicipalityOptions(["All", ...(data.municipality_values || []).filter(Boolean).sort()]);

            minMaxInitialized.current = true;

            // RESET all filters so stale ones don’t block adjacency
            setZoneCodeFilter([]);
            setVacantFilter("All");
            setListedSaleFilter("All");
            setFloodRiskFilter("All");
            setMunicipalityFilter("All");
            setZoningTypFilter([]);
            //  Clear API parcels IMMEDIATELY to prevent old data from showing
            setApiParcels([]);
            setFilteredApiParcels([]);

            //  NOW refetch the parcels using the new mode
            const isBest = adjacentMode ? (adjacentParcelFilter === "Suitable Parcels" ? 1 : 0) : null;
            fetchApiParcels(
                selectedCounty,
                null, null,   // landValue
                null, null,   // zestimate
                null, null,   // parcelsize
                null, null,   // spread
                null,         // zone
                null,         // isVacant
                null,         // listedSale
                null,         // floodRisk
                sortBy,
                sortOrder,
                null,         // municipality
                0,            // offset reset
                isBest,
                null          // zoningTyp
            );
            // Reapply county filter after tile reload
            setTimeout(() => {
                if (mapRef.current && selectedCounty) {
                    mapRef.current.setLayoutProperty("parcel-points", "visibility", "visible");
                    const filterExpr = ["==", ["downcase", ["get", "county"]], selectedCounty.toLowerCase()];
                    mapRef.current.setFilter("parcel-points", filterExpr);
                    if (mapRef.current.getLayer("parcel-outline")) {
                        mapRef.current.setLayoutProperty("parcel-outline", "visibility", "visible");
                        mapRef.current.setFilter("parcel-outline", filterExpr);
                    }
                }
            }, 1000);

            //         if (adjacentMode && selectedCounty !== "all") {
            //              fetchParcelMinMax(selectedCounty, adjacentMode);
            //     // Reload tiles when county changes in adjacent mode
            //     reloadParcelTiles(adjacentMode);

            //     // Reapply county filter after tile reload
            //     setTimeout(() => {
            //         if (mapRef.current && selectedCounty) {
            //             mapRef.current.setLayoutProperty("parcel-points", "visibility", "visible");
            //             const filterExpr = ["==", ["downcase", ["get", "county"]], selectedCounty.toLowerCase()];
            //             mapRef.current.setFilter("parcel-points", filterExpr);
            //             if (mapRef.current.getLayer("parcel-outline")) {
            //                 mapRef.current.setLayoutProperty("parcel-outline", "visibility", "visible");
            //                 mapRef.current.setFilter("parcel-outline", filterExpr);
            //             }
            // //                 if (mapRef.current.getLayer("parcel-selected-outline")) {
            // //     mapRef.current.setFilter("parcel-selected-outline", ["==", ["get", "parcel"], ""]);
            // // }


            //         }
            //     }, 1000);
            // }

        })();
    }, [adjacentMode, selectedCounty]);



    //clear parcel search
    const clearParcelSearch = () => {
        setIsParcelSearchActive(false);
        updateLeftPanelParcels(selectedCounty);
        setSelectedParcelId(null);
        clearParcelHighlight();
        setCurrentOffset(0);
        setHasMoreData(true);
        setIsLoadingMore(false);
        fetchApiParcelsWithCurrentFilters(0);
    };

    const PARCEL_LENGTH_BY_COUNTY = {
        default: 7,
        wake: 10,
    };
    const getMinParcelLength = (county) => {
        if (!county) return PARCEL_LENGTH_BY_COUNTY.default;
        return PARCEL_LENGTH_BY_COUNTY[county.toLowerCase()] || PARCEL_LENGTH_BY_COUNTY.default;
    };
    const minLength = getMinParcelLength(selectedCounty);

    const isRangeFilterActive = (range, min, max, tolerance = 0.01) => {
        if (!Array.isArray(range) || range.length < 2) return false;
        if (!Number.isFinite(Number(range[0])) || !Number.isFinite(Number(range[1]))) return false;
        if (!Number.isFinite(Number(min)) || !Number.isFinite(Number(max))) return false;

        return (
            Math.abs(Number(range[0]) - Number(min)) > tolerance ||
            Math.abs(Number(range[1]) - Number(max)) > tolerance
        );
    };

    const getCurrentApiFilters = (offsetNum = 0) => {
        const isPriceFilterActive = isRangeFilterActive(priceRange, sliderMinPrice, sliderMaxPrice, 0.01);
        const isZestimateFilterActive = isRangeFilterActive(zestimateRange, sliderMinZestimate, sliderMaxZestimate, 0.1);
        const isSizeFilterActive = isRangeFilterActive(sizeRange, sliderMinSize, sliderMaxSize, 0.01);
        const isSpreadFilterActive = isRangeFilterActive(spreadRange, sliderMinSpread, sliderMaxSpread, 0.1);

        return {
            county: selectedCounty,
            landValueMin: isPriceFilterActive ? priceRange[0] : null,
            landValueMax: isPriceFilterActive ? priceRange[1] : null,
            zestimateMin: isZestimateFilterActive ? zestimateRange[0] : null,
            zestimateMax: isZestimateFilterActive ? zestimateRange[1] : null,
            parcelsizeMin: isSizeFilterActive ? sizeRange[0] : null,
            parcelsizeMax: isSizeFilterActive ? sizeRange[1] : null,
            spreadMin: isSpreadFilterActive ? spreadRange[0] : null,
            spreadMax: isSpreadFilterActive ? spreadRange[1] : null,
            zoneCode: zoneCodeFilter && zoneCodeFilter.length > 0 ? zoneCodeFilter : null,
            isVacant: vacantFilter !== "All" ? 1 : null,
            listedSale: listedSaleFilter !== "All" ? "all" : null,
            floodRisk: floodRiskFilter !== "All" ? floodRiskFilter : null,
            sortBy,
            sortOrder,
            municipality: municipalityFilter !== "All" ? municipalityFilter : null,
            offsetNum,
            isBest: adjacentMode ? (adjacentParcelFilter === "Suitable Parcels" ? 1 : 0) : null,
            zoningTyp: zoningTypFilter && zoningTypFilter.length > 0 ? zoningTypFilter : null,
            priceRange: isPriceFilterActive ? priceRange : null,
            zestimateRange: isZestimateFilterActive ? zestimateRange : null,
            sizeRange: isSizeFilterActive ? sizeRange : null,
            spreadRange: isSpreadFilterActive ? spreadRange : null,
        };
    };

    const fetchApiParcelsWithCurrentFilters = (offsetNum = 0) => {
        const filters = getCurrentApiFilters(offsetNum);
        return fetchApiParcels(
            filters.county,
            filters.landValueMin,
            filters.landValueMax,
            filters.zestimateMin,
            filters.zestimateMax,
            filters.parcelsizeMin,
            filters.parcelsizeMax,
            filters.spreadMin,
            filters.spreadMax,
            filters.zoneCode,
            filters.isVacant,
            filters.listedSale,
            filters.floodRisk,
            filters.sortBy,
            filters.sortOrder,
            filters.municipality,
            filters.offsetNum,
            filters.isBest,
            filters.zoningTyp
        );
    };

    const getRowKey = (item) => {
        if (!item) return "";
        const parcel = item.parcel || item.parcelid || item.geo_id || item.id || "";
        if (!parcel) return "";
        const adjacent = item.touching_parcel || item.adjacent_parcel || item.adjacent || "";
        return adjacent ? `${parcel}-${adjacent}` : parcel;
    };

    const getParcelIdentifier = (item) => {
        if (!item) return "";
        return item.parcel || item.parcelid || item.parcel_id || item.parcelid_no || item.parcel_number || item.geo_id || item.id || "";
    };

    const normalizeParcelIdentifier = (parcelId) => {
        return parcelId == null ? "" : String(parcelId).trim().toLowerCase();
    };

    const isFavoriteParcelId = (parcelId) => {
        const normalizedParcelId = normalizeParcelIdentifier(parcelId);
        if (!normalizedParcelId) return false;
        return favoriteParcelIdsRef.current.some((fav) => normalizeParcelIdentifier(fav) === normalizedParcelId);
    };

    const deriveFavoriteParcelsFromRows = (ids, rows) => {
        if (!Array.isArray(ids) || ids.length === 0 || !Array.isArray(rows)) return [];
        const idSet = new Set(ids.map(normalizeParcelIdentifier));
        return rows.filter((item) => {
            const parcelId = getParcelIdentifier(item);
            return parcelId && idSet.has(normalizeParcelIdentifier(parcelId));
        });
    };

    const parseFavoriteResponse = (data) => {
        if (!data) return { ids: [], parcels: [], meta: {} };
        let items = [];
        if (Array.isArray(data)) {
            items = data;
        } else if (data?.features && Array.isArray(data.features)) {
            items = data.features.map(f => f.properties || f);
        } else if (Array.isArray(data.results)) {
            items = data.results;
        } else if (Array.isArray(data.favorites)) {
            items = data.favorites;
        } else if (Array.isArray(data.favourites)) {
            items = data.favourites;
        } else if (data && data.data && Array.isArray(data.data.favorites)) {
            items = data.data.favorites;
        } else if (data && data.data && Array.isArray(data.data.favourites)) {
            items = data.data.favourites;
        } else if (Array.isArray(data.data)) {
            items = data.data;
        }

        const ids = Array.from(
            new Set(
                items
                    .map((item) => {
                        if (!item) return "";
                        if (typeof item === "string") return item;
                        const row = item.properties || item.parcel_data || item.parcelData || item.data || item;
                        return getParcelIdentifier(row);
                    })
                    .filter(Boolean)
            )
        );

        const rawParcels = items
            .map((item) => {
                if (!item) return null;
                if (typeof item === "string") return { parcel: item };
                return item.properties || item.parcel_data || item.parcelData || item.data || item;
            })
            .filter((item) => item && getParcelIdentifier(item));

        const parcels = rawParcels.filter((item) => {
            if (!item) return false;
            const keys = Object.keys(item);
            if (keys.length === 1 && keys[0] === "parcel") {
                return false; // only parcel ID, no full row data
            }
            return true;
        });

        const meta = {
            count: Number.isFinite(data?.count) ? data.count : Number.isFinite(data?.total) ? data.total : null,
            offset: Number.isFinite(data?.offset) ? data.offset : null,
            pageCount: Number.isFinite(data?.page_count) ? data.page_count : null,
            hasNext: typeof data?.has_next === "boolean" ? data.has_next : null,
            hasPrevious: typeof data?.has_previous === "boolean" ? data.has_previous : null,
            limit: Number.isFinite(data?.limit) ? data.limit : null,
        };

        return { ids, parcels, meta };
    };

    const fetchFavoriteParcelIds = async (offset = 0) => {
        const token = localStorage.getItem("authToken");
        if (!token) { handleLogout(); return; }
        if (offset === 0) {
            setFavoriteOffset(0);
            setFavoriteHasMore(false);
            setFavoriteTotalCount();
            setFavoritePageCount(null);
        }
        setIsLoadingFavorites(true);

        try {
            const params = new URLSearchParams();
            params.append("limit", "100");
            params.append("offset", String(offset));
            if (selectedCounty && selectedCounty !== "all") {
                params.append("county", selectedCounty);
            }

            const resp = await fetch(`https://beta.flowlogic.com/api/parcels/favorite/all-test/?${params.toString()}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });
            if (resp.status === 401) { handleLogout(); return; }
            if (!resp.ok) {
                throw new Error(`Favorites fetch failed ${resp.status}`);
            }

            const data = await resp.json();
            const { ids, parcels, meta } = parseFavoriteResponse(data);

            setFavoriteParcelIds((prevIds) => {
                const merged = offset === 0 ? ids : [...prevIds, ...ids];
                return Array.from(new Set(merged));
            });
            setFavoriteParcels((prevParcels) => (offset === 0 ? parcels : [...prevParcels, ...parcels]));
            setFavoriteOffset(offset);
            // setFavoriteTotalCount(meta.count ?? (offset === 0 ? ids.length : favoriteTotalCount));
            setFavoriteTotalCount(meta.count ?? (offset === 0 ? ids.length : favoriteTotalCount));
            setFavoritePageCount(meta.pageCount);

            const limit = meta.limit || 100;
            const hasMore = meta.hasNext !== null
                ? meta.hasNext
                : meta.count !== null
                    ? offset + parcels.length < meta.count
                    : parcels.length >= limit;
            setFavoriteHasMore(hasMore);
        } catch (err) {
            console.error("Failed to fetch favorite parcels", err);
            if (offset === 0) {
                setFavoriteParcelIds([]);
                setFavoriteParcels([]);
                setFavoriteHasMore(false);
                setFavoriteTotalCount();
                setFavoritePageCount(null);
            }
        } finally {
            setIsLoadingFavorites(false);
        }
    };

    const loadMoreFavorites = () => {
        if (isParcelSearchActive) {
            console.log("Favorite load more blocked — search is active");
            return;
        }
        if (isLoadingFavorites || !favoriteHasMore) {
            return;
        }
        fetchFavoriteParcelIds(favoriteOffset + 100);
    };

    const updateFavoritesFromResponse = (data) => {
        const { ids, parcels } = parseFavoriteResponse(data);
        setFavoriteParcelIds(ids);
        setFavoriteParcels(parcels);
    };

    const sendFavoriteRequest = async (endpoint, parcelIds) => {
        const token = localStorage.getItem("authToken");
        if (!token) { handleLogout(); return; }
        try {
            const resp = await fetch(`https://beta.flowlogic.com/api/parcels/favorite/${endpoint}/`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ parcels: Array.isArray(parcelIds) ? parcelIds : [parcelIds] }),
            });
            if (resp.status === 401) { handleLogout(); return; }
            if (!resp.ok) {
                // Handle specific error for already added favorites
                if (resp.status === 409 || resp.status === 400) {
                    const errorData = await resp.json().catch(() => ({}));
                    if (errorData?.detail?.includes('already') || errorData?.message?.includes('already')) {
                        console.warn('Parcel already in favorites');
                        return null; // Don't treat as error for UI
                    }
                }
                throw new Error(`Favorite ${endpoint} failed ${resp.status}`);
            }
            const data = await resp.json();
            // Don't update favorites from response for individual add/remove operations
            // This preserves optimistic updates
            // updateFavoritesFromResponse(data);
            return data;
        } catch (err) {
            console.error(`Favorite ${endpoint} error`, err);
            throw err; // Re-throw to allow UI to handle
        }
    };

    const addFavoriteParcelIds = async (parcelIds) => {
        if (!parcelIds || parcelIds.length === 0) return;
        const uniqueParcelIds = Array.from(
            new Set(parcelIds.map((id) => String(id || "").trim()).filter(Boolean))
        );
        if (uniqueParcelIds.length === 0) return;

        await sendFavoriteRequest("add", uniqueParcelIds);

        setFavoriteTotalCount((prev) => {
            const currentCount = prev || 0;
            const existingIds = new Set(favoriteParcelIds.map(normalizeParcelIdentifier));
            const addedCount = uniqueParcelIds.filter(
                (id) => !existingIds.has(normalizeParcelIdentifier(id))
            ).length;
            return currentCount + addedCount;
        });
    };

    const removeFavoriteParcelIds = async (parcelIds) => {
        if (!parcelIds || parcelIds.length === 0) return;
        const uniqueParcelIds = Array.from(
            new Set(parcelIds.map((id) => String(id || "").trim()).filter(Boolean))
        );
        if (uniqueParcelIds.length === 0) return;

        await sendFavoriteRequest("remove", uniqueParcelIds);

        setFavoriteTotalCount((prev) => {
            const currentCount = prev || 0;
            const existingIds = new Set(favoriteParcelIds.map(normalizeParcelIdentifier));
            const removedCount = uniqueParcelIds.filter(
                (id) => existingIds.has(normalizeParcelIdentifier(id))
            ).length;
            return Math.max(0, currentCount - removedCount);
        });
    };

    const toggleFavoriteParcel = async (item) => {
        const parcelId = getParcelIdentifier(item);
        if (!parcelId) return;

        const isCurrentlyFavorite = favoriteParcelIdSet.has(parcelId);

        if (isCurrentlyFavorite) {
            setFavoriteParcelIds((prev) => prev.filter((id) => id !== parcelId));
            setFavoriteParcels((prev) => prev.filter((p) => getParcelIdentifier(p) !== parcelId));
        } else {
            setFavoriteParcelIds((prev) => Array.from(new Set([...(prev || []), parcelId])));
            setFavoriteParcels((prev) => {
                const existingIds = new Set((prev || []).map(getParcelIdentifier).filter(Boolean));
                if (existingIds.has(parcelId)) return prev || [];
                return [...(prev || []), item];
            });
        }

        try {
            if (isCurrentlyFavorite) {
                await removeFavoriteParcelIds([parcelId]);
            } else {
                await addFavoriteParcelIds([parcelId]);
            }
        } catch (error) {
            console.error("Failed to toggle favorite:", error);
            if (isCurrentlyFavorite) {
                setFavoriteParcelIds((prev) => Array.from(new Set([...(prev || []), parcelId])));
                setFavoriteParcels((prev) => {
                    const existingIds = new Set((prev || []).map(getParcelIdentifier).filter(Boolean));
                    if (existingIds.has(parcelId)) return prev || [];
                    return [...(prev || []), item];
                });
            } else {
                setFavoriteParcelIds((prev) => (prev || []).filter((id) => id !== parcelId));
                setFavoriteParcels((prev) => (prev || []).filter((p) => getParcelIdentifier(p) !== parcelId));
            }
        }
    };

    const addSelectedRowsToFavorites = async () => {
        const parcelIds = Array.from(
            new Set(
                (selectedRows || [])
                    .map(getParcelIdentifier)
                    .filter(Boolean)
            )
        );
        if (parcelIds.length === 0) return;

        const existingIds = new Set(favoriteParcelIds.map(normalizeParcelIdentifier));
        const newFavoriteIds = parcelIds.filter(
            (id) => !existingIds.has(normalizeParcelIdentifier(id))
        );
        if (newFavoriteIds.length > 0) {
            await addFavoriteParcelIds(newFavoriteIds);
            setFavoriteParcelIds((prev) => Array.from(new Set([...(prev || []), ...newFavoriteIds])));
            setFavoriteParcels((prev) => {
                const existingRowIds = new Set((prev || []).map(getParcelIdentifier).filter(Boolean));
                const newRows = (selectedRows || []).filter((item) => {
                    const parcelId = getParcelIdentifier(item);
                    return (
                        parcelId &&
                        newFavoriteIds.includes(parcelId) &&
                        !existingRowIds.has(parcelId)
                    );
                });
                return [...(prev || []), ...newRows];
            });
        }

        clearSelectionAndCheckFilters();
    };

    const clearSelectedRowsToFavorites = async () => {
        const parcelIds = Array.from(
            new Set(
                (selectedRows || [])
                    .map(getParcelIdentifier)
                    .filter(Boolean)
            )
        );
        if (parcelIds.length === 0) return;
        setFavoriteParcelIds((prev) => Array.from(new Set([...prev, ...parcelIds])));
        setFavoriteParcels((prev) => {
            const existingIds = new Set(prev.map((item) => String(getParcelIdentifier(item))));
            const newRows = (selectedRows || []).filter((item) => {
                const parcelId = getParcelIdentifier(item);
                return parcelId && !existingIds.has(String(parcelId));
            });
            return [...prev, ...newRows];
        });

        // After saving to favorites, clear selection and check filters
        clearSelectionAndCheckFilters();
    };


    const removeAllFavorites = async () => {
        const token = localStorage.getItem("authToken");

        if (!token) {
            handleLogout();
            return;
        }

        try {
            const params = new URLSearchParams();

            if (selectedCounty && selectedCounty !== "all") {
                params.append("county", selectedCounty);
            }

            const resp = await fetch(
                `https://beta.flowlogic.com/api/parcels/favorite/remove-all/?${params.toString()}`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (resp.status === 401) {
                handleLogout();
                return;
            }

            if (!resp.ok) {
                throw new Error(`Remove all favorites failed ${resp.status}`);
            }

            // Clear all favorite states instantly
            setFavoriteParcelIds([]);
            setFavoriteParcels([]);
            setFavoriteTotalCount(0);

            // Clear selections also
            setSelectedRows([]);
            setSelectionMode(false);

            localStorage.removeItem("ncSelectedRows");

        } catch (err) {
            console.error("Remove all favorites error", err);
        }
    };


    const removeSelectedRowsFromFavorites = async () => {
        const parcelIds = Array.from(
            new Set(
                (selectedRows || [])
                    .map(getParcelIdentifier)
                    .filter(Boolean)
            )
        );
        if (parcelIds.length === 0) return;
        await removeFavoriteParcelIds(parcelIds);
        const removeSet = new Set(parcelIds.map((id) => String(id)));
        setFavoriteParcelIds((prev) => prev.filter((id) => !removeSet.has(String(id))));
        setFavoriteParcels((prev) => prev.filter((item) => !removeSet.has(String(getParcelIdentifier(item)))));
        setSelectedRows([]);
        setSelectionMode(false);
        localStorage.removeItem("ncSelectedRows");

        // Trigger useEffect refresh
        setFavoritesRefreshKey((prev) => prev + 1);
    };

    const clearAllFavorites = () => {
        setShowFavoritesOnly(false);
    };

    const toggleShowFavorites = () => {
        setShowFavoritesOnly((prev) => !prev);
    };

    const selectedFavoriteCount = favoriteParcelIds.length;

    useEffect(() => {
        if (!authToken) return;
        fetchFavoriteParcelIds();
    }, [authToken]);

    useEffect(() => {
        if (!authToken || !selectedCounty || selectedCounty === "all") return;
        fetchFavoriteParcelIds();
    }, [authToken, selectedCounty, favoritesRefreshKey]);

    useEffect(() => {
        setShowFavoritesOnly(activeTab === "favorites");
    }, [activeTab,]);

    useEffect(() => {
        // clearSelectedRowsToFavorites();
        //    Clear everything when button clicked
        setSelectedRows([]);
        setSelectionMode(false);
        localStorage.removeItem("ncSelectedRows");
        // isAllSelected && toggleSelectAll(displayedParcels);
    }, [showFavoritesOnly, activeTab]);

    useEffect(() => {
        if (showFavoritesOnly && displayedParcels.length > 0) {
            const firstParcel = displayedParcels[0];
            const parcelId = getParcelIdentifier(firstParcel);
            const touchingId = firstParcel?.touching_parcel || firstParcel?.adjacent_parcel || null;
            const compositeKey = adjacentMode ? `${parcelId}-${touchingId || ''}` : parcelId;
            setSelectedParcelId(compositeKey);
            centerMapOnParcel(firstParcel);
        }
    }, [showFavoritesOnly, adjacentMode]);

    useEffect(() => {
        if (showFavoritesOnly && authToken) {
            fetchFavoriteParcelIds();
        }
    }, [showFavoritesOnly, authToken]);

    const selectedRowIds = useMemo(() => {
        return new Set((selectedRows || []).map(getRowKey).filter(Boolean));
    }, [selectedRows]);

    const isRowSelected = (item) => {
        const key = getRowKey(item);
        return key ? selectedRowIds.has(key) : false;
    };

    const toggleParcelSelection = (item) => {
        const key = getRowKey(item);
        if (!key) return;

        setSelectedRows((prev) => {
            const normalized = prev || [];
            const exists = normalized.some((row) => getRowKey(row) === key);
            if (exists) {
                return normalized.filter((row) => getRowKey(row) !== key);
            }
            return [...normalized, item];
        });
    };

    const toggleSelectAll = (items) => {
        if (!Array.isArray(items) || items.length === 0) return;
        const allVisibleSelected = items.every((item) => {
            const key = getRowKey(item);
            return key && selectedRowIds.has(key);
        });

        if (allVisibleSelected) {
            setSelectedRows((prev) => {
                const visibleKeys = new Set(items.map(getRowKey).filter(Boolean));
                return (prev || []).filter((row) => !visibleKeys.has(getRowKey(row)));
            });
            return;
        }

        setSelectedRows((prev) => {
            const existingKeys = new Set((prev || []).map(getRowKey).filter(Boolean));
            const newRows = items.filter((item) => {
                const key = getRowKey(item);
                return key && !existingKeys.has(key);
            });
            return [...(prev || []), ...newRows];
        });
    };

    const buildExportData = (rows = selectedRows) => {
        return (rows || []).map((item) => ({
            Parcel: item.parcel || item.parcelid || item.geo_id || item.id || "N/A",
            ...(item.touching_parcel && {
                AdjacentParcel:
                    item.touching_parcel ||
                    item.adjacent_parcel ||
                    item.adjacent,
            }),
            Municipality: item.municipality || item.muni || item.county || "N/A",
            Size: item.parcelsize ?? item.parcel_size ?? item.landsize ?? "N/A",
            Zoning: item.zonedcodelocal || item.zoning || item.zone || "N/A",
            "Target ARV": item.targetarv || item.target_arv || "N/A",
            "Land Value/Unit": item.landvalueperunit || item.land_value_per_unit || "N/A",
            "Max Unit": item.totalunits || item.total_units || "N/A",
            "Developed Value": item.totallandvalue || item.total_land_value || "N/A",
            Price: item.zestimate || item.price || item.saleprice || "N/A",
            Spread: item.spread != null ? item.spread : "N/A",
            "Spread %": item.spreadpercentage != null ? item.spreadpercentage : "N/A",
        }));
    };

    const exportSelectedRowsToFavourite = () => {
        if (!selectionMode) {
            performExportToFavourite(displayedParcels || []);
            return;
        }

        performExportToFavourite(selectedRows);
    };

    const exportSelectedRowsToXlsx = exportSelectedRowsToFavourite;
    const exportSelectedRowsToPdf = exportSelectedRowsToFavourite;

    const exportFavorites = () => {
        // Export all favorite parcels from the current favorites source
        const favoriteParcelsData = showFavoritesOnly
            ? (favoriteParcels.length > 0
                ? favoriteParcels
                : filteredApiParcels.filter((p) => favoriteParcelIdSet.has(getParcelIdentifier(p))))
            : favoriteParcels;

        if (favoriteParcelsData.length === 0) {
            console.warn('No favorites to export');
            return;
        }

        performExportToFavourite(favoriteParcelsData);
        setSelectedRows([]);
        setSelectionMode(false);
        localStorage.removeItem("ncSelectedRows");
    };

    const clearSelectionAndCheckFilters = () => {
        // Clear selection
        setSelectedRows([]);
        setSelectionMode(false);
        localStorage.removeItem("ncSelectedRows");

        // Check "List" and "All" filters after saving to favorites
        // Reset to show all parcels in list view
        clearAllFilters();
    };

    const exportOnlySelectedRows = () => {
        if (!selectionMode) {
            performExportOnly(displayedParcels || []);
            return;
        }

        performExportOnly(selectedRows);
    };

    const performExportToFavourite = (rowsToExport) => {
        if (!rowsToExport || !rowsToExport.length) {
            return;
        }
        const data = buildExportData(rowsToExport);
        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = {
            Sheets: { "Selected Parcels": worksheet },
            SheetNames: ["Selected Parcels"],
        };
        const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const blob = new Blob([wbout], { type: "application/octet-stream" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "selected-parcels.xlsx";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        // Add to favorites
        const parcelIds = rowsToExport.map(getParcelIdentifier).filter(Boolean);
        if (parcelIds.length > 0) {
            addFavoriteParcelIds(parcelIds);
        }

        // Show download button and auto-hide after 3 seconds
        setShowDownloadButton(true);
        setTimeout(() => {
            setShowDownloadButton(false);

            selectedRows.length > 0 && setSelectionMode(false); // Exit selection mode after export if there were selected rows
            localStorage.removeItem("ncSelectedRows");
            setSelectedRows([]);
        }, 1000);
    };

    const performExportOnly = (rowsToExport) => {
        if (!rowsToExport || !rowsToExport.length) {
            return;
        }
        const data = buildExportData(rowsToExport);
        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = {
            Sheets: { "Selected Parcels": worksheet },
            SheetNames: ["Selected Parcels"],
        };
        const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const blob = new Blob([wbout], { type: "application/octet-stream" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "selected-parcels.xlsx";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        // Show download button and auto-hide after 3 seconds
        setShowDownloadButton(true);
        setTimeout(() => {
            setShowDownloadButton(false);
            selectedRows.length > 0 && setSelectionMode(false); // Exit selection mode after export if there were selected rows
            localStorage.removeItem("ncSelectedRows");
            setSelectedRows([]);
        }, 1000);
    };

    useEffect(() => {
        try {
            if (!selectedRows || selectedRows.length === 0) {
                localStorage.removeItem("ncSelectedRows");
            } else {
                localStorage.setItem("ncSelectedRows", JSON.stringify(selectedRows));
            }
        } catch (e) {
            console.warn("Failed to save selected rows to localStorage", e);
        }
    }, [selectedRows]);

    // Clear selected rows when exiting selection mode
    useEffect(() => {
        if (!selectionMode) {
            localStorage.removeItem("ncSelectedRows");
            setSelectedRows([]);
        }
        // When entering selection mode, do not auto-select all parcels.
        // Maintain explicit selection via checkbox only.
    }, [selectionMode]);

    const getParcelSearchParams = (options) => {
        const {
            parcelNumber,
            county,
            priceRange,
            zestimateRange,
            sizeRange,
            spreadRange,
            zoneCode,
            vacant,
            listedSale,
            floodRisk,
            municipality,
            zoningTyp,
            sortBy,
            sortOrder,
            adjacentMode,
            adjacentParcelFilter,
            offset = 0,
            limit = 100
        } = options;

        const params = new URLSearchParams();

        // Identifiers & Pagination
        if (parcelNumber) params.append("parcel", parcelNumber);
        if (county && county !== "all") params.append("county", county);
        params.append("limit", String(limit));
        params.append("offset", String(offset));

        // Range Filters - Logic unified for both functions
        const ranges = [
            { key: "totallandvalue", values: priceRange },
            { key: "zestimate", values: zestimateRange },
            { key: "parcelsize", values: sizeRange },
            { key: "spreadpercentage", values: spreadRange },
        ];

        ranges.forEach(({ key, values }) => {
            if (values && values[0] !== undefined && values[1] !== undefined) {
                params.append(`${key}_min`, values[0]);
                params.append(`${key}_max`, values[1]);
            }
        });

        // Categorical Filters
        if (zoneCode?.length) {
            params.append("zonedcodelocal", Array.isArray(zoneCode) ? zoneCode.join(",") : zoneCode);
        }
        if (vacant && vacant !== "All") params.append("is_vaccant", vacant);
        if (listedSale && listedSale !== "All") params.append("listedsale", listedSale);
        if (floodRisk && floodRisk !== "All") params.append("flood_risk", floodRisk);
        if (municipality && municipality !== "All") params.append("municipality", municipality);

        // Zoning Type logic (Handles both Array and String)
        if (Array.isArray(zoningTyp) && zoningTyp.length > 0) {
            params.append("zoning_typ", zoningTyp.join(","));
        } else if (typeof zoningTyp === "string" && zoningTyp !== "All" && zoningTyp.trim() !== "") {
            params.append("zoning_typ", zoningTyp);
        }

        // Sort Logic
        params.append("sort_by", sortBy || "zoning");
        params.append("sort_order", sortOrder || "desc");

        // Assemblage/Adjacent Logic
        if (adjacentMode) {
            params.append("is_best", adjacentParcelFilter === "Suitable Parcels" ? "1" : "0");
        }

        return params;
    };

    const searchParcelApi = async (parcelNumber) => {
        if (!parcelNumber || parcelNumber.length < minLength || !selectedCounty || selectedCounty === "all") {
            return [];
        }

        try {
            setIsLoadingApi(true);
            setCurrentOffset(0);
            setHasMoreData(false);

            const activeFilters = getCurrentApiFilters(0);

            // Use the unified builder with the same active filters as normal pagination.
            const params = getParcelSearchParams({
                parcelNumber,
                county: selectedCounty,
                priceRange: activeFilters.priceRange,
                zestimateRange: activeFilters.zestimateRange,
                sizeRange: activeFilters.sizeRange,
                spreadRange: activeFilters.spreadRange,
                zoneCode: activeFilters.zoneCode,
                vacant: activeFilters.isVacant,
                listedSale: activeFilters.listedSale,
                floodRisk: activeFilters.floodRisk,
                municipality: activeFilters.municipality,
                zoningTyp: activeFilters.zoningTyp,
                sortBy: activeFilters.sortBy,
                sortOrder: activeFilters.sortOrder,
                adjacentMode,
                adjacentParcelFilter
            });

            const baseUrl = adjacentMode
                ? "https://beta.flowlogic.com/api/assemblage/list-assemblage-parcel-data/"
                : "https://beta.flowlogic.com/api/parcels/list-parcel-data/";

            const resp = await fetch(`${baseUrl}?${params.toString()}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    "Content-Type": "application/json",
                },
            });

            if (resp.status === 401) return handleLogout();
            if (!resp.ok) throw new Error("Search failed");

            const data = await resp.json();
            const items = data?.features?.map(f => f.properties) || data?.results || data || [];

            setApiParcels(items);
            setFilteredParcels(items);
            setIsParcelSearchActive(true);
            updateMapWithSearchParcels(items);

            // ... handle setting selectedParcelId ...

        } catch (e) {
            console.error("Search error", e);
        } finally {
            setIsLoadingApi(false);
        }
    };

    //fetch search parcel
    const searchParcelApi2 = async (parcelNumber) => {

        //  Guard: invalid input
        if (!parcelNumber || parcelNumber.length < minLength || !selectedCounty || selectedCounty === "all") {
            return [];
        }

        try {
            setIsLoadingApi(true);

            const params = new URLSearchParams();
            params.append("parcel", parcelNumber);
            params.append("county", selectedCounty);

            const isPriceFilterActive = sliderMinPrice !== undefined && sliderMaxPrice !== undefined &&
                (Math.abs(priceRange[0] - sliderMinPrice) > Math.abs(sliderMaxPrice - sliderMinPrice) * 0.01 ||
                    Math.abs(priceRange[1] - sliderMaxPrice) > Math.abs(sliderMaxPrice - sliderMinPrice) * 0.01);

            const isZestimateFilterActive = sliderMinZestimate !== undefined && sliderMaxZestimate !== undefined &&
                (Math.abs(zestimateRange[0] - sliderMinZestimate) > Math.abs(sliderMaxZestimate - sliderMinZestimate) * 0.01 ||
                    Math.abs(zestimateRange[1] - sliderMaxZestimate) > Math.abs(sliderMaxZestimate - sliderMinZestimate) * 0.01);

            const isSizeFilterActive = sliderMinSize !== undefined && sliderMaxSize !== undefined &&
                (Math.abs(sizeRange[0] - sliderMinSize) > Math.abs(sliderMaxSize - sliderMinSize) * 0.01 ||
                    Math.abs(sizeRange[1] - sliderMaxSize) > Math.abs(sliderMaxSize - sliderMinSize) * 0.01);

            const isSpreadFilterActive = sliderMinSpread !== undefined && sliderMaxSpread !== undefined &&
                (Math.abs(spreadRange[0] - sliderMinSpread) > Math.abs(sliderMaxSpread - sliderMinSpread) * 0.01 ||
                    Math.abs(spreadRange[1] - sliderMaxSpread) > Math.abs(sliderMaxSpread - sliderMinSpread) * 0.01);

            // SAME FILTER LOGIC AS fetchApiParcels useEffect

            // const isPriceFilterActive =
            //     showPriceFilter &&
            //     sliderMinPrice !== undefined &&
            //     sliderMaxPrice !== undefined &&
            //     (
            //         Math.abs(priceRange[0] - sliderMinPrice) > 0.01 ||
            //         Math.abs(priceRange[1] - sliderMaxPrice) > 0.01
            //     );

            // const isZestimateFilterActive =
            //     showZestimateFilter &&
            //     sliderMinZestimate !== undefined &&
            //     sliderMaxZestimate !== undefined &&
            //     (
            //         Math.abs(zestimateRange[0] - sliderMinZestimate) > 0.1 ||
            //         Math.abs(zestimateRange[1] - sliderMaxZestimate) > 0.1
            //     );

            // const isSizeFilterActive =
            //     showSizeFilter &&
            //     sliderMinSize !== undefined &&
            //     sliderMaxSize !== undefined &&
            //     (
            //         Math.abs(sizeRange[0] - sliderMinSize) > 0.01 ||
            //         Math.abs(sizeRange[1] - sliderMaxSize) > 0.01
            //     );

            // const isSpreadFilterActive =
            //     showSpreadFilter &&
            //     sliderMinSpread !== undefined &&
            //     sliderMaxSpread !== undefined &&
            //     (
            //         Math.abs(spreadRange[0] - sliderMinSpread) > 0.1 ||
            //         Math.abs(spreadRange[1] - sliderMaxSpread) > 0.1
            //     );

            if (isPriceFilterActive) {
                params.append("totallandvalue_min", priceRange[0]);
                params.append("totallandvalue_max", priceRange[1]);
            }
            if (isZestimateFilterActive) {
                params.append("zestimate_min", zestimateRange[0]);
                params.append("zestimate_max", zestimateRange[1]);
            }
            if (isSizeFilterActive) {
                params.append("parcelsize_min", sizeRange[0]);
                params.append("parcelsize_max", sizeRange[1]);
            }
            if (isSpreadFilterActive) {
                params.append("spreadpercentage_min", spreadRange[0]);
                params.append("spreadpercentage_max", spreadRange[1]);
            }

            if (zoneCodeFilter && zoneCodeFilter.length > 0) {
                params.append("zonedcodelocal", zoneCodeFilter.join(","));
            }
            if (vacantFilter && vacantFilter !== "All") {
                params.append("is_vaccant", vacantFilter);
            }
            if (listedSaleFilter && listedSaleFilter !== "All") {
                params.append("listedsale", listedSaleFilter);
            }
            if (floodRiskFilter && floodRiskFilter !== "All") {
                params.append("flood_risk", floodRiskFilter);
            }
            if (municipalityFilter && municipalityFilter !== "All") {
                params.append("municipality", municipalityFilter);
            }
            // if (Array.isArray(zoningTypFilter) && zoningTypFilter.length > 0) {
            //     params.append("zoning_typ", zoningTypFilter.join(","));
            // }
            if (Array.isArray(zoningTypFilter) && zoningTypFilter.length > 0) {
                params.append("zoning_typ", zoningTypFilter.join(","));
            } else if (
                typeof zoningTypFilter === "string" &&
                zoningTypFilter !== "All" &&
                zoningTypFilter.trim() !== ""
            ) {
                params.append("zoning_typ", zoningTypFilter);
            }


            params.append("sort_by", sortBy || "zoning");
            if (sortOrder) {
                params.append("sort_order", sortOrder);
            }

            if (adjacentMode && adjacentParcelFilter === "Suitable Parcels") {
                params.append("is_best", "1");
            } else if (adjacentMode && adjacentParcelFilter !== "All") {
                params.append("is_best", "0");
            }

            const token = localStorage.getItem("authToken");
            if (!token) { handleLogout(); return; }

            const baseUrl = adjacentMode
                ? "https://beta.flowlogic.com/api/assemblage/list-assemblage-parcel-data/"
                : "https://beta.flowlogic.com/api/parcels/list-parcel-data/";

            const resp = await fetch(`${baseUrl}?${params.toString()}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });

            if (resp.status === 401) { handleLogout(); return; }
            if (!resp.ok) throw new Error("Search failed");

            const data = await resp.json();
            const items =
                data?.features?.map(f => f.properties) ||
                data?.results ||
                data ||
                [];

            setApiParcels(items);
            setFilteredParcels(items);
            setIsParcelSearchActive(true);
            updateMapWithSearchParcels(items);

            if (items.length > 0) {
                const first = items[0];

                const parcelId =
                    first.parcel ||
                    first.parcelid ||
                    first.geo_id ||
                    first.id;

                const touchingId =
                    first?.touching_parcel ||
                    first?.adjacent_parcel ||
                    null;

                setSelectedParcelId(
                    adjacentMode ? `${parcelId}-${touchingId || ''}` : parcelId
                );
            }

        } catch (e) {
            console.error("Parcel search error", e);
            setApiParcels([]);
            setFilteredParcels([]);
        } finally {
            setIsLoadingApi(false);
        }
    };



    // Fetch parcels from API
    const fetchApiParcels = async (countyFilter, landValueMin = null, landValueMax = null, zestimateMin = null, zestimateMax = null, parcelsizeMin = null, parcelsizeMax = null, spreadMin = null, spreadMax = null, zoneCode = null, isVacant = null, listedSale = null, floodRisk = null, sortBy = null, sortOrder = 'desc', municipality = null, offsetNum = 0, is_best = null, zoningTyp = null) => {
        //  if (isParcelSearchActive) return;
        if (offsetNum === 0) {
            setResetParcelSearchSignal(prev => prev + 1);
        }

        const fetchId = ++latestFetchIdRef.current;

        if (countyFilter === "all") {
            setApiParcels([]);
            return;
        }
        console.log("countyFilter", countyFilter, "municipality", municipality, "landValueMin", landValueMin, "landValueMax", landValueMax, "zestimateMin", zestimateMin, "zestimateMax", zestimateMax, "parcelsizeMin", parcelsizeMin, "parcelsizeMax", parcelsizeMax, "spreadMin", spreadMin, "spreadMax", spreadMax, "zoneCode", zoneCode, "isVacant", isVacant, "listedSale", listedSale, "is_best", is_best);

        // Only set isLoadingApi for initial load, not for "Load More"
        // if (offsetNum === 0) {
        //     setIsLoadingApi(true);
        // }

        // Only set isLoadingApi for initial load, not for "Load More"
        if (offsetNum === 0) {
            // clear previous results immediately for fresh queries
            setApiParcels([]);
            setFilteredApiParcels([]);
            setIsLoadingApi(true);
            setHasMoreData(true);
            setCurrentOffset(0);
        }

        try {

            clickPopupRef.current?.remove();
            clickPopupRef.current = null;

            const params = new URLSearchParams();
            params.append("limit", "100");
            params.append("offset", String(offsetNum)); // Record offset: 0, 100, 200, ...

            console.log(`🔍 API Call - Offset: ${offsetNum}, Limit: 100, Expected records: ${offsetNum * 5}-${(offsetNum * 5) + 499}`);


            // if (countyFilter && countyFilter !== "all" && !adjacentMode) {
            if (countyFilter && countyFilter !== "all") {
                params.append("county", countyFilter);
            }

            // Add land value filter parameters if provided
            // if (showPriceFilter && landValueMin !== null) {
            if (landValueMin !== null) {
                params.append("totallandvalue_min", landValueMin);
            }
            // if (showPriceFilter && landValueMax !== null) {
            if (landValueMax !== null) {
                params.append("totallandvalue_max", landValueMax);
            }

            // Add zestimate filter parameters if provided
            // if (showZestimateFilter && zestimateMin !== null) {
            if (zestimateMin !== null) {
                params.append("zestimate_min", zestimateMin);
            }
            // if (showZestimateFilter && zestimateMax !== null) {
            if (zestimateMax !== null) {
                params.append("zestimate_max", zestimateMax);
            }

            // Add parcelsize filter parameters if provided
            // if (showSizeFilter && parcelsizeMin !== null) {
            if (parcelsizeMin !== null) {
                params.append("parcelsize_min", parcelsizeMin);
            }
            // if (showSizeFilter && parcelsizeMax !== null) {
            if (parcelsizeMax !== null) {
                params.append("parcelsize_max", parcelsizeMax);
            }

            // Add spread filter parameters if provided
            // if (showSpreadFilter && spreadMin !== null) {
            if (spreadMin !== null) {
                params.append("spreadpercentage_min", spreadMin);
            }
            // if (showSpreadFilter && spreadMin !== null) {
            if (spreadMax !== null) {
                params.append("spreadpercentage_max", spreadMax);
            }

            // Add zone code filter parameter if provided
            if (zoneCode !== null && zoneCode !== "" && typeof zoneCode === 'string' && zoneCode.trim() !== "") {
                params.append("zonedcodelocal", zoneCode);
            } else if (Array.isArray(zoneCode) && zoneCode.length > 0) {
                params.append("zonedcodelocal", zoneCode.join(","));
            }

            // Add vacant filter parameter if provided
            if (isVacant !== null) {
                params.append("is_vaccant", isVacant);
            }

            // Add listed sale filter parameter if provided
            if (listedSale !== null) {
                params.append("listedsale", listedSale);
            }

            // Add flood risk filter parameter if provided
            if (floodRisk !== null && floodRisk !== "All") {
                params.append("flood_risk", floodRisk);
            }

            // Add sorting parameters if provided
            if (sortBy !== null) {
                params.append("sort_by", sortBy);
            }
            if (sortOrder !== null) {
                params.append("sort_order", sortOrder);
            }

            // Add municipality filter parameter if provided
            if (municipality !== null && municipality !== "All" && municipality.trim() !== "") {
                params.append("municipality", municipality);
            }

            // Add zoning type filter parameter if provided (only in parcel mode)
            if (Array.isArray(zoningTyp) && zoningTyp.length > 0) {
                params.append("zoning_typ", zoningTyp.join(","));
            } else if (typeof zoningTyp === "string" && zoningTyp !== "All" && zoningTyp.trim() !== "") {
                params.append("zoning_typ", zoningTyp);
            }

            // Add this to the API parameters
            if (adjacentMode && adjacentParcelFilter === "Suitable Parcels") {
                params.append('is_best', '1');
            }
            else if (adjacentMode && adjacentParcelFilter !== "All") {
                params.append('is_best', '0');
            }

            if (adjacentMode) {
                if (adjacentParcelFilter === "Suitable Parcels") {
                    params.append('is_best', '1');  // Suitable parcels
                } else {
                    params.append('is_best', '0');  // Unsuitable parcels
                }
            }
            // if (is_best !== null) {
            // params.append('is_best', is_best.toString());// Add this to the API parameters
            // if (adjacentMode && adjacentParcelFilter !== "Suitable Parcels") {
            //     params.append('is_best', '0');
            // } else if (adjacentMode && adjacentParcelFilter !== "All") {
            //     params.append('is_best', '0');
            // }
            // }

            const url = adjacentMode ? `https://beta.flowlogic.com/api/assemblage/list-assemblage-parcel-data/?${params.toString()}` : `https://beta.flowlogic.com/api/parcels/list-parcel-data/?${params.toString()}`;

            const token = localStorage.getItem("authToken");
            if (!token) { handleLogout(); return; }
            const resp = await fetch(url, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json"
                },
            });

            if (resp.status === 401) { handleLogout(); return; }
            if (!resp.ok) {
                throw new Error(`HTTP ${resp.status}`);
            }

            const data = await resp.json();
            console.log("API Response:", data);



            // Extract total count from response
            const count = data?.count || data?.total || 0;
            setTotalParcelCount(count);

            // Handle different response formats
            let items = [];
            if (data?.features && Array.isArray(data.features)) {
                // FeatureCollection format
                items = data.features.map(f => f.properties || f);
            } else if (Array.isArray(data?.results)) {
                // results array format
                items = data.results;
            } else if (Array.isArray(data)) {
                // plain array format
                items = data;
            }

            // // Append if loading more (offsetNum > 0), otherwise replace
            // setApiParcels(prev => offsetNum > 0 ? [...prev, ...items] : items);

            // // Check if there's more data (if we got 100 items, there might be more)
            // setHasMoreData(items.length >= 100);

            // Ignore stale responses
            if (fetchId !== latestFetchIdRef.current) {
                console.log(`Stale API response (id ${fetchId}) — ignoring`);
                return;
            }

            // Append if loading more (offsetNum > 0), otherwise replace
            setApiParcels(prev => offsetNum > 0 ? [...prev, ...items] : items);

            // Check if there's more data using the server count when available.
            setHasMoreData(count ? offsetNum + items.length < count : items.length >= 100);

            // Debug: Log what records were actually received
            if (items.length > 0) {
                const firstParcel = items[0]?.parcel || items[0]?.parcelid || 'unknown';
                const lastParcel = items[items.length - 1]?.parcel || items[items.length - 1]?.parcelid || 'unknown';
                console.log(` Received ${items.length} parcels. First: ${firstParcel}, Last: ${lastParcel}`);
            } else {
                console.log(` Received 0 parcels from API (offsetNum: ${offsetNum})`);
            }

            console.log(`API Response: Fetched ${items.length} parcels from API for ${countyFilter} with applied filters`);
        } catch (e) {
            console.error("API fetch error:", e);
            // setApiParcels([]);
            // setHasMoreData(false);

            // Only clear if this is the latest fetch
            if (fetchId === latestFetchIdRef.current) {
                setApiParcels([]);
                setFilteredApiParcels([]);
                setHasMoreData(false);
            }
        } finally {
            // // Only reset isLoadingApi for initial load
            // if (offsetNum === 0) {
            //     setIsLoadingApi(false);
            // }
            // setIsLoadingMore(false);

            // Only update loading flags for the latest fetch
            if (fetchId === latestFetchIdRef.current) {
                if (offsetNum === 0) setIsLoadingApi(false);
                setIsLoadingMore(false);
            } else {
                // stale response — don't modify loading flags
            }
        }


    };

    // Load more parcels for pagination
    const loadMoreParcels = () => {

        //  Block pagination while search is active
        if (isParcelSearchActive) {
            console.log("Load more blocked — search is active");
            return;
        }
        if (isLoadingMore || !hasMoreData || isLoadingApi) {
            return;
        }

        const nextOffset = currentOffset + 100;
        setIsLoadingMore(true);
        setCurrentOffset(nextOffset);
        fetchApiParcelsWithCurrentFilters(nextOffset);
    };

    // Fetch total count from parcel-minmax API
    const fetchTotalCount = async (countyFilter) => {
        if (!countyFilter || countyFilter === "all") {
            setTotalParcelCount(0);
            return;
        }

        try {
            const url = adjacentMode
                ? `https://beta.flowlogic.com/api/assemblage/assemblage-minmax?county=${countyFilter}`
                : `https://beta.flowlogic.com/api/parcels/parcel-minmax/?county=${countyFilter}`;
            const token = localStorage.getItem("authToken");
            if (!token) { handleLogout(); return; }


            const resp = await fetch(url, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json"
                },
            });

            if (resp.status === 401) { handleLogout(); return; }
            if (!resp.ok) {
                throw new Error(`HTTP ${resp.status}`);
            }

            const data = await resp.json();
            console.log("Total Count API Response:", data);

            // Extract total count from response
            const count = data?.count || data?.total || 0;
            setTotalParcelCount(count);
        } catch (e) {
            console.error("Total count fetch error:", e);
            setTotalParcelCount(0);
        }
    };

    // Format currency helper
    // const formatCurrency = (num) => num ? "$" + num.toLocaleString("en-US") : "N/A";
    const formatCurrency = (num) => (num !== null && num !== undefined) ? "$" + num.toLocaleString("en-US") : "N/A";

    // Compute zone codes from API parcels
    // const zoneCodes = useMemo(() => {
    //     const codes = new Set();
    //     apiParcels.forEach((p) => {
    //         const z = p.zonedcodelocal ?? p.ZonedCodeLocal ?? p.ZonedCode ?? p.zoning ?? "Unknown";
    //         if (z) codes.add(String(z));
    //     });
    //     return [...Array.from(codes).sort()];
    // }, [apiParcels]);

    const clearFiltersForSearch = () => {
        // setPriceRange([sliderMinPrice ?? 0, sliderMaxPrice ?? 1000000]);
        // setSizeRange([sliderMinSize ?? 0, sliderMaxSize ?? 100]);
        // setZestimateRange([sliderMinZestimate ?? 0, sliderMaxZestimate ?? 1000000]);
        // setSpreadRange([sliderMinSpread ?? -100, sliderMaxSpread ?? 100]);


        if (sliderMinPrice !== undefined && sliderMaxPrice !== undefined) {
            setPriceRange([sliderMinPrice, sliderMaxPrice]);
        } else {
            setPriceRange([0, 10000000]); // Wide open fallback if no data
        }

        if (sliderMinSize !== undefined && sliderMaxSize !== undefined) {
            setSizeRange([sliderMinSize, sliderMaxSize]);
        } else {
            setSizeRange([0, 100000]); // Wide open fallback
        }

        if (sliderMinZestimate !== undefined && sliderMaxZestimate !== undefined) {
            setZestimateRange([sliderMinZestimate, sliderMaxZestimate]);
        } else {
            setZestimateRange([0, 10000000]); // Wide open fallback
        }

        if (sliderMinSpread !== undefined && sliderMaxSpread !== undefined) {
            setSpreadRange([sliderMinSpread, sliderMaxSpread]);
        } else {
            setSpreadRange([-100, 100]); // Wide open fallback
        }
        setFloodRiskFilter("All");
        setZoneCodeFilter([]);
        setListedSaleFilter("All");
        setVacantFilter("All");
        setMunicipalityFilter("All");
        setZoningTypFilter([]);

        vacantFilterTouched.current = false;
        zoneFilterTouched.current = false;
        listedSaleFilterTouched.current = false;
        municipalityFilterTouched.current = false;

        // close dropdowns
        setShowPriceFilter(false);
        setShowSizeFilter(false);
        setShowFloodFilter(false);
        setShowZoneFilter(false);
        setShowMunicipalityFilter(false);
        setShowZestimateFilter(false);
        setShowSpreadFilter(false);
        setShowListedSaleFilter(false);
        setShowZoningTypFilter(false);
    };

    // Clear all filters function
    const clearAllFilters = () => {

        //    localStorage.removeItem("ncSelectedRows");
        //     setSelectedRows([]);


        // Set flag to indicate filters are being cleared
        filtersClearedRef.current = true;

        setResetParcelSearchSignal(prev => prev + 1);
        // Clear API parcels so list can refresh with tile data if zoom >=13.1
        clearFiltersForSearch();
        setApiParcels([]);

        clickPopupRef.current?.remove();
        clickPopupRef.current = null;


        // Ensure parcel layers remain visible if county is selected
        // Use setTimeout to ensure this runs after state updates
        setTimeout(() => {
            if (selectedCounty && selectedCounty !== "all" && mapRef.current) {
                updateLeftPanelParcels(selectedCounty);
                console.log(' Clear filters: Ensuring parcel layers visible');
            }
        }, 100);
    };

    useEffect(() => {
        if (!previousBuilderLayerActiveRef.current && builderLayerActive) {
            console.log('Builder mode enabled, clearing normal filters and resetting builder drag state');
            clearAllFilters();
            setPriceRangeDragComplete(0);
            setZestimateRangeDragComplete(0);
            setSizeRangeDragComplete(0);
            setSpreadRangeDragComplete(0);
        }

        if (previousBuilderLayerActiveRef.current && !builderLayerActive) {
            console.log('Builder mode disabled, clearing all filters and resetting builder slider state');
            clearAllFilters();
            setPriceRange([sliderMinPrice, sliderMaxPrice]);
            setSizeRange([sliderMinSize, sliderMaxSize]);
            setZestimateRange([sliderMinZestimate, sliderMaxZestimate]);
            setSpreadRange([sliderMinSpread, sliderMaxSpread]);
            setPriceRangeDragComplete(0);
            setZestimateRangeDragComplete(0);
            setSizeRangeDragComplete(0);
            setSpreadRangeDragComplete(0);
        }

        if (mapRef.current && mapLoaded) {
            const map = mapRef.current;
            const setLayerVisibility = (layerId, visible) => {
                if (!map.getLayer(layerId)) return;
                map.setLayoutProperty(layerId, "visibility", visible ? "visible" : "none");
            };

            const hideNormalParcelLayers = () => {
                [
                    "parcel-points",
                    "parcel-outline",
                    "parcel-fill",
                    "parcel-hover",
                    "parcel-hitbox",
                    "parcel-selected-outline",
                    "parcel-selected-fill"
                ].forEach((layerId) => setLayerVisibility(layerId, false));
            };

            const showNormalParcelLayers = selectedCounty && selectedCounty !== "all";

            if (builderLayerActive) {
                setLayerVisibility("builder-parcel-fill", true);
                setLayerVisibility("builder-parcel-outline", true);
                hideNormalParcelLayers();
            } else {
                setLayerVisibility("builder-parcel-fill", false);
                setLayerVisibility("builder-parcel-outline", false);
                [
                    "parcel-points",
                    "parcel-outline",
                    "parcel-fill",
                    "parcel-selected-outline",
                    "parcel-selected-fill"
                ].forEach((layerId) => setLayerVisibility(layerId, showNormalParcelLayers));
            }
        }

        previousBuilderLayerActiveRef.current = builderLayerActive;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [builderLayerActive, mapLoaded, selectedCounty, sliderMinPrice, sliderMaxPrice, sliderMinSize, sliderMaxSize, sliderMinZestimate, sliderMaxZestimate, sliderMinSpread, sliderMaxSpread]);



    // Clear all filters function
    // const clearAllFilters = () => {

    //     setResetParcelSearchSignal(prev => prev + 1);

    //        //  FIX: Reset pagination state
    //     setCurrentOffset(0);
    //     setHasMoreData(true);
    //         // Clear all filters function
    // const clearAllFilters = () => {

    //     setResetParcelSearchSignal(prev => prev + 1);

    //     // ✅ FIX: Reset pagination state
    //     setCurrentOffset(0);
    //     setHasMoreData(true);
    //     setIsLoadingMore(false);

    //     // Clear API parcels so list can refresh with tile data if zoom >=13.1
    //     clearFiltersForSearch();

    //     // ✅ CRITICAL: Clear both apiParcels AND filteredApiParcels
    //     setApiParcels([]);
    //     setFilteredApiParcels([]);

    //     clickPopupRef.current?.remove();
    //     clickPopupRef.current = null;

    //     // Ensure parcel layers remain visible if county is selected
    //     // Use setTimeout to ensure this runs after state updates
    //     setTimeout(() => {
    //         if (selectedCounty && selectedCounty !== "all" && mapRef.current) {
    //             updateLeftPanelParcels(selectedCounty);
    //             console.log(' Clear filters: Ensuring parcel layers visible');
    //         }
    //     }, 100);
    // };

    //     // Clear API parcels so list can refresh with tile data if zoom >=13.1
    //     clearFiltersForSearch();
    //     setApiParcels([]);


    //     clickPopupRef.current?.remove();
    //     clickPopupRef.current = null;


    //     // Ensure parcel layers remain visible if county is selected
    //     // Use setTimeout to ensure this runs after state updates
    //     setTimeout(() => {
    //         if (selectedCounty && selectedCounty !== "all" && mapRef.current) {
    //             updateLeftPanelParcels(selectedCounty);
    //             console.log(' Clear filters: Ensuring parcel layers visible');
    //         }
    //     }, 100);
    // };

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const handleLogout = () => {

        localStorage.removeItem("access");
        localStorage.removeItem("refresh");

        localStorage.removeItem("authToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("email");
        localStorage.removeItem("isSubscribed");
        navigate("/");
    };

    // Keep authToken in sync (storage + interval)
    useEffect(() => {
        const syncToken = () => setAuthToken(localStorage.getItem("authToken"));
        window.addEventListener("storage", syncToken);
        const id = setInterval(syncToken, 30000); // re-check every 30s
        return () => {
            window.removeEventListener("storage", syncToken);
            clearInterval(id);
        };
    }, []);


    // Authentication check - redirect to login if not authenticated
    useEffect(() => {
        console.log("🔍 Auth check: token =", authToken ? "true" : "false");
        const checkTokenValidity = () => {
            if (!authToken) {
                // navigate("/");
                handleLogout();
                return;
            }

            try {
                const decoded = jwtDecode(authToken);
                const isExpired = decoded.exp * 1000 < Date.now();

                if (isExpired) {
                    console.warn("⏰ Token expired");
                    // Clear storage
                    // localStorage.removeItem("authToken");
                    // localStorage.removeItem("refreshToken");
                    // localStorage.removeItem("email");
                    // localStorage.removeItem("isSubscribed");
                    // navigate("/");
                    handleLogout();
                }
            } catch (e) {
                console.error("🚨 Token decode error:", e);
                // If token is malformed or can't be decoded

                // localStorage.removeItem("authToken");
                // navigate("/");
                handleLogout();
            }
        };

        checkTokenValidity();
        // }, [authToken, navigate]);
    }, [authToken]);

    // Fly to largest parcel when filters are applied
    useEffect(() => {
        // Only proceed if map is ready and we have API parcels
        if (!mapRef.current || !apiParcels || apiParcels.length === 0) return;

        // Check if any filters are active (including municipality)
        const hasActiveFilters =
            (vacantFilter && vacantFilter !== "All") ||
            (zoneCodeFilter && zoneCodeFilter.length > 0) ||
            (listedSaleFilter && listedSaleFilter !== "All") ||
            (floodRiskFilter && floodRiskFilter !== "All") ||
            (municipalityFilter && municipalityFilter !== "All") ||
            (Array.isArray(zoningTypFilter) ? zoningTypFilter.length > 0 : zoningTypFilter && zoningTypFilter !== "All") ||
            (priceRange[0] !== sliderMinPrice || priceRange[1] !== sliderMaxPrice) ||
            (zestimateRange[0] !== sliderMinZestimate || zestimateRange[1] !== sliderMaxZestimate) ||
            (sizeRange[0] !== sliderMinSize || sizeRange[1] !== sliderMaxSize) ||
            (spreadRange[0] !== sliderMinSpread || spreadRange[1] !== sliderMaxSpread);

        // Only fly to parcel if filters are active
        if (!hasActiveFilters) return;

        // Find parcel with maximum parcelsize
        const maxParcel = apiParcels.reduce((max, p) => {
            const currentSize = parseFloat(p.parcelsize || 0);
            const maxSize = parseFloat(max.parcelsize || 0);
            console.log(`Current parcel size: ${currentSize}, Max parcel size: ${maxSize}`);
            return currentSize > maxSize ? p : max;
        }, apiParcels[0]);

        // Fly to the coordinates of the largest parcel
        if (maxParcel && maxParcel.latitude && maxParcel.longitude) {
            const lat = parseCoordinate(maxParcel.latitude);
            const lon = parseCoordinate(maxParcel.longitude);

            if (!isValidLngLat(lon, lat)) {
                console.warn("Skipping flyTo for largest parcel with invalid coords", { lat, lon, maxParcel });
                return;
            }

            console.log(`Flying to largest parcel with size ${maxParcel.parcelsize} at [${lon}, ${lat}]`);
            console.log(`Parcel details:`, maxParcel);

            // Calculate offset for left panel (same as row click handler)
            const screenWidth = window.innerWidth;
            // const leftPanelWidth = screenWidth * 0.60;
            const leftPanelWidth = screenWidth * (listWidth / 100);
            const offset = -(leftPanelWidth / 2);

            // Use flyTo with precise coordinates and offset
            mapRef.current.flyTo({
                center: [lon, lat],
                zoom: 11,
            });
        }
    }, [apiParcels]); // Only depend on apiParcels - triggers when API returns new results after drag complete


    // Initialize map
    useEffect(() => {
        if (!mapContainerRef.current) return;
        if (mapRef.current) return; // prevent double-init

        const styleUrl = mapStyle === 'satellite'
            ? "mapbox://styles/mapbox/satellite-streets-v12"
            : "mapbox://styles/mapbox/light-v11";

        // mapRef.current = new mapboxgl.Map({
        //     container: mapContainerRef.current,
        //     style: styleUrl,
        //     center: NC_CENTER,
        //     zoom: 6.5,
        //     maxZoom: 22,
        // });

        try {
            mapRef.current = new mapboxgl.Map({
                container: mapContainerRef.current,
                style: styleUrl,
                center: NC_CENTER,
                zoom: 6.5,
                maxZoom: 22,
            });
        } catch (e) {
            console.error("Map init failed:", e);
            setMapInitError(e);
            return;
        }

        // Add map style toggle control
        class StyleToggleControl {
            onAdd(map) {
                this._map = map;
                this._container = document.createElement('div');
                this._container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group';

                this._button = document.createElement('button');
                this._button.type = 'button';
                this._button.style.width = '100px';
                this._button.style.padding = '8px';
                this._button.style.cursor = 'pointer';
                this._button.style.fontSize = '13px';
                this._button.style.fontWeight = '500';
                this._button.style.display = 'flex';
                this._button.style.alignItems = 'center';
                this._button.style.justifyContent = 'center';
                this._button.innerHTML = 'Satellite';

                this._button.onclick = () => {
                    const currentStyle = this._map.getStyle().sprite.includes('satellite') ? 'satellite' : 'light';
                    const newStyle = currentStyle === 'light' ? 'satellite' : 'light';
                    const styleUrl = newStyle === 'satellite'
                        ? "mapbox://styles/mapbox/satellite-streets-v12"
                        : "mapbox://styles/mapbox/light-v11";

                    // Update button text
                    this._button.innerHTML = newStyle === 'light' ? 'Satellite' : 'Light';

                    // Store current layers and sources before changing style
                    const currentLayers = [];
                    const currentSources = {};
                    const layerPaintProperties = {};

                    // Save custom layers (not built-in ones)
                    this._map.getStyle().layers.forEach(layer => {
                        // Save any layer that's custom or has been modified
                        if (layer.id.includes('parcel') ||
                            layer.id.includes('counties') ||
                            layer.id.includes('highlight') ||
                            layer.id === 'counties-fill' ||
                            layer.id === 'counties-outline' ||
                            layer.id === 'parcel-points' ||
                            layer.id.includes('nc-') ||
                            layer.id === 'water-ssgravitymain-layer' ||
                            layer.id === 'raleigh-2-foot-contours-layer' ||
                            layer.id === 'raleigh-2-foot-contours-labels' ||
                            // layer.id === 'builder-projects-points' ||
                            layer.id === 'builder-projects-symbols' ||
                            layer.id === 'ongoing-builder-projects-symbols' ||
                            layer.id === 'fema-flood-zones-layer' ||
                            layer.source === 'composite' && layer.id.includes('admin')) {
                            currentLayers.push(layer);
                            // Store paint properties
                            if (layer.paint) {
                                layerPaintProperties[layer.id] = layer.paint;
                            }
                        }
                    });

                    // Save custom sources
                    Object.keys(this._map.getStyle().sources).forEach(sourceId => {
                        if (sourceId.includes('parcel') ||
                            sourceId.includes('counties') ||
                            sourceId.includes('highlight') ||
                            sourceId === 'counties' ||
                            sourceId === 'water-ssgravitymain' ||
                            sourceId === 'raleigh-2-foot-contours' ||
                            sourceId === 'parcels_near_to_builder' ||
                            sourceId === 'nc_builder_communities' ||
                            sourceId === 'parcels' ||
                            sourceId === 'nc-raleigh-flood') {
                            currentSources[sourceId] = this._map.getSource(sourceId).serialize();
                        }
                    });

                    // Change map style
                    this._map.setStyle(styleUrl);

                    // Re-add layers and sources after style loads
                    this._map.once('style.load', () => {
                        // addBuilderMarkerImage(this._map);
                        // addBuilderMarkerImage(this._map, "builder-marker-icon", "/assets/building.svg");
                        addBuilderMarkerImage(this._map, "ongoing-builder-marker-icon", "/assets/builders.svg");

                        // Re-add sources
                        Object.keys(currentSources).forEach(sourceId => {
                            if (!this._map.getSource(sourceId)) {
                                this._map.addSource(sourceId, currentSources[sourceId]);
                            }
                        });

                        // Re-add layers in correct order: counties first, then parcels
                        // This ensures parcel points render on top of county fill
                        const countyLayers = currentLayers.filter(l => l.id.includes('counties'));
                        const parcelLayers = currentLayers.filter(l => l.id.includes('parcel'));
                        const utilityLayers = currentLayers.filter(l =>
                            l.id === 'water-ssgravitymain-layer' ||
                            l.id === 'raleigh-2-foot-contours-layer' ||
                            l.id === 'raleigh-2-foot-contours-labels' ||
                            // l.id === 'builder-projects-points' ||
                            l.id === 'builder-projects-symbols' ||
                            l.id === 'ongoing-builder-projects-symbols' ||
                            l.id === 'fema-flood-zones-layer'
                        );
                        const otherLayers = currentLayers.filter(l => !l.id.includes('counties') && !l.id.includes('parcel'));
                        // Add in order: counties, other, parcels (parcels on top)
                        [...countyLayers, ...otherLayers, ...parcelLayers, ...utilityLayers].forEach(layer => {
                            if (!this._map.getLayer(layer.id)) {
                                this._map.addLayer(layer);
                            }
                        });

                        // Apply visibility and opacity based on current zoom and style
                        const currentZoom = this._map.getZoom();

                        if (newStyle === 'satellite' && currentZoom >= 13.1) {
                            // Hide county layers
                            if (this._map.getLayer('counties-fill')) {
                                this._map.setLayoutProperty('counties-fill', 'visibility', 'none');
                            }
                            if (this._map.getLayer('counties-outline')) {
                                this._map.setLayoutProperty('counties-outline', 'visibility', 'none');
                            }
                            // Reduce parcel points opacity
                            if (this._map.getLayer('parcel-points')) {
                                try {
                                    this._map.setPaintProperty('parcel-points', 'fill-opacity', 0.2);
                                } catch (e) {
                                    console.warn('Could not set parcel-points opacity:', e);
                                }
                            }
                        } else if (newStyle === 'satellite' && currentZoom < 13.1) {
                            // Show county layers, restore opacity
                            if (this._map.getLayer('counties-fill')) {
                                this._map.setLayoutProperty('counties-fill', 'visibility', 'visible');
                            }
                            if (this._map.getLayer('counties-outline')) {
                                this._map.setLayoutProperty('counties-outline', 'visibility', 'visible');
                            }
                            if (this._map.getLayer('parcel-points')) {
                                try {
                                    this._map.setPaintProperty('parcel-points', 'fill-opacity', 0.8);
                                } catch (e) {
                                    console.warn('Could not set parcel-points opacity:', e);
                                }
                            }
                        } else {
                            // Light view - show all, restore opacity
                            if (this._map.getLayer('counties-fill')) {
                                this._map.setLayoutProperty('counties-fill', 'visibility', 'visible');
                            }
                            if (this._map.getLayer('counties-outline')) {
                                this._map.setLayoutProperty('counties-outline', 'visibility', 'visible');
                            }
                            if (this._map.getLayer('parcel-points')) {
                                try {
                                    this._map.setPaintProperty('parcel-points', 'fill-opacity', 0.8);
                                } catch (e) {
                                    console.warn('Could not set parcel-points opacity:', e);
                                }
                            }
                        }

                        // Re-apply paint properties to existing layers (like county fills)
                        Object.keys(layerPaintProperties).forEach(layerId => {
                            if (this._map.getLayer(layerId)) {
                                Object.keys(layerPaintProperties[layerId]).forEach(prop => {
                                    this._map.setPaintProperty(layerId, prop, layerPaintProperties[layerId][prop]);
                                });
                            }
                        });
                    });

                    setMapStyle(newStyle);
                };

                this._container.appendChild(this._button);
                return this._container;
            }

            onRemove() {
                this._container.parentNode.removeChild(this._container);
                this._map = undefined;
            }
        }

        mapRef.current.addControl(new StyleToggleControl(), 'top-right');

        // Add zoom listener to toggle county layers visibility
        mapRef.current.on('zoom', () => {
            const currentZoom = mapRef.current.getZoom();
            const currentStyle = mapRef.current.getStyle().sprite.includes('satellite') ? 'satellite' : 'light';

            // Debug: Log parcel-related layers
            const layers = mapRef.current.getStyle().layers;
            const parcelLayers = layers.filter(l => l.id.includes('parcel'));
            if (parcelLayers.length > 0) {
                // console.log('Parcel layers:', parcelLayers.map(l => l.id));
            }

            if (currentStyle === 'satellite' && currentZoom >= 13.1) {
                // Hide county layers
                if (mapRef.current.getLayer('counties-fill')) {
                    mapRef.current.setLayoutProperty('counties-fill', 'visibility', 'none');
                }
                if (mapRef.current.getLayer('counties-outline')) {
                    mapRef.current.setLayoutProperty('counties-outline', 'visibility', 'none');
                }
                // Reduce parcel points opacity
                if (mapRef.current.getLayer('parcel-points')) {
                    try {
                        mapRef.current.setPaintProperty('parcel-points', 'fill-opacity', 0.3);
                    } catch (e) {
                        console.warn('Could not set parcel-points opacity:', e);
                    }
                }
            } else if (currentStyle === 'satellite' && currentZoom < 13.1) {
                // Show county layers
                if (mapRef.current.getLayer('counties-fill')) {
                    mapRef.current.setLayoutProperty('counties-fill', 'visibility', 'visible');
                }
                if (mapRef.current.getLayer('counties-outline')) {
                    mapRef.current.setLayoutProperty('counties-outline', 'visibility', 'visible');
                }
                // Restore parcel points opacity
                if (mapRef.current.getLayer('parcel-points')) {
                    try {
                        mapRef.current.setPaintProperty('parcel-points', 'fill-opacity', 0.8);
                    } catch (e) {
                        console.warn('Could not set parcel-points opacity:', e);
                    }
                }
            } else if (currentStyle === 'light') {
                // Light view - restore full opacity
                if (mapRef.current.getLayer('parcel-points')) {
                    try {
                        mapRef.current.setPaintProperty('parcel-points', 'fill-opacity', 0.8);
                    } catch (e) {
                        console.warn('Could not set parcel-points opacity:', e);
                    }
                }
            }
        });

        mapRef.current.on("load", async () => {

            // ===============================
            // Sewage Gravity Main Source
            // ===============================

            if (!mapRef.current.getSource("water-ssgravitymain")) {
                mapRef.current.addSource("water-ssgravitymain", {
                    type: "vector",
                    tiles: [
                        "https://demo.flowlogic.com/tilesgl/data/water_ssgravitymain/{z}/{x}/{y}.pbf"
                    ],
                    minzoom: 10,
                    // maxzoom: 17,
                });
            }

            // ===============================
            // Sewage Layer
            // ===============================

            if (!mapRef.current.getLayer("water-ssgravitymain-layer")) {

                mapRef.current.addLayer({
                    id: "water-ssgravitymain-layer",
                    type: "line",
                    source: "water-ssgravitymain",
                    "source-layer": "water_ssgravitymain",

                    minzoom: 10,
                    // maxzoom: 17,

                    layout: {
                        visibility: "none",
                        "line-join": "round",
                        "line-cap": "round",
                    },

                    paint: {
                        // "line-color": "#00BCD4",
                        "line-color": "#ff3dc2",

                        //  "line-width": 2,
                        "line-width": [
                            "interpolate",
                            ["linear"],
                            ["zoom"],
                            10, 1,
                            13, 2,
                            17, 3
                        ],

                        "line-opacity": 1,
                    },
                });

                console.log(
                    "Sewage layer added:",
                    mapRef.current.getLayer("water-ssgravitymain-layer")
                );
            }

            // ===============================
            // Builder Projects Parcel Points
            // ===============================

            addBuilderMarkerImage(mapRef.current);
            // addOngoingBuilderMarkerImage(mapRef.current);

            if (!mapRef.current.getSource("parcels_near_to_builder")) {
                mapRef.current.addSource("parcels_near_to_builder", {
                    type: "vector",
                    tiles: [
                        // "http://demo.flowlogic.com:8080/data/nc_parcels_near_to_builder_projects/{z}/{x}/{y}.pbf"
                         "https://demo.flowlogic.com/tilesgl/data/nc_parcels_near_to_builder_projects/{z}/{x}/{y}.pbf"
                    ],
                    minzoom: 0,
                    maxzoom: 18,
                });
            }

            if (!mapRef.current.getLayer("builder-parcel-fill")) {
                mapRef.current.addLayer({
                    id: "builder-parcel-fill",
                    type: "fill",
                    source: "parcels_near_to_builder",
                    "source-layer": "parcels_near_to_builder",
                    minzoom: 7,
                    layout: {
                        visibility: "none",
                    },
                    paint: {
                        "fill-color": "#1c8f7d",
                        "fill-opacity": 0.35,
                    },
                });
            }

            if (!mapRef.current.getLayer("builder-parcel-outline")) {
                mapRef.current.addLayer({
                    id: "builder-parcel-outline",
                    type: "line",
                    source: "parcels_near_to_builder",
                    "source-layer": "parcels_near_to_builder",
                    minzoom: 7,
                    layout: {
                        visibility: "none",
                    },
                    paint: {
                        "line-color": "#147dff",
                        "line-width": [
                            "interpolate",
                            ["linear"],
                            ["zoom"],
                            7, 0.8,
                            13, 1.2,
                            18, 1.8
                        ],
                        "line-opacity": 0.9,
                    },
                });
            }

            // if (!mapRef.current.getLayer("builder-projects-points")) {
            //     mapRef.current.addLayer({
            //         id: "builder-projects-points",
            //         type: "circle",
            //         source: "parcels_near_to_builder",
            //         "source-layer": "parcels_near_to_builder",
            //         minzoom: 7,
            //         layout: {
            //             visibility: "none",
            //         },
            //         paint: {
            //             "circle-radius": [
            //                 "interpolate",
            //                 ["linear"],
            //                 ["zoom"],
            //                 7, 4,
            //                 12, 6,
            //                 16, 8
            //             ],
            //             "circle-color": "#1c8f7d",
            //             "circle-stroke-color": "#ffffff",
            //             "circle-stroke-width": 2,
            //             "circle-opacity": 0.95,
            //         },
            //     });
            // }


            if (!mapRef.current.getLayer("builder-projects-symbols")) {
                mapRef.current.addLayer({
                    id: "builder-projects-symbols",
                    type: "symbol",
                    source: "parcels_near_to_builder",
                    "source-layer": "parcels_near_to_builder",
                    minzoom: 10,
                    layout: {
                        visibility: "none",
                        "icon-image": "builder-marker-icon",
                        "icon-size": [
                            "interpolate",
                            ["linear"],
                            ["zoom"],
                            7, 0.45,
                            12, 0.95,
                            16, 1
                        ],
                        // "icon-anchor": "bottom",
                        "icon-anchor": "center",
                        "icon-allow-overlap": true,
                        "icon-ignore-placement": true,
                    },
                });
            }

            // addBuilderMarkerImage(mapRef.current);
            // addBuilderMarkerImage(mapRef.current, "builder-marker-icon", "/assets/building.svg");
            addBuilderMarkerImage(mapRef.current, "ongoing-builder-marker-icon", "/assets/builders.svg");
            if (!mapRef.current.getSource("nc_builder_communities")) {
                mapRef.current.addSource("nc_builder_communities", {
                    type: "vector",
                    tiles: [
                        // "http://demo.flowlogic.com:8080/data/nc_builder_communities/{z}/{x}/{y}.pbf"
                         "https://demo.flowlogic.com/tilesgl/data/nc_builder_communities/{z}/{x}/{y}.pbf"
                    ],
                    minzoom: 0,
                    maxzoom: 18,
                });
            }
            if (!mapRef.current.getLayer("ongoing-builder-projects-symbols")) {
                mapRef.current.addLayer({
                    id: "ongoing-builder-projects-symbols",
                    //  type: "circle",
                    type: "symbol",
                    source: "nc_builder_communities",
                    "source-layer": "nc_builder_communities",
                    minzoom: 10,
                    filter: [
            "in",
            ["get", "city"], 
            ["literal", ["Apex", "Cary", "Fuquay-Varina", "Garner", "Raleigh", "Rolesville", "Wendell"]]
        ],

//         filter: [
//   "all",
//   [
//     "in",
//     ["get", "city"],
//     ["literal", [
//       "Apex",
//       "Cary",
//       "Fuquay-Varina",
//       "Garner",
//       "Raleigh",
//       "Rolesville",
//       "Wendell"
//     ]]
//   ],
//   ["==", ["get", "builder"], "Toll Brothers"]
// ],
                    //           paint: {
                    //     "circle-radius": [
                    //         "interpolate",
                    //         ["linear"],
                    //         ["zoom"],
                    //         7, 4,
                    //         12, 6,
                    //         16, 8
                    //     ],
                    //     "circle-color": "#1c8f7d",
                    //     "circle-stroke-color": "#ffffff",
                    //     "circle-stroke-width": 2,
                    //     "circle-opacity": 0.95,
                    // },
                    layout: {
                        visibility: "none",
                        // "icon-image": "builder-marker-icon",
                        "icon-image": "ongoing-builder-marker-icon",
                        "icon-size": [
                            "interpolate",
                            ["linear"],
                            ["zoom"],
                            7, 0.45,
                            12, 0.95,
                            16, 1
                        ],
                        // "icon-anchor": "bottom",
                        "icon-anchor": "center",
                        "icon-allow-overlap": true,
                        "icon-ignore-placement": true,
                    },
                });
            }

                 const showOngoingBuilderProjectPopup = (e) => {
                const feature = e.features?.[0];
                if (!feature?.properties) return;

                const p = feature.properties;
                const lon = parseCoordinate(p.longitude ?? p.lon ?? p.lng ?? p.x) ?? e.lngLat.lng;
                const lat = parseCoordinate(p.latitude ?? p.lat ?? p.y) ?? e.lngLat.lat;
                if (!isValidLngLat(lon, lat)) return;

                builderOngoingProjectsPopupRef.current?.remove();
                builderOngoingProjectsPopupRef.current = new mapboxgl.Popup({
                    closeButton: true,
                    closeOnClick: true,
                    maxWidth: "260px",
                    className: "builder-project-popup",
                })
                    .setLngLat([lon, lat])
                    .setHTML(`
                        <div style="
                            background:#ffffff;
                            padding:12px;
                            border-radius:8px;
                            box-shadow:0 4px 14px rgba(0,0,0,0.16);
                            font-family:Inter, sans-serif;
                            font-size:12px;
                            line-height:1.6;
                        ">
                            <div style="font-weight:700;color:#1c8f7d;margin-bottom:6px;">Ongoing Builder Project</div>
                            <div><strong>Builder:</strong> ${p.builder || "N/A"}</div>
                            <div><strong>City:</strong> ${p.city || "N/A"}</div>
                            <div><strong>Community:</strong> ${p.community || "N/A"}</div>
                       
        
   
                          
                        </div>
                    `)
                    .addTo(mapRef.current);
            };
            //          <div><strong>Latitude:</strong> ${p.latitude || lat || "N/A"}</div>
            //  <div><strong>Longitude:</strong> ${p.longitude || lon || "N/A"}</div>  

            const showBuilderProjectPopup = (e) => {
                const feature = e.features?.[0];
                if (!feature?.properties) return;

                const p = feature.properties;
                const lon = parseCoordinate(p.longitude ?? p.lon ?? p.lng ?? p.x) ?? e.lngLat.lng;
                const lat = parseCoordinate(p.latitude ?? p.lat ?? p.y) ?? e.lngLat.lat;
                if (!isValidLngLat(lon, lat)) return;

                const parcelId = getParcelIdentifier(p);
                const isFavorite = isFavoriteParcelId(parcelId);
                const renderFavoriteButton = (button, favorite) => {
                    if (!button) return;
                    button.dataset.favorite = favorite ? "true" : "false";
                    button.innerHTML = `<img src="${favorite ? "/assets/favorite-G.svg" : "/assets/not_favorite.svg"}" alt="${favorite ? "Favorite" : "Not Favorite"}" style="width:18px;height:18px;display:block;" />`;
                    button.title = favorite ? "Remove favorite" : "Add favorite";
                };

                builderProjectsPopupRef.current?.remove();
                builderProjectsPopupRef.current = new mapboxgl.Popup({
                    closeButton: true,
                    closeOnClick: true,
                    maxWidth: "260px",
                    className: "builder-project-popup",
                })
                    .setLngLat([lon, lat])
                    .setHTML(`
                        <div style="
                            background:#ffffff;
                            padding:20px;
                            border-radius:8px;
                            box-shadow:0 4px 14px rgba(0,0,0,0.16);
                            font-family:Inter, sans-serif;
                            font-size:12px;
                            line-height:1.6;
                        ">
                            <div style="display:flex; align-items:center; justify-content:space-between; gap:8px; margin-bottom:8px;">
                                <div style="font-weight:700;color:#1c8f7d;">Parcel near Builder</div>
                                ${parcelId ? `<button type="button" class="builder-parcel-popup-favorite-btn" data-favorite="${isFavorite ? "true" : "false"}" style="border:none; background:transparent; cursor:pointer; line-height:1; padding:0;" title="${isFavorite ? "Remove favorite" : "Add favorite"}"><img src="${isFavorite ? "/assets/favorite-G.svg" : "/assets/not_favorite.svg"}" alt="${isFavorite ? "Favorite" : "Not Favorite"}" style="width:18px;height:18px;display:block;" /></button>` : ""}
                            </div>
                            <div><strong>Parcel:</strong> ${p.parcel || "N/A"}</div>
                            <div><strong>County:</strong> ${p.county || "N/A"}</div>
                            <div><strong>Address:</strong> ${p.addrline1 || "N/A"}</div>
        
        <div style="margin-top: 6px; border-top: 1px dashed #eee; padding-top: 6px;"></div>
        
 
                           
                        </div>
                    `)
                    .addTo(mapRef.current);

                           // <div>
        //     <strong style="color: #2c3e50;">Nearby Builders:</strong> 
        //     <span style="color: #0f5d51; font-weight: 600;">
        //         ${p.nearby_builder_names || "N/A"}
        //     </span>
        // </div>
                const favoriteButton = builderProjectsPopupRef.current
                    ?.getElement()
                    ?.querySelector(".builder-parcel-popup-favorite-btn");

                if (favoriteButton) {
                    favoriteButton.addEventListener("click", async (event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        if (!parcelId || favoriteButton.disabled) return;

                        const currentlyFavorite = favoriteButton.dataset.favorite === "true";
                        favoriteButton.disabled = true;
                        favoriteButton.style.cursor = "wait";

                        try {
                            if (currentlyFavorite) {
                                await removeFavoriteParcelIds([parcelId]);
                                setFavoriteParcelIds((prev) => (prev || []).filter((id) => String(id) !== String(parcelId)));
                                setFavoriteParcels((prev) => (prev || []).filter((item) => String(getParcelIdentifier(item)) !== String(parcelId)));
                                renderFavoriteButton(favoriteButton, false);
                            } else {
                                await addFavoriteParcelIds([parcelId]);
                                setFavoriteParcelIds((prev) => Array.from(new Set([...(prev || []), parcelId])));
                                setFavoriteParcels((prev) => {
                                    const existing = new Set((prev || []).map(getParcelIdentifier).filter(Boolean));
                                    if (existing.has(parcelId)) return prev || [];
                                    return [...(prev || []), p];
                                });
                                renderFavoriteButton(favoriteButton, true);
                            }
                        } catch (error) {
                            console.error("Builder parcel popup favorite toggle failed", error);
                        } finally {
                            favoriteButton.disabled = false;
                            favoriteButton.style.cursor = "pointer";
                        }
                    });
                }
            };

            //     <div><strong>Latitude:</strong> ${p.latitude || lat || "N/A"}</div>
            //  <div><strong>Longitude:</strong> ${p.longitude || lon || "N/A"}</div> 
       

            // ["builder-projects-points", "builder-projects-symbols"].forEach((layerId) => {
            ["builder-projects-symbols", "builder-parcel-fill", "builder-parcel-outline"].forEach((layerId) => {
                mapRef.current.on("click", layerId, showBuilderProjectPopup);
                mapRef.current.on("mouseenter", layerId, () => {
                    mapRef.current.getCanvas().style.cursor = "pointer";
                });
                mapRef.current.on("mouseleave", layerId, () => {
                    mapRef.current.getCanvas().style.cursor = "";
                });
            });

                 ["ongoing-builder-projects-symbols"].forEach((layerId) => {
                mapRef.current.on("click", layerId, showOngoingBuilderProjectPopup);
                mapRef.current.on("mouseenter", layerId, () => {
                    mapRef.current.getCanvas().style.cursor = "pointer";
                });
                mapRef.current.on("mouseleave", layerId, () => {
                    mapRef.current.getCanvas().style.cursor = "";
                });
            });
            
            // ===============================
            // Raleigh 2 Foot Contours Source
            // ===============================

            if (!mapRef.current.getSource("raleigh-2-foot-contours")) {
                mapRef.current.addSource("raleigh-2-foot-contours", {
                    type: "vector",
                    tiles: [
                        "https://demo.flowlogic.com/tilesgl/data/raleigh_2_foot_contours/{z}/{x}/{y}.pbf"
                    ],
                    minzoom: 10,
                    // maxzoom: 17,
                });
            }

            // ===============================
            // Raleigh 2 Foot Contours Layer
            // ===============================

            if (!mapRef.current.getLayer("raleigh-2-foot-contours-layer")) {
                mapRef.current.addLayer({
                    id: "raleigh-2-foot-contours-layer",
                    type: "line",
                    source: "raleigh-2-foot-contours",
                    "source-layer": "raleigh_2_foot_contours",
                    minzoom: 10,
                    // maxzoom: 17,
                    layout: {
                        visibility: "none",
                        "line-join": "round",
                        "line-cap": "round",
                    },

                    paint: {
                        "line-color": "#8a492b",
                        "line-width": [
                            "interpolate",
                            ["linear"],
                            ["zoom"],
                            10, 0.5,
                            13, 1,
                            17, 1.6
                        ],
                        "line-opacity": 0.85,
                    },
                });
            }

            // LAYER 2: The Elevation Labels (Add this right below Layer 1)
            if (!mapRef.current.getLayer("raleigh-2-foot-contours-labels")) {
                mapRef.current.addLayer({
                    id: "raleigh-2-foot-contours-labels",
                    type: "symbol", //  This layer type is REQUIRED to render text
                    source: "raleigh-2-foot-contours",
                    "source-layer": "raleigh_2_foot_contours",
                    minzoom: 13, // Recommended to show text only when zoomed in close enough to read it
                    layout: {
                        // Keep visibility in sync with your line layer toggle if needed
                        // visibility: "visible",
                        visibility: "none",

                        // Pulls the 'elev' key from your MBTile properties and parses to text
                        "text-field": ["to-string", ["get", "elev"]],

                        // Standard cross-compatible map fonts
                        "text-font": ["Open Sans Regular", "Arial Unicode MS Regular"],
                        "text-size": 11,

                        // Layout rules to lock text down on top of your line geometry paths
                        "symbol-placement": "line",
                        "text-max-angle": 45,
                        "symbol-spacing": 300,
                        "text-keep-upright": true,
                        // This ensures text doesn't overlap or look clipped along curves
                        "text-padding": 10,
                        "text-pitch-alignment": "viewport"
                    },
                    paint: {
                        "text-color": "#574949",
                        // "text-color": "#8a492b", // Matches your line color
                        "text-halo-color": "#ffffff", // Creates a clean cut white badge under numbers
                        "text-halo-width": 2, // Clears the contour line path under the text for readability
                        "text-halo-blur": 0.5
                    },
                });
            }

            // ==========================================
            // Contour Hover Popup & Highlight Events
            // ==========================================

            //         mapRef.current.on("mousemove", "raleigh-2-foot-contours-layer", (e) => {
            //             // Optional zoom restriction (adjust if needed)
            //             if (mapRef.current.getZoom() <= 11) return;

            //             const feature = e.features?.[0];
            //             if (!feature?.properties) return;

            //             const p = feature.properties;
            //             const shapeLength = p.shape_Leng;

            //             // Highlight the active contour line using shape_Leng as the unique key
            //             if (mapRef.current.getLayer("contour-hover-highlight") && shapeLength) {
            //                 mapRef.current.setFilter("contour-hover-highlight", ["==", ["get", "shape_Leng"], shapeLength]);
            //                 mapRef.current.setPaintProperty("contour-hover-highlight", "line-opacity", 1);
            //             }

            //             // Remove previous contour popup safely
            //             contourHoverPopupRef.current?.remove();

            //             // Create and render new popup
            //             contourHoverPopupRef.current = new mapboxgl.Popup({
            //                 closeButton: false,
            //                 closeOnClick: false
            //             })
            //                 .setLngLat(e.lngLat)
            //                 .setHTML(`
            //     <div style="
            //         background: #fff;
            //         padding: 10px;
            //         border-radius: 6px;
            //         box-shadow: 0 2px 6px rgba(0,0,0,0.15);
            //         font-family: Inter, sans-serif;
            //         font-size: 12px;
            //         width: 180px;
            //     ">

            // <strong style="color: #8a492b;">Contour Elevation:</strong> ${p.elev || "N/A"}<br>
            //     </div>
            // `)
            //                 .addTo(mapRef.current);

            //             mapRef.current.getCanvas().style.cursor = "pointer";
            //         });




            // mapRef.current.on("mouseleave", "raleigh-2-foot-contours-layer", () => {
            //     // Clean up popup
            //     contourHoverPopupRef.current?.remove();
            //     contourHoverPopupRef.current = null;

            //     mapRef.current.getCanvas().style.cursor = "";

            //     // Clear the line highlight
            //     if (mapRef.current.getLayer("contour-hover-highlight")) {
            //         mapRef.current.setPaintProperty("contour-hover-highlight", "line-opacity", 0);
            //     }
            // });

            // Highlight layer for the hovered contour line
            if (!mapRef.current.getLayer("contour-hover-highlight")) {
                mapRef.current.addLayer({
                    id: "contour-hover-highlight",
                    type: "line",
                    source: "raleigh-2-foot-contours",
                    "source-layer": "raleigh_2_foot_contours",
                    layout: {
                        "line-join": "round",
                        "line-cap": "round",
                    },
                    paint: {
                        "line-color": "#4a90e2", // Clear blue highlight, or choose a bolder brown like #5c321d
                        "line-width": 3,
                        "line-opacity": 0, // Hidden by default
                    },
                    // Filter out everything initially using a property that likely won't match a blank string
                    filter: ["==", ["get", "shape_Leng"], ""],
                });
            }

            // ===============================
            // MOVE ABOVE PARCELS
            // ===============================

            try {
                //   mapRef.current.moveLayer("builder-projects-points");
                mapRef.current.moveLayer("builder-projects-symbols");
                mapRef.current.moveLayer("ongoing-builder-projects-symbols");
                mapRef.current.moveLayer(
                    "water-ssgravitymain-layer"
                );

                mapRef.current.moveLayer(
                    "water-ssgravitymain-layer"
                );
                mapRef.current.moveLayer(
                    "raleigh-2-foot-contours-layer"
                );
                mapRef.current.moveLayer("raleigh-2-foot-contours-labels");


            } catch (e) {
                console.warn("Could not move utility layers", e);
            }


//             try {
//     const map = mapRef.current;
//     const parcelId = "parcel-outline"; // <-- Replace with your actual parcel layer ID

//     if (map.getLayer(parcelId)) {
//         // The safest trick: Move the parcel layer BEHIND your lowest utility layer.
//         // This instantly pushes all the utility layers above it.
//         // map.moveLayer(parcelId, "raleigh-2-foot-contours-layer");
        
//         // Alternatively, if you want to explicitly move the utilities one by one 
//         // to the very top of the map stack (above everything including parcels):
        
//         map.moveLayer("raleigh-2-foot-contours-layer");
//         map.moveLayer("raleigh-2-foot-contours-labels");
//         map.moveLayer("water-ssgravitymain-layer");
//         map.moveLayer("builder-projects-symbols");
//         map.moveLayer("ongoing-builder-projects-symbols");
        
        
//     } else {
//         console.warn(`Parcel layer '${parcelId}' not found in the map style yet.`);
//     }

// } catch (e) {
//     console.warn("Could not reorder layers", e);
// }

            // ===============================
            // NC Raleigh Flood Source
            // ===============================

            if (!mapRef.current.getSource("nc-raleigh-flood")) {
                mapRef.current.addSource("nc-raleigh-flood", {
                    type: "vector",
                    tiles: [
                        "https://demo.flowlogic.com/tilesgl/data/nc_raleigh_flood_test/{z}/{x}/{y}.pbf"
                    ],
                    minzoom: 10,
                    // maxzoom: 17,
                    // url: "http://demo.flowlogic.com:8080/data/nc_raleigh_flood_test.json",
                });
            }

            // ===============================
            // NC Raleigh Flood Layer (Polygons)
            // ===============================

            if (!mapRef.current.getLayer("fema-flood-zones-layer")) {
                mapRef.current.addLayer({
                    id: "fema-flood-zones-layer",
                    type: "fill",
                    // type: "line",
                    source: "nc-raleigh-flood",
                    // "source-layer": "nc_raleigh_flood",
                    "source-layer": "nc_raleigh_flood_test",

                    minzoom: 10,
                    // maxzoom: 14,
                    layout: {
                        visibility: "none",
                    },
                    paint: {
                        "fill-color": [
                            // "line-color": [
                            "match",
                            ["get", "FLD_ZONE"],
                            "A", "#e31a1c",   // High Risk: Red
                            // "AE", "#1f78b4",  // High Risk: Blue
                            "AE", "#166da7",  // High Risk: Blue

                            // "X", "#fed976",   // Moderate/Low Risk: Yellow
                            // "X", "#79d47d",   // Moderate/Low Risk: Yellow new
                            "X", "rgba(248, 243, 243, 0)",   // Moderate/Low Risk: Yellow
                            // "X", "#4bfc54",   // Moderate/Low Risk: Yellow
                            // "#b9b9b9"         // Default: Gray
                            "rgba(248, 243, 243, 0)"
                        ],
                        // "fill-color": "#b1982b" ,
                        // "fill-opacity": .8,
                        // "fill-outline-color": "#ffffff"

                        "fill-opacity": 0.7,
                        "fill-outline-color": [
                            "match",
                            ["get", "FLD_ZONE"],
                            "X", "transparent", // Optional: hide outline for "X" as well
                            "#ffffff"           // Keep white outline for A and AE
                        ]
                    },
                });
            }

            // ==========================================
            // FEMA Flood Zone Hover Popup & Highlight
            // ==========================================

            mapRef.current.on("mousemove", "fema-flood-zones-layer", (e) => {
                const feature = e.features?.[0];
                if (!feature?.properties) return;

                const p = feature.properties;

                // We use ZONE_SUBTY or FLD_ZONE to target and isolate the specific shape boundary 
                const zoneSubtype = p.ZONE_SUBTY || "";
                const floodZone = p.FLD_ZONE || "";

                // 1. Trigger the border highlight around the hovered area
                // if (mapRef.current.getLayer("fema-flood-zones-highlight")) {
                //     mapRef.current.setFilter("fema-flood-zones-highlight", [
                //         "all",
                //         ["==", ["get", "FLD_ZONE"], floodZone],
                //         ["==", ["get", "ZONE_SUBTY"], zoneSubtype]
                //     ]);
                //     mapRef.current.setPaintProperty("fema-flood-zones-highlight", "line-opacity", 1);
                // }

                // 2. Clear out any older instance of the flood popup safely
                // floodHoverPopupRef.current?.remove();

                // 3. Build and position the new popup
                // floodHoverPopupRef.current = new mapboxgl.Popup({
                //     closeButton: false,
                //     closeOnClick: false
                // })
                // .setLngLat(e.lngLat)
                // .setHTML(`
                //     <div style="
                //         background: #fff;
                //         padding: 12px;
                //         border-radius: 6px;
                //         box-shadow: 0 2px 8px rgba(0,0,0,0.18);
                //         font-family: Inter, sans-serif;
                //         font-size: 12px;
                //         width: 240px;
                //         line-height: 1.4;
                //     ">
                //         <div style="border-bottom: 2px solid #1f78b4; margin-bottom: 8px; padding-bottom: 4px;">
                //             <strong style="color: #1f78b4; font-size: 13px;">FEMA Flood Zone Information</strong>
                //         </div>

                //         <div style="margin-bottom: 4px;">
                //             <strong>Flood Zone:</strong> 
                //             <span style="background: #f0f0f0; padding: 2px 6px; border-radius: 4px; font-weight: bold;">
                //                 ${floodZone || "N/A"}
                //             </span>
                //         </div>

                //         <div style="margin-bottom: 4px;">
                //             <strong>Zone Subtype:</strong> ${zoneSubtype || "N/A"}
                //         </div>

                //         <div>
                //             <strong>Study Type:</strong> ${p.STUDY_TYP || "N/A"}
                //         </div>
                //     </div>
                // `)
                // .addTo(mapRef.current);

                // mapRef.current.getCanvas().style.cursor = "pointer";
            });

            // mapRef.current.on("mouseleave", "fema-flood-zones-layer", () => {
            //     // Drop the active popup reference
            //     floodHoverPopupRef.current?.remove();
            //     floodHoverPopupRef.current = null;

            //     mapRef.current.getCanvas().style.cursor = "";

            //     // Turn off the border highlight completely
            //     if (mapRef.current.getLayer("fema-flood-zones-highlight")) {
            //         mapRef.current.setPaintProperty("fema-flood-zones-highlight", "line-opacity", 0);
            //     }
            // });


            // ==========================================
            // FEMA Flood Zone Highlight Outline Layer
            // ==========================================

            // if (!mapRef.current.getLayer("fema-flood-zones-highlight")) {
            //     mapRef.current.addLayer({
            //         id: "fema-flood-zones-highlight",
            //         type: "line", // Line layer cleanly outlines your hovered fill polygon
            //         source: "nc-raleigh-flood",
            //         "source-layer": "nc_raleigh_flood",
            //         layout: {
            //             "line-join": "round",
            //             "line-cap": "round"
            //         },
            //         paint: {
            //             "line-color": "#00ffff", // Neon cyan highlight box (stands out against blue/red fills)
            //             "line-width": 2.5,
            //             "line-opacity": 0 // Hidden by default
            //         },
            //         // Filter everything out initially using a temporary matching block
            //         filter: ["==", ["get", "ZONE_SUBTY"], ""]
            //     });
            // }

            // ===============================
            // MOVE LAYERS TO TOP
            // ===============================

            try {
                // Moving them to the top so they sit above base map features
                //    mapRef.current.moveLayer("builder-projects-points");
                mapRef.current.moveLayer("builder-projects-symbols");
                mapRef.current.moveLayer("ongoing-builder-projects-symbols");
                mapRef.current.moveLayer("fema-flood-zones-layer");
                mapRef.current.moveLayer("water-ssgravitymain-layer");
                mapRef.current.moveLayer("raleigh-2-foot-contours-layer");
                mapRef.current.moveLayer("raleigh-2-foot-contours-labels");

            } catch (e) {
                console.warn("Could not move utility layers", e);
            }

            class ResetViewControl {
                constructor({ initialCenter, initialZoom, popupRef, customPopupRef }) {
                    this.initialCenter = initialCenter;
                    this.initialZoom = initialZoom;
                    this.popupRef = popupRef;
                    this.customPopupRef = customPopupRef;
                }

                onAdd(map) {
                    this.map = map;

                    this.button = document.createElement("button");
                    this.button.className = "mapboxgl-ctrl-icon mapboxgl-ctrl-reset";
                    this.button.type = "button";
                    this.button.title = "Reset View";
                    this.button.innerHTML = "&#x21BB;";

                    this.button.addEventListener("click", () => {
                        this.map.flyTo({
                            center: this.initialCenter,
                            zoom: this.initialZoom,
                            essential: true,
                        });


                        // setSelectedCounty("chatham");
                        // setSelectedCounty("All");


                    });

                    this.container = document.createElement("div");
                    this.container.className = "mapboxgl-ctrl mapboxgl-ctrl-group";
                    this.container.appendChild(this.button);

                    return this.container;
                }

                onRemove() {
                    this.container.parentNode.removeChild(this.container);
                    this.map = undefined;
                }
            }



            // 1. Counties will be loaded from vector tiles after map is ready
            // We'll query the vector tiles after they load to extract county names and centers


            // Add custom legend control (bottom-left, horizontal)
            class LegendControl {
                onAdd(map) {
                    this._map = map;
                    this._container = document.createElement('div');
                    this._container.className = 'mapboxgl-ctrl';
                    this._container.style.background = '#fff';
                    this._container.style.padding = '6px 10px';
                    this._container.style.borderRadius = '4px';
                    this._container.style.boxShadow = '0 0 0 2px rgba(0,0,0,0.1)';
                    this._container.style.fontFamily = 'Inter, -apple-system, sans-serif';
                    this._container.style.fontSize = '11px';
                    this._container.style.display = 'none'; // Initially hidden
                    this._container.style.gap = '10px';
                    this._container.style.alignItems = 'center';

                    const zones = [
                        { code: 'X', color: '#4CAF50' },
                        { code: 'AE', color: '#FF9800' },
                        { code: 'VE', color: '#E91E63' },
                        { code: 'A', color: '#2196F3' },
                        { code: 'Other', color: '#9fa2e7' }
                    ];

                    zones.forEach(zone => {
                        const item = document.createElement('div');
                        item.style.display = 'flex';
                        item.style.alignItems = 'center';
                        item.style.gap = '4px';

                        const colorBox = document.createElement('div');
                        colorBox.style.width = '14px';
                        colorBox.style.height = '10px';
                        colorBox.style.background = zone.color;
                        colorBox.style.borderRadius = '2px';
                        colorBox.style.border = '1px solid rgba(0,0,0,0.15)';

                        const label = document.createElement('span');
                        label.textContent = zone.code;
                        label.style.fontWeight = '500';
                        label.style.fontSize = '10px';
                        label.style.color = '#000';


                        item.appendChild(colorBox);
                        item.appendChild(label);
                        this._container.appendChild(item);
                    });

                    return this._container;
                }

                onRemove() {
                    this._container.parentNode.removeChild(this._container);
                    this._map = undefined;
                }
            }

            const legendControl = new LegendControl();
            mapRef.current.addControl(legendControl, 'bottom-left');
            legendControlRef.current = legendControl;

            //  Reset Control
            const resetControl = new ResetViewControl({
                initialCenter: NC_CENTER,
                initialZoom: 6.5,
                // popupRef: popupRef,
                // customPopupRef: customPopupRef
            });

            mapRef.current.addControl(resetControl, 'bottom-right');


            // Add zoom controls (bottom-right)
            mapRef.current.addControl(new mapboxgl.NavigationControl(), 'bottom-right');

            // const fetchNearbyParcels = async (polygonGeometry) => {
            const fetchNearbyParcels = async (polygonGeometry, county, offset = 0, append = false) => {
                const token = localStorage.getItem("authToken");
                if (!token) { handleLogout(); return; }

                if (!polygonGeometry) return;

                nearbyPolygonRef.current = polygonGeometry;
                nearbyCountyRef.current = county;

                if (append) {
                    setIsLoadingMoreNearby(true);
                } else {
                    setIsLoadingNearby(true);
                    setNearbyParcels([]);
                    setNearbyOffset(0);
                    setNearbyTotalCount(0);
                    setNearbyHasMore(false);
                    nearbyOffsetRef.current = 0;
                    nearbyHasMoreRef.current = false;
                }
                nearbyLoadingRef.current = true;
                setNearbyError(null);

                try {
                    const body = {
                        polygon: polygonGeometry,
                        limit: NEARBY_PAGE_LIMIT,
                        offset,
                    };

                    //  Only add county if valid
                    if (county && county !== "all") {
                        body.county = county;
                    }
                    const resp = await fetch("https://beta.flowlogic.com/api/parcels/nearby/", {
                        method: "POST",
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify(body),
                        // body: JSON.stringify({
                        //     // polygon: polygonGeometry,
                        //     polygon: polygonGeometry,
                        //     // limit: 50,
                        //     county: selectedCounty !== "all" ? selectedCounty : undefined,
                        // }),
                    });

                    if (resp.status === 401) { handleLogout(); return; }
                    if (!resp.ok) throw new Error(`Nearby API failed: ${resp.status}`);

                    const data = await resp.json();

                    // Handle various response shapes
                    const items =
                        data?.features?.map(f => f.properties || f) ||
                        data?.results ||
                        (Array.isArray(data) ? data : []);

                    const count = Number.isFinite(Number(data?.count)) ? Number(data.count) : null;
                    const responseLimit = Number.isFinite(Number(data?.limit)) ? Number(data.limit) : NEARBY_PAGE_LIMIT;
                    const nextOffset = offset + items.length;

                    setNearbyParcels(prev => append ? [...prev, ...items] : items);
                    setNearbyOffset(nextOffset);
                    setNearbyTotalCount(count ?? nextOffset);
                    setNearbyHasMore(count !== null ? nextOffset < count : items.length >= responseLimit);
                    nearbyOffsetRef.current = nextOffset;
                    nearbyHasMoreRef.current = count !== null ? nextOffset < count : items.length >= responseLimit;
                } catch (err) {
                    console.error("Nearby parcels fetch error:", err);
                    setNearbyError("Failed to load nearby parcels.");
                } finally {
                    setIsLoadingNearby(false);
                    setIsLoadingMoreNearby(false);
                    nearbyLoadingRef.current = false;
                }
            };

            const loadMoreNearbyParcels = () => {
                if (nearbyLoadingRef.current || !nearbyHasMoreRef.current) return;
                fetchNearbyParcels(
                    nearbyPolygonRef.current,
                    nearbyCountyRef.current,
                    nearbyOffsetRef.current,
                    true
                );
            };
            nearbyLoadMoreRef.current = loadMoreNearbyParcels;

            const loadNearbyBuilderParcelsFromPolygon = (polygonFeature, map) => {
                if (!polygonFeature || !map || !map.getSource("parcels_near_to_builder")) {
                    setNearbyParcels([]);
                    setNearbyOffset(0);
                    setNearbyTotalCount(0);
                    setNearbyHasMore(false);
                    nearbyOffsetRef.current = 0;
                    nearbyHasMoreRef.current = false;
                    return;
                }

                setIsLoadingNearby(true);
                setNearbyError(null);

                try {
                    const polygon = polygonFeature.type === "Feature"
                        ? polygonFeature
                        : { type: "Feature", geometry: polygonFeature, properties: {} };

                    const sourceFeatures = map.querySourceFeatures("parcels_near_to_builder", {
                        sourceLayer: "parcels_near_to_builder",
                    }) || [];

                    const unique = new Map();
                    sourceFeatures.forEach((feature) => {
                        if (!feature || !feature.geometry) return;
                        const tileFeature = feature.type === "Feature"
                            ? feature
                            : { type: "Feature", geometry: feature.geometry, properties: feature.properties || {} };

                        try {
                            if (!turf.booleanIntersects(polygon, tileFeature)) return;
                        } catch (err) {
                            return;
                        }

                        const props = tileFeature.properties || {};
                        const id = props.parcel || props.parcelid || props.id || props.geo_id;
                        if (id && !unique.has(id)) {
                            unique.set(id, { ...props });
                        }
                    });

                    const items = Array.from(unique.values());
                    setNearbyParcels(items);
                    setNearbyOffset(0);
                    setNearbyTotalCount(items.length);
                    setNearbyHasMore(false);
                    nearbyOffsetRef.current = 0;
                    nearbyHasMoreRef.current = false;
                } catch (err) {
                    console.error("Builder MBTiles nearby parcel query failed:", err);
                    setNearbyError("Failed to load nearby builder parcels.");
                    setNearbyParcels([]);
                    setNearbyOffset(0);
                    setNearbyTotalCount(0);
                    setNearbyHasMore(false);
                    nearbyOffsetRef.current = 0;
                    nearbyHasMoreRef.current = false;
                } finally {
                    setIsLoadingNearby(false);
                    setIsLoadingMoreNearby(false);
                    nearbyLoadingRef.current = false;
                }
            };

            // Add MapboxDraw
            const draw = new MapboxDraw({
                displayControlsDefault: false,
                controls: {
                    polygon: true,
                    trash: true
                },
                styles: [
                    // ACTIVE (being drawn)
                    // line stroke
                    {
                        "id": "gl-draw-line",
                        "type": "line",
                        "filter": ["all", ["==", "$type", "LineString"], ["!=", "mode", "static"]],
                        "layout": {
                            "line-cap": "round",
                            "line-join": "round"
                        },
                        "paint": {
                            "line-color": "#FF8C00",
                            "line-dasharray": [0.2, 2],
                            "line-width": 2
                        }
                    },
                    // polygon fill
                    {
                        "id": "gl-draw-polygon-fill",
                        "type": "fill",
                        "filter": ["all", ["==", "$type", "Polygon"], ["!=", "mode", "static"]],
                        "paint": {
                            "fill-color": "#FF8C00",
                            "fill-outline-color": "#FF8C00",
                            "fill-opacity": 0.6
                        }
                    },
                    // polygon outline stroke
                    {
                        "id": "gl-draw-polygon-stroke-active",
                        "type": "line",
                        "filter": ["all", ["==", "$type", "Polygon"], ["!=", "mode", "static"]],
                        "layout": {
                            "line-cap": "round",
                            "line-join": "round"
                        },
                        "paint": {
                            "line-color": "#FF8C00",
                            "line-dasharray": [0.2, 2],
                            "line-width": 2
                        }
                    },
                    // vertex point halos
                    {
                        "id": "gl-draw-polygon-and-line-vertex-halo-active",
                        "type": "circle",
                        "filter": ["all", ["==", "meta", "vertex"], ["==", "$type", "Point"], ["!=", "mode", "static"]],
                        "paint": {
                            "circle-radius": 6,
                            "circle-color": "#FFF"
                        }
                    },
                    // vertex points
                    {
                        "id": "gl-draw-polygon-and-line-vertex-active",
                        "type": "circle",
                        "filter": ["all", ["==", "meta", "vertex"], ["==", "$type", "Point"], ["!=", "mode", "static"]],
                        "paint": {
                            "circle-radius": 4,
                            "circle-color": "#FF8C00",
                        }
                    }
                ]
            });
            mapRef.current.addControl(draw, 'top-left');
            drawRef.current = draw;

            const drawControlContainer = mapContainerRef.current?.querySelector(".mapbox-gl-draw");
            if (drawControlContainer) {
                drawControlRef.current = drawControlContainer;
                drawControlContainer.style.display = "none";
            }

            const deleteDrawnPolygons = () => {
                try {
                    draw.changeMode("simple_select");
                } catch (e) { }

                try {
                    draw.deleteAll();
                } catch (e) {
                    console.warn("Failed to clear drawn polygons", e);
                }
            };

            setTimeout(() => {
                const trashButton = mapContainerRef.current?.querySelector(".mapbox-gl-draw_trash");
                if (!trashButton) return;

                trashButton.addEventListener("click", (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    deleteDrawnPolygons();
                    handleClearAll();
                }, true);
            }, 0);

            const setDrawingParcelSelection = (isDrawing) => {
                isDrawingParcelSelectionRef.current = isDrawing;

                if (isDrawing) {
                    hoverPopupRef.current?.remove();
                    hoverPopupRef.current = null;

                    clickPopupRef.current?.remove();
                    clickPopupRef.current = null;

                    if (mapRef.current?.getLayer("parcel-hover")) {
                        mapRef.current.setPaintProperty("parcel-hover", "line-opacity", 0);
                    }

                    if (mapRef.current?.getCanvas()) {
                        mapRef.current.getCanvas().style.cursor = "";
                    }
                }
            };

            // Master clean-up function to instantly hide the pop-up and empty data sets
            const handleClearAll = (reloadBuilderFeatures = true) => {
                setDrawingParcelSelection(false);
                setDrawnCoordinates(null); // This instantly unmounts/hides your sidebar pop-up card
                setDrawnArea(null);
                setDrawnParcelIds([]);
                setNearbyParcels([]);
                setNearbyError(null);
                setNearbyOffset(0);
                setNearbyTotalCount(0);
                setNearbyHasMore(false);
                setIsLoadingMoreNearby(false);
                nearbyOffsetRef.current = 0;
                nearbyHasMoreRef.current = false;
                nearbyLoadingRef.current = false;
                drawnPolygonRef.current = null;

                if (reloadBuilderFeatures && !skipBuilderFeatureReloadRef.current && builderLayerActive && mapRef.current) {
                    loadBuilderLayerFeatures(mapRef.current);
                }

                //     if (draw && typeof draw.deleteAll === 'function') {
                //     draw.deleteAll();
                // }

                // Ensure Mapbox Draw's internal state engine is forced empty as well
                deleteDrawnPolygons();
            };

            const updateArea = (e) => {
                setDrawingParcelSelection(false);
                const data = draw.getAll();
                // if (data.features.length > 0) {
                if (data && data.features && data.features.length > 0) {
                    const polygonFeature = data.features[data.features.length - 1];
                    const coords = polygonFeature.geometry.coordinates;
                    setDrawnCoordinates(JSON.stringify(coords, null, 2));
                    // Calculate area in square meters using Turf
                    const areaInSquareMeters = turf.area(polygonFeature);
                    //  Convert square meters to acres 
                    const areaInAcres = areaInSquareMeters * 0.000247105;
                    //  Save to a state variable
                    setDrawnArea(areaInAcres.toFixed(2));
                    drawnPolygonRef.current = polygonFeature;

                    // Call builder MBTiles query for parcels inside the drawn polygon area
                    // fetchNearbyParcels(polygonFeature.geometry, selectedCountyRef.current);
                    loadNearbyBuilderParcelsFromPolygon(polygonFeature, mapRef.current);
                   
                    setDrawnParcelIds([]);
                                        //  try {
                                        //     const bbox = turf.bbox(polygonFeature);
                                        //     const p1 = mapRef.current.project([bbox[0], bbox[1]]);
                                        //     const p2 = mapRef.current.project([bbox[2], bbox[3]]);
                    
                                        //     const features = mapRef.current.queryRenderedFeatures(
                                        //         [p1, p2],
                                        //         { layers: ['parcel-points', 'parcel-selected-fill', 'parcel-outline', 'parcel-hover'] }
                                        //     );
                    
                                        //     const intersectingParcels = new Set();
                                        //     features.forEach(f => {
                                        //         try {
                                        //             if (f.geometry && turf.booleanIntersects(polygonFeature, f)) {
                                        //                 const pid = f.properties.parcel || f.properties.parcelid || f.properties.PARCEL || f.properties.id;
                                        //                 if (pid) intersectingParcels.add(pid);
                                        //             }
                                        //         } catch (err) {
                                        //             // Ignore intersection errors on invalid geometry
                                        //         }
                                        //     });
                                        //     setDrawnParcelIds(Array.from(intersectingParcels));
                                        // } catch (error) {
                                        //     console.error("Error querying parcels:", error);
                                        //     setDrawnParcelIds([]);
                                        // }
                } else {
                    // setDrawnCoordinates("");
                    // setDrawnParcelIds([]);
                    // setDrawnArea(null);
                    // setNearbyParcels([]);
                    // setNearbyError(null);
                    handleClearAll();
                }
            };

            mapRef.current.on('draw.modechange', (e) => {
                setDrawingParcelSelection(e.mode === 'draw_polygon');
            });
            mapRef.current.on('draw.create', updateArea);
            mapRef.current.on('draw.update', updateArea);
            mapRef.current.on('draw.delete', updateArea);
            mapRef.current.on('draw.delete', handleClearAll);

              class BuilderProjectsControl {
                    constructor(setShowFemaLayer, setBuilderLayerActive, setBuilderLayerFeatures, loadBuilderLayerFeatures) {
                        this.setShowFemaLayer = setShowFemaLayer;
                        this.setBuilderLayerActive = setBuilderLayerActive;
                        this.setBuilderLayerFeatures = setBuilderLayerFeatures;
                        this.loadBuilderLayerFeatures = loadBuilderLayerFeatures;
                        this.isBuilderLayerActiveRef = isBuilderLayerActiveRef;
                    }

                    setLayerVisibility(layerId, visible) {
                        if (!this.map?.getLayer(layerId)) return;
                        this.map.setLayoutProperty(layerId, "visibility", visible ? "visible" : "none");
                    }

                    setButtonActive(active) {
                        if (!this.button) return;
                        this.button.style.backgroundColor = active ? "#e1faee" : "transparent";
                        this.button.style.color = active ? "#1c8f7d" : "#3a3131";
                    }

                    forceOff() {
                        if (!this.map) return;

                        const normalParcelLayerIds = [
                            "parcel-points",
                            "parcel-outline",
                            "parcel-fill",
                            "parcel-hover",
                            "parcel-hitbox"
                        ];
                        const showNormalParcelLayers = selectedCountyRef.current && selectedCountyRef.current !== "all";

                        this.setLayerVisibility("builder-projects-symbols", false);
                        this.setLayerVisibility("ongoing-builder-projects-symbols", false);
                        this.setLayerVisibility("builder-parcel-fill", false);
                        this.setLayerVisibility("builder-parcel-outline", false);
                        normalParcelLayerIds.forEach((layerId) => this.setLayerVisibility(layerId, showNormalParcelLayers));

                        if (this.setBuilderLayerActive) {
                            this.setBuilderLayerActive(false);
                        }

                        if (this.setBuilderLayerFeatures) {
                            this.setBuilderLayerFeatures([]);
                        }

                        if (this.isBuilderLayerActiveRef) {
                            this.isBuilderLayerActiveRef.current = false;
                        }

                        if (drawControlRef.current) {
                            drawControlRef.current.style.display = "none";
                        }

                        skipBuilderFeatureReloadRef.current = true;
                        try {
                            handleClearAll(false);
                        } finally {
                            skipBuilderFeatureReloadRef.current = false;
                        }
                        if (this.setBuilderLayerFeatures) {
                            this.setBuilderLayerFeatures([]);
                        }
                        this.setButtonActive(false);
                    }

                    onAdd(map) {
                    this.map = map;

                    this.button = document.createElement("button");
                    this.button.className = "mapboxgl-ctrl-icon";
                    this.button.type = "button";
                    this.button.title = "Toggle Builder Projects";
                    this.button.style.display = "flex";
                    this.button.style.alignItems = "center";
                    this.button.style.gap = "6px";
                    this.button.style.padding = "0 8px";
                    this.button.style.width = "auto";
                    this.button.style.cursor = "pointer";
                    this.button.style.transition = "background-color 0.2s";
                    this.button.innerHTML = `
                            <img src="/assets/building.svg" alt="Sewage" style="width:16px;height:16px;"/>
                        <span style="font-size: 12px; font-weight: 600; font-family: Inter, sans-serif;">Builders</span>
                    `;

                    this.button.addEventListener("click", () => {
                        const builderLayerId = "builder-projects-symbols";
                        const builderOngoingLayerId = "ongoing-builder-projects-symbols";
                        const normalParcelLayerIds = [
                            "parcel-points",
                            "parcel-outline",
                            "parcel-fill",
                            "parcel-hover",
                            "parcel-hitbox"
                        ];

                        if (!map.getLayer(builderLayerId)) return;

                        if (showFavoritesOnlyRef.current || adjacentModeRef.current) {
                            this.forceOff();
                            return;
                        }

                        const visibility = map.getLayoutProperty(builderLayerId, "visibility");
                        const isVisible = visibility === "visible";
                        const setLayerVisibility = (layerId, visible) => {
                            if (!map.getLayer(layerId)) return;
                            map.setLayoutProperty(layerId, "visibility", visible ? "visible" : "none");
                        };

                        if (!isVisible) {
                            const currentZoom = map.getZoom();
                            const center = WAKE_CENTER;
                            // if (currentZoom < 10) {
                            //     map.flyTo({ center, zoom: 10, speed: 0.8 });
                            // }

                            setLayerVisibility(builderLayerId, true);
                            setLayerVisibility(builderOngoingLayerId, true);
                            normalParcelLayerIds.forEach((layerId) => setLayerVisibility(layerId, false));
                        } else {
                            setLayerVisibility(builderLayerId, false);
                            setLayerVisibility(builderOngoingLayerId, false);
                            const showNormalParcelLayers = selectedCountyRef.current && selectedCountyRef.current !== "all";
                            normalParcelLayerIds.forEach((layerId) => setLayerVisibility(layerId, showNormalParcelLayers));
                        }

                        if (this.setShowFemaLayer) {
                            this.setShowFemaLayer(!this.setShowFemaLayer ? false : true);
                        }

                        if (this.setBuilderLayerActive) {
                            this.setBuilderLayerActive(!isVisible);
                        }

                        if (!isVisible && this.loadBuilderLayerFeatures) {
                            const updateList = () => this.loadBuilderLayerFeatures(map);
                            map.once("idle", updateList);
                            setTimeout(updateList, 800);
                        }

                        if (isVisible && this.setBuilderLayerFeatures) {
                            this.setBuilderLayerFeatures([]);
                        }

                        if (this.isBuilderLayerActiveRef) {
                            this.isBuilderLayerActiveRef.current = !isVisible;
                        }

                        if (drawControlRef.current) {
                            drawControlRef.current.style.display = !isVisible ? "block" : "none";
                        }

                        if (isVisible) {
                            deleteDrawnPolygons();
                            handleClearAll();
                        }

                        // if (!isVisible && map.getZoom() < 10) {
                        //     map.easeTo({
                        //         zoom: 10,
                        //         duration: 800,
                        //         essential: true,
                        //     });
                        // }

                        this.setButtonActive(!isVisible);
                    });

                    this.container = document.createElement("div");
                    this.container.className = "mapboxgl-ctrl mapboxgl-ctrl-group";
                    this.container.appendChild(this.button);

                    return this.container;
                }

                onRemove() {
                    this.container.parentNode.removeChild(this.container);
                    this.map = undefined;
                    if (builderProjectsControlRef.current === this) {
                        builderProjectsControlRef.current = null;
                    }
                }
            }

            const builderProjectsControl = new BuilderProjectsControl(
                setShowFemaLayer,
                setBuilderLayerActive,
                setBuilderLayerFeatures,
                loadBuilderLayerFeatures
            );
            builderProjectsControlRef.current = builderProjectsControl;
            mapRef.current.addControl(builderProjectsControl, "bottom-right");


            class SewageMapControl {
                onAdd(map) {
                    this.map = map;

                    this.button = document.createElement("button");
                    this.button.className = "mapboxgl-ctrl-icon";
                    this.button.type = "button";
                    this.button.title = "Toggle Sewage Map";

                    // Flexbox ensures the icon and text sit nicely side-by-side
                    this.button.style.display = "flex";
                    this.button.style.alignItems = "center";
                    this.button.style.gap = "6px";
                    this.button.style.padding = "0 8px";
                    this.button.style.width = "auto"; // Override default square size
                    this.button.style.cursor = "pointer";
                    this.button.style.transition = "background-color 0.2s";
                    // this.button.innerHTML = "💧";

                    //         this.button.innerHTML = `
                    //     <img 
                    //         src="/assets/sewage.svg" 
                    //         alt="Sewage Control"
                    //         style="
                    //             width:18px;
                    //             height:18px;
                    //             object-fit:contain;
                    //         "
                    //     />
                    // `;

                    this.button.innerHTML = `
            <img src="/assets/sewage.svg" alt="Sewage" style="width:16px;height:16px;"/>
            <span style="font-size: 12px; font-weight: 600; font-family: Inter, sans-serif;">Sewage</span>
        `;


                    this.button.style.fontSize = "18px";
                    this.button.style.cursor = "pointer";

                    // this.button.addEventListener("click", () => {
                    //     const visibility = map.getLayoutProperty(
                    //         "water-ssgravitymain-layer",
                    //         "visibility"
                    //     );

                    //     map.setLayoutProperty(
                    //         "water-ssgravitymain-layer",
                    //         "visibility",
                    //         visibility === "visible" ? "none" : "visible"
                    //     );
                    // });

                    this.button.addEventListener("click", () => {

                        const layerId = "water-ssgravitymain-layer";
                        if (!map.getLayer(layerId)) return;

                        if (!map.getLayer(layerId)) {
                            console.error("Sewage layer not found");
                            return;
                        }

                        const visibility = map.getLayoutProperty(
                            layerId,
                            "visibility"
                        );
                        const isVisible = visibility === "visible";

                        //         // 2. Change Zoom Level
                        // if (!isVisible) {
                        //     map.easeTo({
                        //         zoom: 10,
                        //         duration: 800
                        //     });
                        // }


                        // 2. Conditional Zoom (Only if zooming in AND current zoom is less than 10)
                        if (!isVisible) {
                            const currentZoom = map.getZoom();
                            const center =  WAKE_CENTER;

                            if (currentZoom < 10) {
                                 map.flyTo({ center, zoom: 10, speed: 0.8 });
                                // map.easeTo({
                                //     zoom: 10,
                                //     duration: 800,
                                //     essential: true // ensures the transition happens even if prefers-reduced-motion is on
                                // });
                            }
                        }

                        map.setLayoutProperty(
                            layerId,
                            "visibility",
                            visibility === "visible"
                                ? "none"
                                : "visible"
                        );

                        // Toggle "Selected" Background (Enlighten when active)
                        // If it WAS visible, we are turning it off (transparent). If it was hidden, we turn it on (#e1faee).
                        this.button.style.backgroundColor = !isVisible ? "#e1faee" : "transparent";
                        this.button.style.color = !isVisible ? "#1c8f7d" : "#3a3131";
                    });

                    this.container = document.createElement("div");
                    this.container.className = "mapboxgl-ctrl mapboxgl-ctrl-group";

                    this.container.appendChild(this.button);

                    return this.container;
                }

                onRemove() {
                    this.container.parentNode.removeChild(this.container);
                    this.map = undefined;
                }
            }

            // if (
            //     console.log("Selected county for sewage control:", selectedCounty),
            //     selectedCounty?.toLowerCase() === "wake" 
            //     // &&
            //     // selectedMunicipality?.toLowerCase() === "raleigh"
            // ) {
            const sewageControl = new SewageMapControl();

            mapRef.current.addControl(sewageControl, "bottom-right");
            // }

          
            class ContourMapControl {
                onAdd(map) {
                    this.map = map;

                    this.button = document.createElement("button");
                    this.button.className = "mapboxgl-ctrl-icon";
                    this.button.type = "button";
                    this.button.title = "Toggle Contour Map";
                    // this.button.textContent = "C";
                    this.button.style.fontSize = "14px";
                    this.button.style.fontWeight = "700";
                    // this.button.style.color = "#8a5a2b";
                    this.button.style.cursor = "pointer";

                    this.button.style.display = "flex";
                    this.button.style.alignItems = "center";
                    this.button.style.gap = "6px";
                    this.button.style.padding = "0 8px";
                    this.button.style.width = "auto";
                    this.button.style.transition = "background-color 0.2s";

                    this.button.innerHTML = `
            <span style="font-size: 14px; font-weight: 600; color: #3a3131;">C</span>
            <span style="font-size: 12px; font-weight: 600; font-family: Inter, sans-serif;">Contours</span>
        `;

                    this.button.addEventListener("click", () => {
                        const layerId = "raleigh-2-foot-contours-layer";
                        const layerId2 = "raleigh-2-foot-contours-labels";


                        // if (!map.getLayer(layerId)) {
                        if (!map.getLayer(layerId) || !map.getLayer(layerId2)) {
                            console.error("Contour layer not found");
                            return;
                        }

                        const visibility = map.getLayoutProperty(layerId, "visibility");
                        const isVisible = visibility === "visible";

                        //         // 2. Change Zoom Level
                        // if (!isVisible) {
                        //     map.easeTo({
                        //         zoom: 10,
                        //         duration: 800
                        //     });
                        // }

                        // 2. Conditional Zoom (Only if zooming in AND current zoom is less than 10)

                        if (!isVisible) {
                            const currentZoom = map.getZoom();
                                const center =  WAKE_CENTER;

                            if (currentZoom < 10) {
                                 map.flyTo({ center, zoom: 11, speed: 0.8 });

                                // map.easeTo({
                                //     zoom: 10,
                                //     duration: 800,
                                //     essential: true // ensures the transition happens even if prefers-reduced-motion is on
                                // });
                            }
                        }

                        map.setLayoutProperty(
                            layerId,
                            "visibility",
                            visibility === "visible" ? "none" : "visible"
                        );
                        map.setLayoutProperty(layerId2, "visibility", visibility === "visible" ? "none" : "visible");

                        // Enlighten background when selected (using a light wood/sand color)
                        // this.button.style.backgroundColor = !isVisible ? "#fcf3e8" : "transparent";
                        this.button.style.backgroundColor = !isVisible ? "#e1faee" : "transparent";
                        this.button.style.color = !isVisible ? "#1c8f7d" : "#3a3131";
                    });

                    this.container = document.createElement("div");
                    this.container.className = "mapboxgl-ctrl mapboxgl-ctrl-group";
                    this.container.appendChild(this.button);

                    return this.container;
                }

                onRemove() {
                    this.container.parentNode.removeChild(this.container);
                    this.map = undefined;
                }
            }

            const contourControl = new ContourMapControl();

            mapRef.current.addControl(contourControl, "bottom-right");
            class FloodMapControl {
                constructor(setShowFemaLayer) {
                    this.setShowFemaLayer = setShowFemaLayer;
                }
                onAdd(map) {
                    this.map = map;
                    this.button = document.createElement("button");
                    this.button.className = "mapboxgl-ctrl-icon";
                    this.button.type = "button";
                    this.button.title = "Toggle Flood Zones";

                    this.button.style.display = "flex";
                    this.button.style.alignItems = "center";
                    this.button.style.gap = "6px";
                    this.button.style.padding = "0 8px";
                    this.button.style.width = "auto";
                    this.button.style.cursor = "pointer";
                    this.button.style.transition = "background-color 0.2s";

                    this.button.innerHTML = `
            <span style="font-size: 12px; font-weight: 600; font-family: Inter, sans-serif;">Flood Zone</span>
        `;

                    this.button.addEventListener("click", () => {
                        const layerId = "fema-flood-zones-layer";
                        const layerId2 = "parcel-points";
                        if (!map.getLayer(layerId)) return;

                        const visibility = map.getLayoutProperty(layerId, "visibility") || "visible";
                        const visibility2 = map.getLayoutProperty(layerId2, "visibility") || "visible";
                        const isVisible = visibility === "visible";

                        // Toggle Layer
                        map.setLayoutProperty(layerId, "visibility", isVisible ? "none" : "visible");
                        map.setLayoutProperty(layerId2, "visibility", isVisible ? "visible" : "none");

                        if (this.setShowFemaLayer) {
                            this.setShowFemaLayer(!this.setShowFemaLayer ? false : true);
                        }


                        // Zoom to 10 if turning on
                        // if (!isVisible) {
                        //     map.flyTo({ zoom: 10, essential: true });
                        // }
                        if (!isVisible) {
                            const currentZoom = map.getZoom();
                             const center =  WAKE_CENTER;

                            if (currentZoom < 10) {
                                 map.flyTo({ center, zoom: 11, speed: 0.8 });

                                // map.easeTo({
                                //     zoom: 10,
                                //     duration: 800,
                                //     essential: true // ensures the transition happens even if prefers-reduced-motion is on
                                // });
                            }
                        }


                        // Update Background: Light Blue for "Flood" active state
                        // this.button.style.backgroundColor = !isVisible ? "#e1f5fe" : "transparent";
                        // this.button.style.color = !isVisible ? "#0277bd" : "inherit";

                        this.button.style.backgroundColor = !isVisible ? "#e1faee" : "transparent";
                        this.button.style.color = !isVisible ? "#1c8f7d" : "#3a3131";
                    });

                    this.container = document.createElement("div");
                    this.container.className = "mapboxgl-ctrl mapboxgl-ctrl-group";
                    this.container.appendChild(this.button);

                    return this.container;
                }

                onRemove() {
                    this.container.parentNode.removeChild(this.container);
                    this.map = undefined;
                }
            }

            // Add to map

            const floodControl = new FloodMapControl();
            mapRef.current.addControl(floodControl, "bottom-right");


            // 2. Add County Source + Layers using vector tiles
            if (!mapRef.current.getSource("nc-counties")) {
                mapRef.current.addSource("nc-counties", {
                    type: "vector",
                    //tiles: ["http://localhost:8080/data/nc_counties/{z}/{x}/{y}.pbf"]
                    tiles: ["https://demo.flowlogic.com/tilesgl/data/nc_counties/{z}/{x}/{y}.pbf"]
                });
            }

            if (!mapRef.current.getLayer("counties-fill")) {
                mapRef.current.addLayer({
                    id: "counties-fill",
                    type: "fill",
                    source: "nc-counties",
                    "source-layer": "counties", // You may need to adjust this based on your MBTiles layer name
                    paint: {
                        "fill-color": [
                            "case",
                            ["==", ["downcase", ["get", "County"]], ["literal", selectedCounty || ""]],
                            "#42A5F5",  // Lighter blue for selected county
                            "#1565C0"   // Default blue for other counties
                        ],
                        "fill-opacity": [
                            "case",
                            ["==", ["downcase", ["get", "County"]], ["literal", selectedCounty || ""]],
                            // 0.5,   // Higher opacity for selected county
                            0.026,   // Higher opacity for selected county
                            0.025   // Default opacity for other counties
                        ],
                    },
                });
            }

            if (!mapRef.current.getLayer("counties-outline")) {
                mapRef.current.addLayer({
                    id: "counties-outline",
                    type: "line",
                    source: "nc-counties",
                    "source-layer": "counties", // You may need to adjust this based on your MBTiles layer name
                    paint: {
                        "line-color": [
                            "case",
                            ["==", ["downcase", ["get", "County"]], ["literal", selectedCounty || ""]],
                            "#1976D2",  // Brighter blue for selected county
                            "#0D47A1"   // Default dark blue for other counties
                        ],
                        "line-width": [
                            "case",
                            ["==", ["downcase", ["get", "County"]], ["literal", selectedCounty || ""]],
                            2.5,   // Thicker line for selected county
                            1.2    // Default line width for other counties
                        ],
                    },
                });
            }

            // Add non-interactive county overlay layer
            if (!mapRef.current.getLayer("counties-non-interactive")) {
                mapRef.current.addLayer({
                    id: "counties-non-interactive",
                    type: "fill",
                    source: "nc-counties",
                    "source-layer": "counties",
                    paint: {
                        "fill-color": "#cccccc",
                        "fill-opacity": .5,
                    },
                    layout: {
                        visibility: "visible",
                    },
                    // Filter to show all counties EXCEPT Wake and Chatham
                    filter: [
                        "!",
                        [
                            "in",
                            ["downcase", ["get", "County"]],
                            ["literal", ["wake", "chatham"]]
                        ]
                    ]
                });
            }

            // Wait for tiles to load, then query features to extract county names and centers
            mapRef.current.once('idle', () => {
                try {
                    // Query all features from the counties layer
                    const features = mapRef.current.querySourceFeatures('nc-counties', {
                        sourceLayer: 'counties' // Adjust this if your layer name is different
                    });

                    console.log('Queried', features.length, 'county features from vector tiles');

                    if (features.length === 0) {
                        console.warn('No county features found - check source-layer name');
                        return;
                    }

                    // Extract county names for dropdown (sorted alphabetically)
                    const names = [...new Set(features.map(f => f.properties.County))].filter(Boolean).sort();
                    setCountiesList(names);

                    // Calculate county centers from feature geometries
                    const centers = {};
                    const countyFeatures = {};

                    // Group features by county
                    features.forEach(f => {
                        const countyName = f.properties.County;
                        if (!countyName) return;

                        if (!countyFeatures[countyName]) {
                            countyFeatures[countyName] = [];
                        }
                        countyFeatures[countyName].push(f);
                    });

                    // Calculate center for each county
                    Object.keys(countyFeatures).forEach(countyName => {
                        const countyParts = countyFeatures[countyName];
                        let totalX = 0, totalY = 0, pointCount = 0;

                        countyParts.forEach(feature => {
                            if (feature.geometry && feature.geometry.type === 'Polygon') {
                                const coords = feature.geometry.coordinates[0];
                                coords.forEach(coord => {
                                    totalX += coord[0];
                                    totalY += coord[1];
                                    pointCount++;
                                });
                            } else if (feature.geometry && feature.geometry.type === 'MultiPolygon') {
                                feature.geometry.coordinates.forEach(polygon => {
                                    polygon[0].forEach(coord => {
                                        totalX += coord[0];
                                        totalY += coord[1];
                                        pointCount++;
                                    });
                                });
                            }
                        });

                        centers[countyName.toLowerCase()] = pointCount ? [totalX / pointCount, totalY / pointCount] : NC_CENTER;
                    });

                    setCountyCenters(centers);
                    console.log('Loaded', names.length, 'counties from vector tiles:', names.join(', '));
                } catch (error) {
                    console.error('Error querying county features:', error);
                }
            });

            // 3. Add Parcels vector source & layer but hidden initially
            if (!mapRef.current.getSource("parcels")) {
                mapRef.current.addSource("parcels", {
                    type: "vector",
                    //tiles: ["http://demo.flowlogic.com:8080/data/ncarolina/{z}/{x}/{y}.pbf"],
                    // tiles: ["https://demo.flowlogic.com/tilesgl/data/ncarolina/{z}/{x}/{y}.pbf"],
                    //tiles: ["http://localhost:8080/data/ncarolina/{z}/{x}/{y}.pbf"],

                    // tiles: [adjacentMode ? "http://localhost:8007/data/adjparcels/{z}/{x}/{y}.pbf" : "https://demo.flowlogic.com/tilesgl/data/ncarolina/{z}/{x}/{y}.pbf"],
                    // tiles: [adjacentMode ? "https://demo.flowlogic.com/tilesgl/data/adjparcels/{z}/{x}/{y}.pbf" : "https://demo.flowlogic.com/tilesgl/data/ncarolina/{z}/{x}/{y}.pbf"],
                    tiles: [adjacentMode ?

                        adjacentParcelFilter === "Suitable Parcels"


                            ? "https://demo.flowlogic.com/tilesgl/data/adjparcelsfinal/{z}/{x}/{y}.pbf"
                            : "https://demo.flowlogic.com/tilesgl/data/adjparcelsrejected/{z}/{x}/{y}.pbf"

                        // ? "http://localhost:8009/data/adjparcelsfinal/{z}/{x}/{y}.pbf"
                        // : "http://localhost:8009/data/adjparcelsrejected/{z}/{x}/{y}.pbf"

                        : "https://demo.flowlogic.com/tilesgl/data/ncarolina/{z}/{x}/{y}.pbf"],
                    //    : "http://localhost:8009/data/ncarolina/{z}/{x}/{y}.pbf"],
                });
            }

            if (!mapRef.current.getLayer("parcel-points")) {
                mapRef.current.addLayer({
                    id: "parcel-points",
                    type: "fill",
                    source: "parcels",
                    "source-layer": "fema",
                    // maxzoom: 13 , // This automatically hides the layer when zoom > 13
                    layout: { visibility: "none" },
                    paint: {
                        "fill-color": [
                            "match",
                            ["get", "parcelcustomfemaflood"],
                            "X",
                            "#4CAF50",
                            "AE",
                            "#FF9800",
                            "VE",
                            "#E91E63",
                            "A",
                            "#2196F3",
                            "#9fa2e7",
                        ],
                        "fill-opacity": 0.45,
                    },
                });
            }

            if (!mapRef.current.getLayer("parcel-hitbox")) {
                mapRef.current.addLayer({
                    id: "parcel-hitbox",
                    type: "fill",
                    source: "parcels",
                    "source-layer": "fema",
                    layout: { visibility: "visible" },
                    paint: {
                        "fill-color": "rgba(0,0,0,0)",
                        "fill-opacity": 0.01,
                    },
                });
            }

            // Add parcel outline layer with orange color at zoom >= 13
            if (!mapRef.current.getLayer("parcel-outline")) {
                mapRef.current.addLayer({
                    id: "parcel-outline",
                    type: "line",
                    source: "parcels",
                    "source-layer": "fema",
                    layout: { visibility: "none" },
                    paint: {
                        // "line-color": [
                        //     "step",
                        //     ["zoom"],
                        //     "#333",      // Dark gray below zoom 13
                        //     13, "#ebeaea"  // Orange at zoom 13 and above
                        // ],

                        "line-color": [
                            "step",
                            ["zoom"],

                            // Below zoom 13
                            // [
                            //     "match",
                            //     ["get", "parcelcustomfemaflood"],
                            //     "X", "#81C784",
                            //     "AE", "#fcee2a",
                            //     "VE", "#F06292",
                            //     "A", "#64B5F6",
                            //     "#C5CAE9"
                            // ],


                            [
                                "match",
                                ["get", "parcelcustomfemaflood"],
                                "X", "#31f03b",
                                "AE", "#f5f25b",
                                "VE", "#f07fa5",
                                "A", "#64B5F6",
                                "#C5CAE9"
                            ],

                            // Zoom 13 and above
                            // 13, "#ffffff"
                            // 13, "#dfdd8a"
                            13, "#bebd6b"

                        ],
                        // "line-width": [
                        //     "interpolate",
                        //     ["linear"],
                        //     ["zoom"],
                        //     0, 0.5,      // Thin at low zoom
                        //     13, 1.0,     // Medium at zoom 13
                        //     18, 2.0      // Thicker at high zoom
                        // ],
                        "line-width": [
                            "interpolate",
                            ["linear"],
                            ["zoom"],
                            0, 0.5,      // Thin at low zoom
                            13, .9,     // Medium at zoom 13
                            18, 1.5      // Thicker at high zoom
                        ],

                        // "line-opacity": [
                        //     "step",
                        //     ["zoom"],
                        //     0,           // Invisible below zoom 13
                        //     13, 0.9      // Visible at zoom 13 and above
                        // ]
                        "line-opacity": [
                            "interpolate",
                            ["linear"],
                            ["zoom"],
                            0, 0.65,   // faint below zoom 13
                            13, 0.9
                        ]
                    },
                });
            }

            // Add parcel hover highlight layer (red border on mouseover)
            if (!mapRef.current.getLayer("parcel-hover")) {
                mapRef.current.addLayer({
                    id: "parcel-hover",
                    type: "line",
                    source: "parcels",
                    "source-layer": "fema",
                    layout: { visibility: "none" },
                    paint: {
                        "line-color": "#ff7300",  // Red color
                        // FF0000
                        "line-width": 3,
                        "line-opacity": 0
                    },
                    filter: ["==", ["get", "parcel"], ""] // Initially show nothing
                });
            }

            // Selected parcels fill for click highlight

            // if (!mapRef.current.getLayer("parcel-selected-fill")) {
            //     mapRef.current.addLayer({
            //         id: "parcel-selected-fill",
            //         type: "fill",
            //         source: "parcels",
            //         "source-layer": "fema",
            //         layout: { visibility: "none" },
            //         paint: {
            //             "fill-color": "#b8eee6",
            //             "fill-opacity": 0.4
            //         },
            //         filter: ["in", ["get", "parcel"], ["literal", []]]
            //     });
            // }

            // Selected parcels outline

            if (!mapRef.current.getLayer("parcel-selected-outline")) {
                mapRef.current.addLayer({
                    id: "parcel-selected-outline",
                    type: "line",
                    source: "parcels",
                    "source-layer": "fema",
                    layout: { visibility: "none" },
                    paint: {
                        "line-color": "#fd0224",
                        "line-width": 2.5,
                        "line-opacity": 0.95
                    },
                    // filter: ["in", ["get", "parcel"], ["literal", []]]
                    filter: ["==", ["get", "parcel"], ""],
                    //  filter: ["in", "parcel", ""]
                });
            }

            // 4. Ensure counties above parcels
            mapRef.current.on("idle", () => {
                try {
                    mapRef.current.moveLayer("counties-fill", "parcel-points");
                    mapRef.current.moveLayer("counties-outline", "parcel-points");
                } catch (e) { }
            });

            // Debug: verify parcels source-layer returns features
            mapRef.current.once('idle', () => {
                try {
                    const feats = mapRef.current.querySourceFeatures('parcels', { sourceLayer: 'fema' }) || [];
                    console.log('Parcels sourceLayer=fema features available:', feats.length);
                } catch (e) { console.warn('Could not query parcels source features. Check source-layer name.', e); }
            });



            mapRef.current.on("click", "counties-fill", e => {
                const feature = e.features?.[0];
                if (!feature) return;

                const countyName = String(feature.properties.County || "").toLowerCase();

                // Skip if clicking the same county that's already selected
                if (selectedCountyRef.current === countyName) {
                    console.log(`County "${countyName}" is already selected, skipping reload`);
                    return;
                }

                // Only update panel / selected county for supported counties
                if (countyName === "chatham" || countyName === "wake") {
                    updateLeftPanelParcels(countyName);
                    setSelectedCounty(countyName);
                    //fetchTotalCount(countyName); // Fetch total count for the county
                } else {
                    console.log(`County "${countyName}" clicked but not in [chatham, wake]; skipping panel update`);
                }

                boundsInitialized.current = false;
                vacantFilterTouched.current = false;
                zoneFilterTouched.current = false;
                listedSaleFilterTouched.current = false;

                // Clear checkbox filters when county changes
                setZoneCodeFilter([]);
                setFloodRiskFilter("All");
                setListedSaleFilter("All");
                setVacantFilter("All");

                //  SAFELY compute center from Polygon / MultiPolygon
                let bounds = new mapboxgl.LngLatBounds();

                const geom = feature.geometry;

                if (geom.type === "Polygon") {
                    geom.coordinates[0].forEach(coord => bounds.extend(coord));
                } else if (geom.type === "MultiPolygon") {
                    geom.coordinates.forEach(polygon => {
                        polygon[0].forEach(coord => bounds.extend(coord));
                    });
                }

                const center = bounds.getCenter();

                if (!Number.isFinite(center.lng) || !Number.isFinite(center.lat)) {
                    console.warn(" Invalid county center:", feature);
                    return;
                }

                const screenWidth = window.innerWidth;
                // const leftPanelWidth = screenWidth * 0.6;
                const leftPanelWidth = screenWidth * (listWidth / 100);
                const offset = -(leftPanelWidth / 2);

                // Only animate flyTo for supported counties
                if (countyName === "chatham" || countyName === "wake") {
                    mapRef.current.flyTo({
                        center: [center.lng, center.lat],
                        zoom: 9,
                        speed: 0.8,
                        offset: [offset, 0],
                    });
                }
            });



            const getParcelLayerId = (p) => p?.parcel || p?.parcelid || p?.geo_id || p?.id;
            const parcelInteractiveLayers = ["parcel-hitbox", "parcel-points", "parcel-outline"];

            // 6. Parcel click → zoom and show detailed popup (only at zoom > 13.1)
            const handleParcelLayerClick = (e) => {
                // if (isDrawingParcelSelectionRef.current) return;
                // If drawing or if the builder layer control is toggled active, do nothing
    // if (isDrawingParcelSelectionRef.current || isBuilderLayerActiveRef.current) return;

                // const p = e.features && e.features[0] && e.features[0].properties;
                const p = e.features?.[0]?.properties;
                if (!p) return;

                // Only trigger zoom/popup if zoom level > 13.1
                const currentZoom = mapRef.current.getZoom();
                // if (currentZoom >= 13.1 && adjacentMode == false) {
                if (currentZoom >= 13.1 && adjacentModeRef.current === false) {
                    const parcelId = getParcelLayerId(p);
                    const now = Date.now();
                    if (
                        parcelId &&
                        lastParcelLayerClickRef.current.parcelId === parcelId &&
                        now - lastParcelLayerClickRef.current.time < 250
                    ) {
                        return;
                    }
                    lastParcelLayerClickRef.current = { parcelId, time: now };

                    // Use the same handler as row clicks for consistent behavior
                    // handleRowClick(p);

                    //  Inject coordinates from the click event itself when tile props lack them
                    const clickLng = e.lngLat.lng;
                    const clickLat = e.lngLat.lat;

                    // Parse properties or fallback to click coordinates
                    const longitude = parseCoordinate(p.longitude ?? p.lon ?? p.lng ?? p.x) ?? clickLng;
                    const latitude = parseCoordinate(p.latitude ?? p.lat ?? p.y) ?? clickLat;

                    const enriched = {
                        ...p,
                        longitude,
                        latitude,
                        // longitude: p.longitude ?? clickLng,
                        // latitude: p.latitude ?? clickLat,
                    };

                    handleRowClick(enriched);
                }
            };

            // Track hovered parcel with mousemove for better adjacent parcel handling
            let hoveredParcelId = null;

            const handleParcelLayerMouseMove = (e) => {
                if (isDrawingParcelSelectionRef.current) return;
                if (mapRef.current.getZoom() <= 13.1) return;

                const feature = e.features?.[0];
                if (!feature?.properties) return;

                const p = feature.properties;
                const parcelId = getParcelLayerId(p);

                if (!parcelId) return;

                // Highlight parcel
                if (mapRef.current.getLayer("parcel-hover")) {
                    mapRef.current.setFilter("parcel-hover", [
                        "any",
                        ["==", ["to-string", ["get", "parcel"]], String(parcelId)],
                        ["==", ["to-string", ["get", "parcelid"]], String(parcelId)],
                    ]);
                    mapRef.current.setPaintProperty("parcel-hover", "line-opacity", 1);
                }

                //     const parcelHTML = adjacentMode ? `
                // <strong>Parcel Area:</strong>
                // ${p.arealotsf ? `${p.arealotsf} sq ft` : "N/A"}<br>

                // <strong>Building Area:</strong>
                // ${p.areabuilding ? `${p.areabuilding} sq ft` : "N/A"}<br>
                // ` : '';


                // Remove previous hover popup ONLY
                hoverPopupRef.current?.remove();

                hoverPopupRef.current = new mapboxgl.Popup({
                    closeButton: false,
                    closeOnClick: false
                })


                    .setLngLat(e.lngLat)
                    .setHTML(`
        <div style="
            background:#fff;
            padding:10px;
            border-radius:6px;
            box-shadow:0 2px 6px rgba(0,0,0,0.15);
            font-family:Inter, sans-serif;
            font-size:12px;
            width:240px;
        ">
            <strong>Parcel ID:</strong>
            ${p.parcel || p.parcelid || "N/A"}<br>
 
            <strong>County:</strong>
            ${p.county || "N/A"}<br>
 
            <strong>Address:</strong>
            ${p.addrline || p.addrline1 || p.address || "N/A"}<br>
 
            <strong>Zoning:</strong>
            ${p.zonedcodelocal || p.ZonedCodeLocal || p.zoning || "N/A"}<br>
 
            <strong>Flood Zone:</strong>
            ${p.parcelcustomfemaflood || p.FloodFemaRisk || "N/A"}<br>

  
     ${adjacentModeRef.current ? `
    <div><strong>Parcel Area:</strong> ${p.arealotsf ? `${p.arealotsf} sq ft` : "N/A"}</div>
    <div><strong>Building Area:</strong> ${p.areabuilding ? `${p.areabuilding} sq ft` : "N/A"}</div>
` : ''}



        </div>
    `)
                    .addTo(mapRef.current);


                mapRef.current.getCanvas().style.cursor = "pointer";
            };


            const handleParcelLayerMouseLeave = () => {
                hoverPopupRef.current?.remove();
                hoverPopupRef.current = null;

                mapRef.current.getCanvas().style.cursor = "";

                // Hide red border
                if (mapRef.current.getLayer("parcel-hover")) {
                    mapRef.current.setPaintProperty("parcel-hover", "line-opacity", 0);
                }
            };

            parcelInteractiveLayers.forEach((layerId) => {
                if (!mapRef.current.getLayer(layerId)) return;
                mapRef.current.on("click", layerId, handleParcelLayerClick);
                mapRef.current.on("mousemove", layerId, handleParcelLayerMouseMove);
                mapRef.current.on("mouseleave", layerId, handleParcelLayerMouseLeave);
            });

            // mapRef.current.on("zoom", () => {
            //     const zoom = mapRef.current.getZoom();

            //     if (zoom < 13.1) {
            //         clickPopupRef.current?.remove();
            //         clickPopupRef.current = null;
            //     }
            // });

            mapRef.current.on("zoom", () => {
                const zoom = mapRef.current.getZoom();

                const isAboveThreshold = zoom >= 13.1;

                //  ONLY clear popup when crossing from above → below
                if (wasAboveZoomThresholdRef.current && !isAboveThreshold) {
                    clickPopupRef.current?.remove();
                    clickPopupRef.current = null;
                }

                // Update previous state
                wasAboveZoomThresholdRef.current = isAboveThreshold;
            });

            setMapLoaded(true);
        });

        // return () => mapRef.current?.remove();

        return () => {
            mapRef.current?.remove();
            mapRef.current = null;
        };

    }, []);


    //     useEffect(() => {
    //     if (!mapRef.current?.getLayer("water-ssgravitymain-layer")) return;

    //     mapRef.current.setLayoutProperty(
    //         "water-ssgravitymain-layer",
    //         "visibility",
    //         showSewageMap ? "visible" : "none"
    //     );
    // }, [showSewageMap]);

    // Track zoom level changes
    useEffect(() => {
        if (!mapRef.current || !mapLoaded) return;
        const map = mapRef.current;

        const handleZoomEnd = () => {
            const zoom = map.getZoom();
            setCurrentZoom(zoom);
        };

        // Set initial zoom
        setCurrentZoom(map.getZoom());

        map.on("zoomend", handleZoomEnd);

        return () => {
            map.off("zoomend", handleZoomEnd);
        };
    }, [mapLoaded]);

    // Update county highlighting when selectedCounty changes
    useEffect(() => {
        if (!mapRef.current || !mapLoaded) return;
        const map = mapRef.current;

        // Update fill color and opacity for selected county

        // if (map.getLayer("counties-fill")) {
        //     map.setPaintProperty("counties-fill", "fill-color", [
        //         "case",
        //         ["==", ["downcase", ["get", "County"]], selectedCounty || ""],
        //         "#42A5F5",  // Lighter blue for selected county
        //         "#1565C0"   // Default blue for other counties
        //     ]);
        //     map.setPaintProperty("counties-fill", "fill-opacity", [
        //         "case",
        //         ["==", ["downcase", ["get", "County"]], selectedCounty || ""],
        //         0.5,   // Higher opacity for selected county
        //         0.25   // Default opacity for other counties
        //     ]);
        // }

        // Update outline color and width for selected county
        if (map.getLayer("counties-outline")) {
            map.setPaintProperty("counties-outline", "line-color", [
                "case",
                ["==", ["downcase", ["get", "County"]], selectedCounty || ""],
                "#1976D2",  // Brighter blue for selected county
                "#0D47A1"   // Default dark blue for other counties
            ]);
            map.setPaintProperty("counties-outline", "line-width", [
                "case",
                ["==", ["downcase", ["get", "County"]], selectedCounty || ""],
                2.5,   // Thicker line for selected county
                1.2    // Default line width for other counties
            ]);
        }
    }, [selectedCounty, mapLoaded]);

    // Control legend visibility based on county selection
    useEffect(() => {
        if (legendControlRef.current && legendControlRef.current._container) {
            if (selectedCounty !== "all") {
                legendControlRef.current._container.style.display = 'flex';
            } else {
                legendControlRef.current._container.style.display = 'none';
            }
        }
    }, [selectedCounty]);

    // Add this useEffect to handle county changes in adjacent mode
    useEffect(() => {
        if (adjacentMode && selectedCounty !== "all") {
            // Reload tiles when county changes in adjacent mode
            reloadParcelTiles(adjacentMode);

            // Reapply county filter after tile reload
            setTimeout(() => {
                if (mapRef.current && selectedCounty) {
                    mapRef.current.setLayoutProperty("parcel-points", "visibility", "visible");
                    const filterExpr = ["==", ["downcase", ["get", "county"]], selectedCounty.toLowerCase()];
                    mapRef.current.setFilter("parcel-points", filterExpr);
                    if (mapRef.current.getLayer("parcel-outline")) {
                        mapRef.current.setLayoutProperty("parcel-outline", "visibility", "visible");
                        mapRef.current.setFilter("parcel-outline", filterExpr);
                    }
                    if (mapRef.current.getLayer("parcel-selected-outline")) {
                        mapRef.current.setFilter("parcel-selected-outline", ["==", ["get", "parcel"], ""]);
                    }


                }
            }, 1000);
        }
    }, [selectedCounty, adjacentMode]);


    //zoom boundary trigger track to invoke api call 
    useEffect(() => {
        const prevZoom = prevZoomRef.current;
        const currZoom = currentZoom;

        const crossedIntoFetchZone =
            prevZoom >= 13.1 && currZoom < 13.1;

        if (crossedIntoFetchZone) {
            // Check if any filters are active
            const hasActiveFilters =
                vacantFilterTouched.current ||
                zoneFilterTouched.current ||
                listedSaleFilterTouched.current ||
                (priceRange[0] !== sliderMinPrice || priceRange[1] !== sliderMaxPrice) ||
                (zestimateRange[0] !== sliderMinZestimate || zestimateRange[1] !== sliderMaxZestimate) ||
                (sizeRange[0] !== sliderMinSize || sizeRange[1] !== sliderMaxSize) ||
                (spreadRange[0] !== sliderMinSpread || spreadRange[1] !== sliderMaxSpread) ||
                zoneCodeFilter.length > 0 ||
                (Array.isArray(zoningTypFilter) ? zoningTypFilter.length > 0 : zoningTypFilter && zoningTypFilter !== "All");

            // Only trigger API fetch if no filters are applied
            // If filters are active, keep the existing filtered data
            if (!hasActiveFilters) {
                console.log(' Zoom crossed below 13.1 → trigger API fetch (no filters applied)');
                setZoomBoundaryTrigger(Date.now());
            } else {
                console.log(' Zoom crossed below 13.1 → skipping API fetch (filters applied, using existing data)');
            }
        }

        prevZoomRef.current = currZoom;
    }, [currentZoom, priceRange, zestimateRange, sizeRange, spreadRange, zoneCodeFilter, zoningTypFilter, sliderMinPrice, sliderMaxPrice, sliderMinZestimate, sliderMaxZestimate, sliderMinSize, sliderMaxSize, sliderMinSpread, sliderMaxSpread]);

    useEffect(() => {
        if (!selectedCounty || selectedCounty === 'all') return;

        // Check if county actually changed from previous value
        const previousCounty = selectedCountyRef.current;
        if (previousCounty !== selectedCounty) {
            // County changed, reset the flag to allow re-initialization
            minMaxInitialized.current = false;
            selectedCountyRef.current = selectedCounty;
            console.log(' County changed from', previousCounty, 'to', selectedCounty, '- resetting minMaxInitialized');
        }

        // Skip min/max initialization since it's handled in the adjacentMode useEffect
        // Only handle listed sale options here
        if (apiParcels.length > 0) {
            const listedVals = apiParcels.map((p) => p.listedsale || p.listed_sale || "Not listed");
            const uniqueListed = Array.from(new Set(listedVals));
            setListedSaleOptions(uniqueListed.sort());
        }
    }, [selectedCounty, apiParcels]);

    const parseCoordinate = (val) => {
        if (val === null || val === undefined) return null;

        // Handle stringified edge cases from MBTiles
        if (typeof val === 'string') {
            const clean = val.trim().toLowerCase();
            if (clean === 'null' || clean === 'undefined' || clean === 'nan' || clean === '') {
                return null;
            }
        }

        const num = parseFloat(val);
        return Number.isFinite(num) ? num : null;
    };

    const isValidLngLat = (lng, lat) => {
        return (
            lng !== null &&
            lat !== null &&
            lng >= -180 &&
            lng <= 180 &&
            lat >= -90 &&
            lat <= 90
        );
    };

    const loadBuilderLayerFeatures = (map) => {
        if (!map || !map.getSource("parcels_near_to_builder")) {
            setBuilderLayerFeatures([]);
            return;
        }

        setIsLoadingBuilderLayerFeatures(true);
        try {
            const features = map.querySourceFeatures("parcels_near_to_builder", {
                sourceLayer: "parcels_near_to_builder",
            }) || [];

            console.log("Source loaded:", map.isSourceLoaded("parcels_near_to_builder"));
console.log("Raw features:", features);
console.log("Feature count:", features.length);

            const unique = new Map();
            features.forEach((feature) => {
                const props = feature.properties || {};
                const id = props.parcel || props.parcelid || props.id || props.geo_id;
                if (id && !unique.has(id)) {
                    unique.set(id, { ...props });
                }
            });

            setBuilderLayerFeatures(Array.from(unique.values()));
            console.log("Loaded builder layer features:", Array.from(unique.values()));
        } catch (error) {
            console.warn("Error loading builder layer features:", error);
            setBuilderLayerFeatures([]);
        } finally {
            setIsLoadingBuilderLayerFeatures(false);
        }
    };




    // Apply filters to API parcels
    useEffect(() => {
        //  HARD GUARD — required
        if (!Array.isArray(apiParcels)) {
            console.warn("apiParcels is not an array:", apiParcels);
            setFilteredApiParcels([]);
            return;
        }

        if (apiParcels.length === 0) {
            setFilteredApiParcels([]);
            return;
        }

        console.log(
            "Client-side filtering API parcels:",
            apiParcels.length,
            "parcels"
        );

        // const filtered = apiParcels.filter(() => true);

        let filtered = apiParcels.map(p => ({ ...p }));

        if (adjacentMode) {
            const seen = new Set();
            filtered = filtered.filter(p => {
                const key = `${p.parcel}-${p.touching_parcel || p.adjacent_parcel}`;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            });
        }

        console.log(
            "Client-side filter result:",
            filtered.length,
            "parcels (filtered from",
            apiParcels.length,
            ")"
        );

        setFilteredApiParcels(filtered);
    }, [apiParcels]);

    // Callback when price range slider drag is complete
    const handlePriceRangeChangeComplete = (newRange) => {
        console.log('Price range drag complete:', newRange);
        setPriceRangeDragComplete(Date.now()); // Update timestamp to trigger effect
    };

    // Callback when zestimate range slider drag is complete
    const handleZestimateRangeChangeComplete = (newRange) => {
        console.log('Zestimate range drag complete:', newRange);
        setZestimateRangeDragComplete(Date.now()); // Update timestamp to trigger effect
    };

    // Callback when size range slider drag is complete
    const handleSizeRangeChangeComplete = (newRange) => {
        console.log('Size range drag complete:', newRange);
        setSizeRangeDragComplete(Date.now()); // Update timestamp to trigger effect
    };

    // Callback when spread range slider drag is complete
    const handleSpreadRangeChangeComplete = (newRange) => {
        console.log('Spread range drag complete:', newRange);
        setSpreadRangeDragComplete(Date.now()); // Update timestamp to trigger effect
    };

    // Handle sorting changes - trigger API call when sort options change
    useEffect(() => {
        if (!selectedCounty || selectedCounty === "all") return;

        console.log("🔄 Sort changed - sortBy:", sortBy, "sortOrder:", sortOrder);

        // Check if any filters are active
        const hasActiveFilters =
            (vacantFilter && vacantFilter !== "All") ||
            (zoneCodeFilter && zoneCodeFilter.length > 0) ||
            (listedSaleFilter && listedSaleFilter !== "All") ||
            (floodRiskFilter && floodRiskFilter !== "All") ||
            (municipalityFilter && municipalityFilter !== "All") ||
            (Array.isArray(zoningTypFilter) ? zoningTypFilter.length > 0 : zoningTypFilter && zoningTypFilter !== "All") ||
            (priceRange[0] !== sliderMinPrice || priceRange[1] !== sliderMaxPrice) ||
            (zestimateRange[0] !== sliderMinZestimate || zestimateRange[1] !== sliderMaxZestimate) ||
            (sizeRange[0] !== sliderMinSize || sizeRange[1] !== sliderMaxSize) ||
            (spreadRange[0] !== sliderMinSpread || spreadRange[1] !== sliderMaxSpread);

        if (hasActiveFilters || currentZoom < 13.1) {
            setCurrentOffset(0);
            setHasMoreData(true);
            setIsLoadingMore(false);
            fetchApiParcelsWithCurrentFilters(0);
        }
    }, [sortBy, sortOrder]);

    // Consolidated API call effect - combines all active filters (price, zestimate, size, spread, zone)
    // Triggers when any filter's drag complete timestamp changes and zoom < 13.1
    useEffect(() => {
        console.log("@@@***inside useeffect ", sortOrder)


        // Check if any filters are active (including municipality)
        const hasActiveFilters =
            (vacantFilter && vacantFilter !== "All") ||
            (zoneCodeFilter && zoneCodeFilter.length > 0) ||
            (listedSaleFilter && listedSaleFilter !== "All") ||
            (floodRiskFilter && floodRiskFilter !== "All") ||
            (municipalityFilter && municipalityFilter !== "All") ||
            (Array.isArray(zoningTypFilter) ? zoningTypFilter.length > 0 : zoningTypFilter && zoningTypFilter !== "All") ||
            (priceRange[0] !== sliderMinPrice || priceRange[1] !== sliderMaxPrice) ||
            (zestimateRange[0] !== sliderMinZestimate || zestimateRange[1] !== sliderMaxZestimate) ||
            (sizeRange[0] !== sliderMinSize || sizeRange[1] !== sliderMaxSize) ||
            (spreadRange[0] !== sliderMinSpread || spreadRange[1] !== sliderMaxSpread);



        console.log(' API Effect triggered - selectedCounty:', selectedCounty, 'zoom:', currentZoom);

        // Skip API call if county just changed but no filters are active, UNLESS filters were just cleared
        // This prevents duplicate calls when min/max initialization updates range states

        // if (selectedCounty && !hasActiveFilters && !filtersClearedRef.current) {
        //     console.log(' Skipping fetchApiParcels (county changed but no filters active)');
        //     return;
        // }

        const isZoneReset =
            zoneFilterTouched.current &&
            zoneCodeFilter.length === 0;

        const isFloodReset =
            floodRiskFilterTouched.current &&
            floodRiskFilter === "All";

        const isFilterReset = isZoneReset || isFloodReset;

        if (
            selectedCounty &&
            !hasActiveFilters &&
            !filtersClearedRef.current &&
            !isFilterReset
        ) {
            console.log(' Skipping fetchApiParcels (county changed but no filters active)');
            return;
        }

        // Reset the filters cleared flag after checking
        if (filtersClearedRef.current) {
            filtersClearedRef.current = false;
            console.log(' Filters were cleared - allowing API call');
        }

        if (isParcelSearchActive && !hasActiveFilters) {
            console.log(" Skipping fetchApiParcels (parcel search active)");
            return;
        }

        if (selectedCounty === "all" || !selectedCounty) {
            console.log(' Skipping: No county selected');
            return;
        }

        // Check if zone code filter is active (dropdown, not slider)
        const isZoneCodeFilterActive = zoneCodeFilter && zoneCodeFilter.length > 0;

        // Check if vacant filter is active (checkbox, not slider)
        const isVacantFilterActive = vacantFilter && vacantFilter !== "All";

        const isMunicipalityFilterActive =
            municipalityFilter && municipalityFilter !== "All";

        if (isMunicipalityFilterActive) {
            municipalityFilterTouched.current = true;
        }


        // Track when filters are touched
        if (isZoneCodeFilterActive) {
            zoneFilterTouched.current = true;
        }
        if (isVacantFilterActive) {
            vacantFilterTouched.current = true;
        }
        if (listedSaleFilter && listedSaleFilter !== "All") {
            listedSaleFilterTouched.current = true;
        }
        if (floodRiskFilter && floodRiskFilter !== "All") {
            floodRiskFilterTouched.current = true;
        }

        console.log(' Filter states:', {
            vacantFilter,
            isVacantFilterActive,
            vacantFilterTouched: vacantFilterTouched.current,
            listedSaleFilter,
            isListedSaleFilterActive: listedSaleFilter && listedSaleFilter !== "All",
            listedSaleFilterTouched: listedSaleFilterTouched.current,
            zoneCodeFilter,
            isZoneCodeFilterActive,
            zoneFilterTouched: zoneFilterTouched.current,
            dragStates: {
                price: priceRangeDragComplete,
                zestimate: zestimateRangeDragComplete,
                size: sizeRangeDragComplete,
                spread: spreadRangeDragComplete
            }
        });

        // Skip ONLY on initial load when no filters have ever been touched
        // We trigger API calls when:
        // 1. Any slider has been dragged at least once (timestamps > 0), OR
        // 2. Any dropdown/checkbox filter is currently active (not "All"), OR
        // 3. Any dropdown/checkbox filter was previously active (touched refs), OR
        // 4. Sorting is active (not default values)
        const hasAnySliderBeenDragged = priceRangeDragComplete !== 0 || zestimateRangeDragComplete !== 0 ||
            sizeRangeDragComplete !== 0 || spreadRangeDragComplete !== 0;


        const isAnyDropdownFilterActive = isZoneCodeFilterActive || isVacantFilterActive || (listedSaleFilter && listedSaleFilter !== "All") || (floodRiskFilter && floodRiskFilter !== "All") || isMunicipalityFilterActive || (Array.isArray(zoningTypFilter) ? zoningTypFilter.length > 0 : zoningTypFilter && zoningTypFilter !== "All");

        const hasAnyDropdownBeenTouched =
            zoneFilterTouched.current ||
            vacantFilterTouched.current ||
            listedSaleFilterTouched.current;
        // ||  municipalityFilterTouched.current;


        const isSortingActive = sortBy !== null; // Always allow API calls when sorting is specified

        // Check if zoning type filter is active
        // const isZoningTypFilterActive = Array.isArray(zoningTypFilter) ? zoningTypFilter.length > 0 : zoningTypFilter && zoningTypFilter !== "All";

        // Check if ANY filter (other than county) is active - INCLUDE zoningTypFilter
        const hasAnyFilterActive = hasAnySliderBeenDragged || isAnyDropdownFilterActive || hasAnyDropdownBeenTouched || isSortingActive;

        if (!hasAnyFilterActive) {

            console.log('Skipping: No filters activated yet');
            return;
        }


        // Only call API with filters when zoom < 13.1
        // if (currentZoom < 13.1) {
        if (currentZoom < 13.1 || hasAnyFilterActive) {
            console.log(' Zoom < 13.1, checking filters...');


            // Check which filters are active using small absolute tolerances (for log scale)
            const isPriceFilterActive = sliderMinPrice !== undefined && sliderMaxPrice !== undefined &&
                (Math.abs(priceRange[0] - sliderMinPrice) > 0.01 ||
                    Math.abs(priceRange[1] - sliderMaxPrice) > 0.01);

            const isZestimateFilterActive = sliderMinZestimate !== undefined && sliderMaxZestimate !== undefined &&
                (Math.abs(zestimateRange[0] - sliderMinZestimate) > 0.1 ||
                    Math.abs(zestimateRange[1] - sliderMaxZestimate) > 0.1);

            const isSizeFilterActive = sliderMinSize !== undefined && sliderMaxSize !== undefined &&
                (Math.abs(sizeRange[0] - sliderMinSize) > 0.01 ||
                    Math.abs(sizeRange[1] - sliderMaxSize) > 0.01);

            const isSpreadFilterActive = sliderMinSpread !== undefined && sliderMaxSpread !== undefined &&
                (Math.abs(spreadRange[0] - sliderMinSpread) > 0.1 ||
                    Math.abs(spreadRange[1] - sliderMaxSpread) > 0.1);

            console.log(' Active Filters:', {
                price: isPriceFilterActive,
                zestimate: isZestimateFilterActive,
                size: isSizeFilterActive,
                spread: isSpreadFilterActive,
                zoneCode: isZoneCodeFilterActive,
                vacant: isVacantFilterActive
            });

            setCurrentOffset(0);
            setHasMoreData(true);
            setIsLoadingMore(false);

            fetchApiParcelsWithCurrentFilters(0);
        }
    }, [
        priceRangeDragComplete,
        zestimateRangeDragComplete,
        sizeRangeDragComplete,
        spreadRangeDragComplete,
        zoneCodeFilter,
        municipalityFilter,
        vacantFilter,
        listedSaleFilter,
        floodRiskFilter,
        zoningTypFilter,
        sortBy,
        sortOrder,
        // currentZoom,
        selectedCounty,
        zoomBoundaryTrigger,
        // zoneCodes,
    ]);

    // Consolidated map tile filter effect - combines all active filters (price, zestimate, size)
    // Applies filters to map layers at all zoom levels
    useEffect(() => {
        if (!mapRef.current || !mapLoaded) return;
        if (adjacentMode) return;
        const map = mapRef.current;

        // Apply filtering when county is selected
        if (selectedCounty !== "all") {
            // Check which filters are active
            const isPriceFilterActive = sliderMinPrice !== undefined && sliderMaxPrice !== undefined &&
                (Math.abs(priceRange[0] - sliderMinPrice) > Math.abs(sliderMaxPrice - sliderMinPrice) * 0.01 ||
                    Math.abs(priceRange[1] - sliderMaxPrice) > Math.abs(sliderMaxPrice - sliderMinPrice) * 0.01);

            const isZestimateFilterActive = sliderMinZestimate !== undefined && sliderMaxZestimate !== undefined &&
                (Math.abs(zestimateRange[0] - sliderMinZestimate) > Math.abs(sliderMaxZestimate - sliderMinZestimate) * 0.01 ||
                    Math.abs(zestimateRange[1] - sliderMaxZestimate) > Math.abs(sliderMaxZestimate - sliderMinZestimate) * 0.01);

            const isSizeFilterActive = sliderMinSize !== undefined && sliderMaxSize !== undefined &&
                (Math.abs(sizeRange[0] - sliderMinSize) > Math.abs(sliderMaxSize - sliderMinSize) * 0.01 ||
                    Math.abs(sizeRange[1] - sliderMaxSize) > Math.abs(sliderMaxSize - sliderMinSize) * 0.01);

            const isSpreadFilterActive = sliderMinSpread !== undefined && sliderMaxSpread !== undefined &&
                (Math.abs(spreadRange[0] - sliderMinSpread) > Math.abs(sliderMaxSpread - sliderMinSpread) * 0.01 ||
                    Math.abs(spreadRange[1] - sliderMaxSpread) > Math.abs(sliderMaxSpread - sliderMinSpread) * 0.01);

            const isZoneCodeFilterActive = zoneCodeFilter && zoneCodeFilter.length > 0;
            const isZoningTypeFilterActive = Array.isArray(zoningTypFilter) ? zoningTypFilter.length > 0 : zoningTypFilter && zoningTypFilter !== "All";

            const isVacantFilterActive = vacantFilter && vacantFilter !== "All";

            // Build combined filter starting with county
            const filter = [
                "all",
                ["==", ["downcase", ["get", "county"]], selectedCounty.toLowerCase()]
            ];

            // Add price filter if active
            if (isPriceFilterActive) {
                filter.push(
                    ["has", "totallandvalue"],
                    ["!=", ["get", "totallandvalue"], null],
                    [">=", ["to-number", ["get", "totallandvalue"], 0], priceRange[0]],
                    ["<=", ["to-number", ["get", "totallandvalue"], 0], priceRange[1]]
                );
            }

            // Add zestimate filter if active
            if (isZestimateFilterActive) {
                filter.push(
                    ["has", "zestimate"],
                    ["!=", ["get", "zestimate"], null],
                    [">=", ["to-number", ["get", "zestimate"], 0], zestimateRange[0]],
                    ["<=", ["to-number", ["get", "zestimate"], 0], zestimateRange[1]]
                );
            }

            // Add size filter if active
            if (isSizeFilterActive) {
                filter.push(
                    ["has", "parcelsize"],
                    ["!=", ["get", "parcelsize"], null],
                    [">=", ["to-number", ["get", "parcelsize"], 0], sizeRange[0]],
                    ["<=", ["to-number", ["get", "parcelsize"], 0], sizeRange[1]]
                );
            }

            // Add spread filter if active
            if (isSpreadFilterActive) {
                filter.push(
                    ["has", "spreadpercentage"],
                    ["!=", ["get", "spreadpercentage"], null],
                    [">=", ["to-number", ["get", "spreadpercentage"], 0], spreadRange[0]],
                    ["<=", ["to-number", ["get", "spreadpercentage"], 0], spreadRange[1]]
                );
            }

            // Add zone code filter if active
            if (isZoneCodeFilterActive) {
                filter.push(
                    ["has", "zonedcodelocal"],
                    ["in", ["downcase", ["get", "zonedcodelocal"]], ["literal", zoneCodeFilter.map(z => z.toLowerCase())]]
                );
            }

            // Add zoning type filter if active
            if (isZoningTypeFilterActive) {
                filter.push(
                    ["has", "parcel_custom_zoning_typ"],
                    ["in", ["downcase", ["get", "parcel_custom_zoning_typ"]], ["literal", (Array.isArray(zoningTypFilter) ? zoningTypFilter : [zoningTypFilter]).map(z => z.toLowerCase())]]
                );
            }

            // Add favorites-only filter if active
            if (showFavoritesOnly) {
                if (favoriteParcelIdsLower.length === 0) {
                    // No favorites yet: hide everything
                    filter.push(["==", ["get", "parcel"], ""]);
                } else {
                    filter.push([
                        "any",
                        ["in", ["downcase", ["get", "parcel"]], ["literal", favoriteParcelIdsLower]],
                        ["in", ["downcase", ["get", "parcelid"]], ["literal", favoriteParcelIdsLower]]
                    ]);
                }
            }

            // Add vacant filter if active
            if (isVacantFilterActive) {
                filter.push(
                    ["has", "is_vaccant"],
                    ["==", ["to-number", ["get", "is_vaccant"], 0], 1]
                );
            }

            // Add listed sale filter if active
            const isListedSaleFilterActive = listedSaleFilter && listedSaleFilter !== "All";
            if (isListedSaleFilterActive) {
                filter.push(
                    ["has", "listedsale"],
                    [
                        "any",
                        ["==", ["get", "listedsale"], "PendingSale"],
                        ["==", ["get", "listedsale"], "ActiveListing"]
                    ]
                );
            }

            // Add flood risk filter if active
            const isFloodFilterActive = floodRiskFilter && floodRiskFilter !== "All";
            // if (isFloodFilterActive) {
            if (isFloodFilterActive && !adjacentMode) {
                filter.push(
                    ["has", "parcelcustomfemaflood"],
                    ["==", ["get", "parcelcustomfemaflood"], floodRiskFilter]
                );
            }

            // Add municipality filter if active
            const isMunicipalityFilterActive =
                municipalityFilter && municipalityFilter !== "All";

            if (isMunicipalityFilterActive) {
                filter.push(
                    ["has", "municipality"],
                    ["==", ["downcase", ["get", "municipality"]], municipalityFilter.toLowerCase()]
                );
            }

            // Check if ANY filter is active (excluding county which is always there)
            const hasAnyActiveFilter = isPriceFilterActive || isZestimateFilterActive ||
                isSizeFilterActive || isSpreadFilterActive ||
                isZoneCodeFilterActive || isVacantFilterActive ||
                isListedSaleFilterActive || isFloodFilterActive;

            console.log('🗺️ Map Filter Update:', {
                isZestimateActive: isZestimateFilterActive,
                zestimateRange,
                sliderMinZestimate,
                sliderMaxZestimate,
                tolerance: Math.abs(sliderMaxZestimate - sliderMinZestimate) * 0.01
            });

            // Don't apply normal parcel filters when builder mode is active
            if (builderLayerActive) {
                const hideAllFilter = ["==", ["get", "county"], ""];
                const normalParcelLayers = [
                    "parcel-fill",
                    "parcel-outline",
                    "parcel-hitbox",
                    "parcel-points",
                    "parcel-hover",
                    "parcel-selected-outline",
                    "parcel-selected-fill"
                ];

                normalParcelLayers.forEach((layerId) => {
                    if (!map.getLayer(layerId)) return;
                    map.setLayoutProperty(layerId, "visibility", "none");
                    map.setFilter(layerId, hideAllFilter);
                });
                return;
            }

            // Apply the combined filter to all parcel layers
            if (map.getLayer("parcel-fill")) map.setFilter("parcel-fill", filter);
            if (map.getLayer("parcel-outline")) map.setFilter("parcel-outline", filter);
            if (map.getLayer("parcel-hitbox")) map.setFilter("parcel-hitbox", filter);
            if (map.getLayer("parcel-points")) map.setFilter("parcel-points", filter);
            if (map.getLayer("parcel-hover")) map.setFilter("parcel-hover", filter);
        } else {
            // No county selected - hide all parcels
            const hideAllFilter = ["==", ["get", "county"], ""];
            if (map.getLayer("parcel-fill")) map.setFilter("parcel-fill", hideAllFilter);
            if (map.getLayer("parcel-outline")) map.setFilter("parcel-outline", hideAllFilter);
            if (map.getLayer("parcel-hitbox")) map.setFilter("parcel-hitbox", hideAllFilter);
            if (map.getLayer("parcel-points")) map.setFilter("parcel-points", hideAllFilter);
            if (map.getLayer("parcel-hover")) map.setFilter("parcel-hover", hideAllFilter);
        }
    }, [
        adjacentMode,
        priceRange,
        zestimateRange,
        municipalityFilter,
        sizeRange,
        spreadRange,
        zoneCodeFilter,
        vacantFilter,
        listedSaleFilter,
        floodRiskFilter,
        selectedCounty,
        sliderMinPrice,
        sliderMaxPrice,
        sliderMinZestimate,
        sliderMaxZestimate,
        sliderMinSize,
        sliderMaxSize,
        sliderMinSpread,
        sliderMaxSpread,
        zoningTypFilter,
        showFavoritesOnly,
        favoriteParcelIdsLower,
        builderLayerActive,
        mapLoaded
    ]);

    useEffect(() => {
        if (!mapRef.current || !mapLoaded) return;
        const map = mapRef.current;

        const updateVisibleParcels = () => {
            // Only update visible parcels when zoom >= 13.1
            const currentZoom = map.getZoom();
            if (currentZoom < 13.1) {
                console.log("Zoom < 13.1, not querying tile parcels");
                return;
            }

            // Only update if the layer exists and is visible
            // if (!map.getLayer("parcel-points")) {
            if (!map.getLayer("parcel-outline")) {
                console.log("parcel-outline layer not ready yet");
                return;
            }

            // const visibility = map.getLayoutProperty("parcel-points", "visibility");
            const visibility = map.getLayoutProperty("parcel-outline", "visibility");
            if (visibility === "none") {
                console.log("parcel-outline layer is hidden");
                setFilteredParcels([]);
                return;
            }

            // Query features rendered in viewport (includes buffer tiles)
            // const featuresInView = map.queryRenderedFeatures({ layers: ["parcel-points"] });
            const featuresInView = map.queryRenderedFeatures({ layers: ["parcel-outline"] });
            console.log(`Raw features from queryRenderedFeatures: ${featuresInView.length}`);

            // Get actual visible viewport bounds to filter out buffer tiles
            const bounds = map.getBounds();

            // Filter to only include features actually within visible bounds
            const filteredFeatures = featuresInView.filter(f => {
                const props = f.properties;
                const lon = parseCoordinate(props.longitude ?? props.lon ?? props.lng ?? props.x);
                const lat = parseCoordinate(props.latitude ?? props.lat ?? props.y);

                if (!isValidLngLat(lon, lat)) {
                    return false; // Skip features without valid coordinates
                }

                // Check if coordinates are within visible bounds
                return bounds.contains([lon, lat]);
            });

            console.log(`After bounds filtering: ${filteredFeatures.length} features`);

            // Deduplicate features by parcel ID (or other unique identifier)
            const uniqueFeatures = new Map();
            filteredFeatures.forEach(f => {
                const props = f.properties;
                const uniqueId = props.parcel || props.parcelid || props.geo_id || props.id;
                if (uniqueId && !uniqueFeatures.has(uniqueId)) {
                    uniqueFeatures.set(uniqueId, props);
                }
            });

            let parcelsArray = Array.from(uniqueFeatures.values());

            // Apply favorites-only filter if active
            if (showFavoritesOnly) {
                if (favoriteParcelIdSet.size === 0) {
                    parcelsArray = [];
                } else {
                    parcelsArray = parcelsArray.filter((p) => {
                        const identifier = (p.parcel || p.parcelid || p.geo_id || p.id || "").toString();
                        return favoriteParcelIdSet.has(identifier);
                    });
                }
            }

            // Apply Price (zestimate) filter if active
            const isZestimateFilterActive = sliderMinZestimate !== undefined && sliderMaxZestimate !== undefined &&
                (Math.abs(zestimateRange[0] - sliderMinZestimate) > Math.abs(sliderMaxZestimate - sliderMinZestimate) * 0.01 ||
                    Math.abs(zestimateRange[1] - sliderMaxZestimate) > Math.abs(sliderMaxZestimate - sliderMinZestimate) * 0.01);

            if (isZestimateFilterActive) {
                parcelsArray = parcelsArray.filter(p => {
                    const zest = parseFloat(p.zestimate) || 0;
                    return zest >= zestimateRange[0] && zest <= zestimateRange[1];
                });
                console.log(`After Price filter: ${parcelsArray.length} parcels (range: ${zestimateRange[0]}-${zestimateRange[1]})`);
            }

            // Apply Dev Value (totallandvalue) filter if active
            const isPriceFilterActive = sliderMinPrice !== undefined && sliderMaxPrice !== undefined &&
                (Math.abs(priceRange[0] - sliderMinPrice) > Math.abs(sliderMaxPrice - sliderMinPrice) * 0.01 ||
                    Math.abs(priceRange[1] - sliderMaxPrice) > Math.abs(sliderMaxPrice - sliderMinPrice) * 0.01);

            if (isPriceFilterActive) {
                parcelsArray = parcelsArray.filter(p => {
                    const landValue = parseFloat(p.totallandvalue) || 0;
                    return landValue >= priceRange[0] && landValue <= priceRange[1];
                });
                console.log(`After Dev Value filter: ${parcelsArray.length} parcels (range: ${priceRange[0]}-${priceRange[1]})`);
            }

            // Apply Size (parcelsize) filter if active
            const isSizeFilterActive = sliderMinSize !== undefined && sliderMaxSize !== undefined &&
                (Math.abs(sizeRange[0] - sliderMinSize) > Math.abs(sliderMaxSize - sliderMinSize) * 0.01 ||
                    Math.abs(sizeRange[1] - sliderMaxSize) > Math.abs(sliderMaxSize - sliderMinSize) * 0.01);

            if (isSizeFilterActive) {
                parcelsArray = parcelsArray.filter(p => {
                    const size = parseFloat(p.parcelsize) || 0;
                    return size >= sizeRange[0] && size <= sizeRange[1];
                });
                console.log(`After Size filter: ${parcelsArray.length} parcels (range: ${sizeRange[0]}-${sizeRange[1]})`);
            }

            // Apply Spread Percentage filter if active
            const isSpreadFilterActive = sliderMinSpread !== undefined && sliderMaxSpread !== undefined &&
                (Math.abs(spreadRange[0] - sliderMinSpread) > Math.abs(sliderMaxSpread - sliderMinSpread) * 0.01 ||
                    Math.abs(spreadRange[1] - sliderMaxSpread) > Math.abs(sliderMaxSpread - sliderMinSpread) * 0.01);

            if (isSpreadFilterActive) {
                parcelsArray = parcelsArray.filter(p => {
                    const spread = parseFloat(p.spreadpercentage) || 0;
                    return spread >= spreadRange[0] && spread <= spreadRange[1];
                });
                console.log(`After Spread % filter: ${parcelsArray.length} parcels (range: ${spreadRange[0]}-${spreadRange[1]})`);
            }

            // Apply Vacant filter if active
            const isVacantFilterActive = vacantFilter && vacantFilter !== "All";

            if (isVacantFilterActive) {
                parcelsArray = parcelsArray.filter(p => {
                    const isVacant = parseInt(p.is_vaccant) || 0;
                    return isVacant === 1;
                });
                console.log(`After Vacant filter: ${parcelsArray.length} parcels (vacant only)`);
            }

            // Apply Listed Sale filter if active
            const isListedSaleFilterActive = listedSaleFilter && listedSaleFilter !== "All";

            if (isListedSaleFilterActive) {
                parcelsArray = parcelsArray.filter(p => {
                    const listed = (p.listedsale || p.listed_sale || "").toLowerCase();
                    return listed === "all";
                });
                console.log(`After Listed Sale filter: ${parcelsArray.length} parcels (${listedSaleFilter} only)`);
            }

            // Apply Flood Risk filter if active
            const isFloodFilterActive = floodRiskFilter && floodRiskFilter !== "All";

            // if (isFloodFilterActive) {
            if (isFloodFilterActive && !adjacentMode) {
                parcelsArray = parcelsArray.filter(p => {
                    const flood = p.parcelcustomfemaflood || p.FloodFemaRisk || "";
                    return flood.includes(floodRiskFilter);
                });
                console.log(`After Flood Risk filter: ${parcelsArray.length} parcels (${floodRiskFilter} only)`);
            }

            console.log(`Found ${parcelsArray.length} unique parcels in viewport at zoom ${currentZoom.toFixed(2)}`);
            setFilteredParcels(parcelsArray);
        };

        map.on("moveend", updateVisibleParcels);
        map.on("idle", updateVisibleParcels); // Also update after filters change
        map.on("zoomend", updateVisibleParcels); // Update when zoom changes
        console.log("Added moveend, idle, and zoomend listeners for visible parcels update.");

        return () => {
            map.off("moveend", updateVisibleParcels);
            map.off("idle", updateVisibleParcels);
            map.off("zoomend", updateVisibleParcels);
        };
    }, [mapLoaded, zestimateRange, priceRange, sizeRange, vacantFilter, listedSaleFilter, floodRiskFilter, zoningTypFilter, sliderMinZestimate, sliderMaxZestimate, sliderMinPrice, sliderMaxPrice, sliderMinSize, sliderMaxSize, showFavoritesOnly, favoriteParcelIdSet]);

    // Manually trigger parcel list update when zoom or county changes
    // This ensures updates happen even if map events don't fire
    useEffect(() => {
        if (!mapRef.current || !mapLoaded) return;
        if (currentZoom < 13.1) return; // Only for zoom >= 13.1
        if (selectedCounty === "all") return;

        const map = mapRef.current;

        // Small delay to ensure map has finished rendering
        const timer = setTimeout(() => {
            // Force update by triggering a map idle event
            map.fire("idle");
        }, 150);

        return () => clearTimeout(timer);
    }, [currentZoom, selectedCounty, mapLoaded, zestimateRangeDragComplete, priceRangeDragComplete, sizeRangeDragComplete]);


    const clearParcelHighlight = () => {
        const map = mapRef.current;
        if (!map) return;

        // if (map.getLayer("parcel-selected-fill")) {
        //     map.setFilter("parcel-selected-fill", ["==", ["get", "parcel"], ""]);
        // }
        if (map.getLayer("parcel-selected-outline")) {
            map.setFilter("parcel-selected-outline", ["==", ["get", "parcel"], ""]);
        }
    };

    const updateMapWithSearchParcels = (parcels) => {
        const map = mapRef.current;
        if (!map) return;

        if (!parcels || parcels.length === 0) return;

        // Collect parcel IDs
        // const parcelIds = parcels
        //     .map(p => p.parcel || p.parcelid || p.geo_id || p.id)
        //     .filter(Boolean)
        //     .map(String);

        const firstParcel = parcels[0];

        const firstParcelId =
            firstParcel?.parcel ||
            firstParcel?.parcelid ||
            firstParcel?.geo_id ||
            firstParcel?.id;

        if (!firstParcelId) return;

        const parcelIds = [String(firstParcelId)];

        if (adjacentMode) {
            const touching =
                firstParcel.touching_parcel ||
                firstParcel.adjacent_parcel;

            if (touching) {
                parcelIds.push(String(touching));
            }
        }

        if (parcelIds.length === 0) return;



        // ⭐ HIGHLIGHT instead of filtering

        // if (map.getLayer("parcel-selected-fill")) {
        //     map.setFilter("parcel-selected-fill", [
        //         "any",
        //         ["in", ["to-string", ["get", "parcel"]], ["literal", parcelIds]],
        //         ["in", ["to-string", ["get", "parcelid"]], ["literal", parcelIds]]
        //     ]);
        //     map.setLayoutProperty("parcel-selected-fill", "visibility", "visible");
        // }

        // if (map.getLayer("parcel-selected-outline")) {
        //     map.setFilter("parcel-selected-outline", [
        //         "any",
        //         ["in", ["to-string", ["get", "parcel"]], ["literal", parcelIds]],
        //         ["in", ["to-string", ["get", "parcelid"]], ["literal", parcelIds]]
        //     ]);
        //     map.setLayoutProperty("parcel-selected-outline", "visibility", "visible");
        // }

        //  Outline handling (update OR create)
        if (map.getLayer("parcel-selected-outline")) {

            map.setFilter("parcel-selected-outline", [
                "any",
                ["in", ["to-string", ["get", "parcel"]], ["literal", parcelIds]],
                ["in", ["to-string", ["get", "parcelid"]], ["literal", parcelIds]]
            ]);

            map.setLayoutProperty("parcel-selected-outline", "visibility", "visible");

        } else if (map.getSource("parcels")) {

            // Create red outline layer if missing
            map.addLayer({
                id: "parcel-selected-outline",
                type: "line",
                source: "parcels",
                "source-layer": "fema",
                layout: { visibility: "visible" },
                paint: {
                    "line-color": "#fd0224",
                    "line-width": 2.5,
                    "line-opacity": 0.95
                },
                filter: [
                    "any",
                    ["in", ["to-string", ["get", "parcel"]], ["literal", parcelIds]],
                    ["in", ["to-string", ["get", "parcelid"]], ["literal", parcelIds]]
                ]
            });

        }

        // ⭐ Fly to first parcel
        // const firstParcel = parcels[0];

        const lon = parseCoordinate(
            firstParcel.longitude ??
            firstParcel.lon ??
            firstParcel.lng ??
            firstParcel.x
        );

        const lat = parseCoordinate(
            firstParcel.latitude ??
            firstParcel.lat ??
            firstParcel.y
        );

        if (isValidLngLat(lon, lat)) {

            const screenWidth = window.innerWidth;
            const leftPanelWidth = screenWidth * (listWidth / 100);
            const offset = -(leftPanelWidth / 2);

            map.flyTo({
                center: [lon, lat],
                zoom: 16,
                speed: 0.9,
                offset: [offset, 0]
            });
        }
    };

    const updateMapWithSearchParcels1 = (parcels) => {
        const map = mapRef.current;
        if (!map || !map.getLayer("parcel-points")) return;

        if (!parcels || parcels.length === 0) {
            // hide parcels if no results
            map.setFilter("parcel-points", ["==", ["get", "parcel"], ""]);
            return;
        }

        // Collect parcel IDs from API results
        const parcelIds = parcels
            .map(p => p.parcel || p.parcelid || p.geo_id || p.id)
            .filter(Boolean);

        if (parcelIds.length === 0) return;

        // Mapbox IN filter
        const filterExpr = [
            "in",
            ["to-string", ["get", "parcel"]],
            ["literal", parcelIds.map(String)]
        ];

        // Apply filter
        map.setLayoutProperty("parcel-points", "visibility", "visible");
        map.setFilter("parcel-points", filterExpr);

        if (map.getLayer("parcel-outline")) {
            map.setFilter("parcel-outline", filterExpr);
        }

        // Fly to first parcel location
        const firstParcel = parcels[0];

        const lon = parseCoordinate(
            firstParcel.longitude ??
            firstParcel.lon ??
            firstParcel.lng ??
            firstParcel.x
        );

        const lat = parseCoordinate(
            firstParcel.latitude ??
            firstParcel.lat ??
            firstParcel.y
        );

        if (isValidLngLat(lon, lat)) {

            // Panel offset logic (same as your row click)
            const screenWidth = window.innerWidth;
            const leftPanelWidth = screenWidth * (listWidth / 100);
            const offset = -(leftPanelWidth / 2);

            map.flyTo({
                center: [lon, lat],
                zoom: 16,
                speed: 0.9,
                offset: [offset, 0]
            });
        }
    };

    // update left panel + show parcels 
    const updateLeftPanelParcels = (countyName) => {

        console.log("=== updateLeftPanelParcels START ===");
        console.log("County:", countyName);
        console.log("Adjacent Mode:", adjacentMode);
        console.log("Selected County:", selectedCounty);
        console.log("Map exists:", !!mapRef.current);

        setIsParcelSearchActive(false);
        const map = mapRef.current;
        // if (!map || !map.getLayer("parcel-points")) return;

        if (!map) {
            console.log("❌ Map not available");
            return;
        }

        console.log("Map layers available:", {
            "parcel-points": !!map.getLayer("parcel-points"),
            "parcel-outline": !!map.getLayer("parcel-outline"),
            "parcel-hover": !!map.getLayer("parcel-hover")
        });

        if (!map.getLayer("parcel-points")) {
            console.log("❌ parcel-points layer not found");
            return;
        }

        if (!countyName || countyName === "all") {
            console.log("🔄 Clearing all parcels");
            map.setLayoutProperty("parcel-points", "visibility", "none");
            if (map.getLayer("parcel-outline")) {
                map.setLayoutProperty("parcel-outline", "visibility", "none");
            }
            if (map.getLayer("parcel-hover")) {
                map.setLayoutProperty("parcel-hover", "visibility", "none");
            }
            map.setFilter("parcel-points", null);
            if (map.getLayer("parcel-outline")) {
                map.setFilter("parcel-outline", null);
            }
            if (map.getLayer("parcel-hover")) {
                map.setFilter("parcel-hover", null);
            }
            setFilteredParcels([]);
            console.log("=== updateLeftPanelParcels END (CLEARED) ===");
            return;
        }
        console.log(" Setting layers visible");
        map.setLayoutProperty("parcel-points", "visibility", "visible");
        if (map.getLayer("parcel-outline")) {
            map.setLayoutProperty("parcel-outline", "visibility", "visible");
        }
        if (map.getLayer("parcel-hover")) {
            map.setLayoutProperty("parcel-hover", "visibility", "visible");
        }

        const filterExpr = ["==", ["downcase", ["get", "county"]], countyName];
        console.log("🔍 Applying filter:", JSON.stringify(filterExpr));


        // Apply filter to both layers
        // try {
        map.setFilter("parcel-points", filterExpr);
        if (map.getLayer("parcel-outline")) {
            map.setFilter("parcel-outline", filterExpr);
        }
        // } catch (e) {
        //     map.once("idle", () => {
        //         map.setFilter("parcel-points", filterExpr);
        //         if (map.getLayer("parcel-outline")) {
        //             map.setFilter("parcel-outline", filterExpr);
        //         }
        //     });
        // }

        // Wait until map is idle (tiles loaded) before querying features
        console.log("⏳ Waiting for map idle to query features");
        map.once("idle", () => {
            console.log("🗺️ Map is idle, querying features");
            // const features = map.queryRenderedFeatures({ layers: ["parcel-points"] });
            const features = map.queryRenderedFeatures({ layers: ["parcel-outline"] });
            console.log("📊 Raw features found:", features.length);

            // Deduplicate features by parcel ID
            const uniqueFeatures = new Map();
            features.forEach(f => {
                const props = f.properties;
                const uniqueId = props.parcel || props.parcelid || props.geo_id || props.id;
                if (uniqueId && !uniqueFeatures.has(uniqueId)) {
                    uniqueFeatures.set(uniqueId, props);
                }
            });

            const uniqueParcels = Array.from(uniqueFeatures.values());
            console.log("📦 Unique parcels:", uniqueParcels.length);
            setFilteredParcels(uniqueParcels);
            console.log(`Found ${uniqueParcels.length} unique parcels in ${countyName} County (${features.length} features total)`);
            console.log("=== updateLeftPanelParcels END ===");
        });
    };


    // --- dropdown change ---
    const onDropdownChange = e => {
        const val = String(e.target.value).toLowerCase();
        setSelectedCounty(val);
        updateLeftPanelParcels(val);
        //fetchTotalCount(val); // Fetch total count for the selected county

        // Reset bounds initialization flag when county changes
        boundsInitialized.current = false;

        // Reset filter touched refs when county changes
        vacantFilterTouched.current = false;
        zoneFilterTouched.current = false;
        listedSaleFilterTouched.current = false;

        // Clear checkbox filters when county changes
        setZoneCodeFilter([]);
        setFloodRiskFilter("All");
        setListedSaleFilter("All");
        setVacantFilter("All");

        // Fetch API data for left panel
        //fetchApiParcels(val);

        // Fly to county center when dropdown changes
        if (mapRef.current && val !== "all") {
            const map = mapRef.current;

            // Query county features to get geometry
            const features = map.querySourceFeatures('nc-counties', {
                sourceLayer: 'counties',
                filter: ['==', ['downcase', ['get', 'County']], val]
            });

            if (features.length === 0) {
                console.warn('No features found for county:', val);
                // Fallback to center-based navigation
                const center = countyCenters[val] || NC_CENTER;
                const screenWidth = window.innerWidth;
                const leftPanelWidth = screenWidth * (listWidth / 100);
                const offset = -(leftPanelWidth / 2);
                map.flyTo({ center, zoom: 9, speed: 0.8, offset: [offset, 0] });
                return;
            }

            // Use first feature to compute bounds (same as county click)
            const feature = features[0];
            let bounds = new mapboxgl.LngLatBounds();

            const geom = feature.geometry;

            if (geom.type === "Polygon") {
                geom.coordinates[0].forEach(coord => bounds.extend(coord));
            } else if (geom.type === "MultiPolygon") {
                geom.coordinates.forEach(polygon => {
                    polygon[0].forEach(coord => bounds.extend(coord));
                });
            }

            const center = bounds.getCenter();

            if (!Number.isFinite(center.lng) || !Number.isFinite(center.lat)) {
                console.warn("Invalid county center:", feature);
                return;
            }

            const screenWidth = window.innerWidth;
            const leftPanelWidth = screenWidth * (listWidth / 100);
            const offset = -(leftPanelWidth / 2);

            map.flyTo({
                center: [center.lng, center.lat],
                zoom: 9,
                speed: 0.8,
                offset: [offset, 0],
            });
            // const originalCenter = countyCenters[val] || NC_CENTER;

            // // Get current map bounds
            // const bounds = map.getBounds();

            // // Calculate map width in km (west → east at same latitude)
            // const mapWidthKm = turf.distance(
            //     [bounds.getWest(), originalCenter[1]],
            //     [bounds.getEast(), originalCenter[1]],
            //     { units: "kilometers" }
            // );

            // // Left panel covers 60%, so shift center to middle of visible 40%
            // // → shift east by 30% of total width
            // const shiftKm = mapWidthKm * 0.4;

            // // Create shifted center using Turf
            // const shiftedCenter = turf.destination(
            //     originalCenter,
            //     shiftKm,
            //     90, // 90° = east
            //     { units: "kilometers" }
            // );

            // map.flyTo({
            //     center: shiftedCenter.geometry.coordinates,
            //     zoom: 9,
            //     speed: 0.8,
            // });
        } else if (mapRef.current && val === "all") {
            // Fly back to NC center when "all" is selected
            mapRef.current.flyTo({
                center: NC_CENTER,
                zoom: 6.5,
                speed: 0.8,
            });
        }
    };


    // --- PropertyList row click handler ---
    const centerMapOnParcel = (property) => {
        if (!mapRef.current) return;

        const lon = parseCoordinate(property.longitude ?? property.lon ?? property.lng ?? property.x);
        const lat = parseCoordinate(property.latitude ?? property.lat ?? property.y);

        // if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;


        if (!isValidLngLat(lon, lat)) {
            console.warn("centerMapOnParcel: invalid coords", { lat, lon });
            return;
        }

        //  Guard here too
        // if (
        //     !Number.isFinite(lat) || !Number.isFinite(lon) ||
        //     lat < -90 || lat > 90 ||
        //     lon < -180 || lon > 180
        // ) {
        //     console.warn("centerMapOnParcel: invalid coords", { lat, lon });
        //     return;
        // }



        // Calculate offset for left panel
        const screenWidth = window.innerWidth;
        const leftPanelWidth = screenWidth * (listWidth / 100);
        const offset = -(leftPanelWidth / 2);


        mapRef.current.flyTo({
            center: [lon, lat],
            zoom: 16,
            speed: 1.2,
            offset: [offset, 0]
        });
    };

    const handleRowClick = (property) => {
        if (!mapRef.current) return;

        // 1. PARSE AND VALIDATE COORDINATES FIRST (ONCE)
        const lon = parseCoordinate(property.longitude ?? property.lon ?? property.lng ?? property.x);
        const lat = parseCoordinate(property.latitude ?? property.lat ?? property.y);
        const popupRequestId = ++clickPopupRequestRef.current;

        // If coordinates are invalid, log it and break out early to avoid map crashes
        if (!isValidLngLat(lon, lat)) {
            console.error("Invalid coordinates extracted for property:", property, { lat, lon });
            return;
        }

        const parcelId =
            property.parcel ||
            property.parcelid ||
            property.geo_id ||
            property.id;

        // setSelectedParcelId(parcelId);

        // Get adjacent/touching parcel from property (already available from API)
        const touchingId = property?.touching_parcel || property?.adjacent_parcel || null;

        // Use composite key in adjacent mode so rows with same parcel but different adjacent partner are unique
        const compositeKey = adjacentMode ? `${parcelId}-${touchingId || ''}` : parcelId;
        setSelectedParcelId(compositeKey);
        console.log('Row click compositeKey:', compositeKey, 'adjacentMode:', adjacentMode);

        // const lon = parseFloat(property.longitude ?? property.lon ?? property.lng ?? property.x);
        // const lat = parseFloat(property.latitude ?? property.lat ?? property.y);

        // if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;

        // centerMapOnParcel(property);
        // 2. PASS PARSED LONGITUDE & LATITUDE DIRECTLY INTO COGNIZANT FLYTO INSTEAD OF STRIP CALLING
        const screenWidth = window.innerWidth;
        const leftPanelWidth = screenWidth * (listWidth / 100);
        const offset = -(leftPanelWidth / 2);

        const map = mapRef.current;
        const popupLngLat = [lon, lat];

        map.flyTo({
            // center: [lon, lat],
            center: [lon, lat],
            zoom: 16,
            speed: 1.2,
            offset: [offset, 0] // Panel offset restored to safely center features beside your list UI
        });

        // mapRef.current.once("moveend", () => {
        //     setIsLoadingParcel(false);
        // });


        //  DO NOT TOUCH hover popup
        // Only remove previous CLICK popup
        clickPopupRef.current?.remove();
        clickPopupRef.current = null;

        // Compute Adjacent Parcel ID (parcel + 1) purely for display

        // let adjacentParcelIdDisplay = null;
        // try {
        //     const pidStr = parcelId != null ? String(parcelId) : null;
        //     const match = pidStr ? pidStr.match(/\d+/) : null;
        //     if (match) {
        //         const orig = match[0];
        //         const next = String(Number(orig) + 1);
        //         const padded = next.padStart(orig.length, '0');
        //         adjacentParcelIdDisplay = pidStr.replace(orig, padded);
        //     }
        // } catch (e) { }

        // Only include adjacent parcel info in popup when in adjacentMode
        const adjacentParcelInfoHTML = adjacentMode
            && touchingId
            ? `<div><strong>Adjacent Parcel ID:</strong> ${touchingId}</div>`
            // : `<div><strong>Adjacent Parcel ID:</strong> ${adjacentParcelIdDisplay || 'N/A'}</div>`
            : '';

        // console.log(' Popup debug - adjacentMode:', adjacentMode, 'touchingId:', touchingId, 'adjacentParcelIdDisplay:', adjacentParcelIdDisplay);
        console.log(' Popup debug - adjacentParcelInfoHTML:', adjacentParcelInfoHTML);
        console.log(' Popup debug - coordinates:', [lon, lat]);

        // Ensure map is ready before creating popup
        const createPopup = () => {
            if (popupRequestId !== clickPopupRequestRef.current) return;

            const isFavorite = isFavoriteParcelId(parcelId);
            const renderFavoriteButton = (button, favorite) => {
                if (!button) return;
                button.dataset.favorite = favorite ? "true" : "false";
                //  button.innerHTML = favorite ? "★" : "☆";
                button.innerHTML = `<img src="${favorite ? "/assets/favorite-G.svg" : "/assets/not_favorite.svg"}" alt="${favorite ? "Favorite" : "Not Favorite"}" style="width:18px;height:18px;display:block;" />`;
                button.title = favorite ? "Remove favorite" : "Add favorite";
                //   button.style.color = favorite ? "#f4b400" : "#8c8c8c";
            };

            console.log(' Creating popup immediately...');
            clickPopupRef.current = new mapboxgl.Popup({
                closeOnClick: false,
                closeButton: true,
                maxWidth: "340px",
                className: adjacentMode
                    ? "parcel-popup fixed-popup"
                    : "parcel-popup",
                offset: 16,
                focusAfterOpen: true
            })
                .setLngLat(popupLngLat)
                .setHTML(`
                    <div style="
                        background:#e1faee;
                        padding:18px 14px;
                        border-radius:10px;
                        
                        box-shadow:0 6px 18px rgba(0,0,0,0.18);
                        font-family:Inter, sans-serif;
                        font-size:13px;
                        width:310px;
                    ">
            
                        <!-- Header -->
                        <div style="
                            font-size:14px;
                            font-weight:600;
                            color:#1c8f7d;
                            margin-bottom:10px;
                            border-bottom:1px solid #e6e6e6;
                            padding-bottom:6px;
                            display:flex;
                            align-items:center;
                            justify-content:space-between;
                            gap:8px;
                        ">
                            <span style="min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">
                            <span style="text-transform: capitalize;">${property.county || "N/A"}</span> |
                             <span style="text-transform: capitalize;">${property.municipality || "N/A"}</span> |
                            <span>${property.parcel || property.parcelid || "N/A"}</span>
                            </span>
                            <button
                                type="button"
                                class="parcel-popup-favorite-btn"
                                data-favorite="${isFavorite ? "true" : "false"}"
                                style="
                                    border:none;
                                    background:transparent;
                                    cursor:pointer;
                                    line-height:1;
                                    padding:0 1px;
                                    flex-shrink:0;
                                "
                                title="${isFavorite ? "Remove favorite" : "Add favorite"}"
                            ><img src="${isFavorite ? "/assets/favorite-G.svg" : "/assets/not_favorite.svg"}" alt="${isFavorite ? "Favorite" : "Not Favorite"}" style="width:18px;height:18px;display:block;" /></button>
                           
                        </div> 
            
                        <!-- Content -->
                        <div style="line-height:1.7; color:#333;">
                          
                           
                         ${!adjacentMode ? `
                          <div><strong>Address:</strong> ${property.addrline || property.addrline1 || "N/A"}</div>
                         <div><strong>Flood Zone:</strong> ${property.parcelcustomfemaflood || "N/A"}</div>
                         ` : ''}
                            <div><strong>Zoning:</strong> ${property.zonedcodelocal || "N/A"}</div>
                            ${adjacentParcelInfoHTML}
                            <div>
                                <strong>Parcel Size:</strong>
                                ${property.parcelsize
                        ? `${parseFloat(property.parcelsize).toFixed(2)} acres`
                        : "N/A"}
                            </div>
                            <div><strong>Max Units:</strong> ${property.totalunits || "N/A"}</div>
                            <div><strong>Price:</strong> ${property.zestimate ? `$${parseFloat(property.zestimate).toLocaleString('en-US')}` : "N/A"}</div>
                            <div><strong>20%Developed value:</strong> ${property.totallandvalue ? `$${parseFloat(property.totallandvalue).toLocaleString('en-US')}` : "N/A"}</div>

                            <div>
  <strong>Spread:</strong> ${property.spread ? property.spread < 0
                        ? `-$${Math.abs(property.spread).toLocaleString("en-US")}`
                        : `$${property.spread.toLocaleString("en-US")}`
                        : "N/A"}
  
     ${property.spreadpercentage
                        ? `(${property.spreadpercentage}%)`
                        : "N/A"}
</div>
                            
                        </div>
            
                    </div>
                `)
                .addTo(map);

            const favoriteButton = clickPopupRef.current
                ?.getElement()
                ?.querySelector(".parcel-popup-favorite-btn");

            if (favoriteButton) {
                favoriteButton.addEventListener("click", async (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    if (!parcelId || favoriteButton.disabled) return;

                    const currentlyFavorite = favoriteButton.dataset.favorite === "true";
                    favoriteButton.disabled = true;
                    favoriteButton.style.cursor = "wait";

                    try {
                        if (currentlyFavorite) {
                            await removeFavoriteParcelIds([parcelId]);
                            setFavoriteParcelIds((prev) => prev.filter((id) => String(id) !== String(parcelId)));
                            setFavoriteParcels((prev) => prev.filter((item) => String(getParcelIdentifier(item)) !== String(parcelId)));
                            renderFavoriteButton(favoriteButton, false);
                        } else {
                            await addFavoriteParcelIds([parcelId]);
                            setFavoriteParcelIds((prev) => Array.from(new Set([...prev, parcelId])));
                            setFavoriteParcels((prev) => {
                                const exists = prev.some((item) => String(getParcelIdentifier(item)) === String(parcelId));
                                return exists ? prev : [...prev, property];
                            });
                            renderFavoriteButton(favoriteButton, true);
                        }
                    } catch (error) {
                        console.error("Popup favorite toggle failed", error);
                    } finally {
                        favoriteButton.disabled = false;
                        favoriteButton.style.cursor = "pointer";
                    }
                });
            }

            console.log(' Popup created and added to map');
        };

        // Check if map is ready, if not wait for it
        const createPopupWhenReady = () => {
            if (map.isMoving()) {
                map.once("moveend", createPopup);
            } else {
                createPopup();
            }
        };

        if (map.isStyleLoaded()) {
            createPopupWhenReady();
        } else {
            console.log(' Map style not loaded, waiting...');
            map.once('styledata', () => {
                console.log(' Map style loaded, creating popup');
                createPopupWhenReady();
            });
        }

        // Highlight the selected parcel and its adjacent parcel
        try {
            const ids = [];
            if (parcelId) {
                const sId = String(parcelId);
                ids.push(sId);
                // Prefer adjacent id from API when available

                // ONLY add adjacent parcel if adjacent mode is ON
                if (adjacentMode) {
                    const touching = property?.touching_parcel || property?.adjacent_parcel;
                    if (touching) {
                        ids.push(String(touching));
                    }
                    // else {
                    //     // Fallback: compute +1 id if numeric segment exists
                    //     const numeric = sId.match(/\d+/);
                    //     if (numeric) {
                    //         const origNum = numeric[0];
                    //         const next = String(Number(origNum) + 1);
                    //         const nextPadded = next.padStart(origNum.length, '0');
                    //         // Padded and unpadded variants
                    //         const nextIdPadded = sId.replace(origNum, nextPadded);
                    //         const nextIdUnpadded = sId.replace(origNum, next);
                    //         ids.push(nextIdPadded);
                    //         if (nextIdUnpadded !== nextIdPadded) ids.push(nextIdUnpadded);
                    //     }
                    // }
                }
            }

            // if (mapRef.current.getLayer('parcel-selected-fill')) {
            //     mapRef.current.setFilter('parcel-selected-fill', [
            //         'any',
            //         ['in', ['get', 'parcel'], ['literal', ids]],
            //         ['in', ['get', 'parcelid'], ['literal', ids]]
            //     ]);
            //     mapRef.current.setLayoutProperty('parcel-selected-fill', 'visibility', 'visible');
            // }
            if (mapRef.current.getLayer('parcel-selected-outline')) {
                mapRef.current.setFilter('parcel-selected-outline', [
                    'any',
                    ['in', ['get', 'parcel'], ['literal', ids]],
                    ['in', ['get', 'parcelid'], ['literal', ids]]
                ]);
                mapRef.current.setLayoutProperty('parcel-selected-outline', 'visibility', 'visible');
            } else {
                // Create parcel-selected-outline layer if it doesn't exist
                mapRef.current.addLayer({
                    id: "parcel-selected-outline",
                    type: "line",
                    source: "parcels",
                    "source-layer": "fema",
                    layout: { visibility: "visible" },
                    paint: {
                        "line-color": "#fd0224",
                        "line-width": 2.5,
                        "line-opacity": 0.95
                    },
                    filter: [
                        'any',
                        ['in', ['get', 'parcel'], ['literal', ids]],
                        ['in', ['get', 'parcelid'], ['literal', ids]]
                    ]
                });
            }
        } catch (e) { }
    };

    // Check if any filters (other than county) are active - determines data source display
    // NOTE: Sorting is NOT included here because it doesn't filter data, just orders it
    // Tile data can still be shown with sorting applied

    const hasAnyFilterActive =
        priceRangeDragComplete !== 0 ||
        zestimateRangeDragComplete !== 0 ||
        sizeRangeDragComplete !== 0 ||
        spreadRangeDragComplete !== 0 ||
        (zoneCodeFilter && zoneCodeFilter.length > 0) ||
        (Array.isArray(zoningTypFilter) ? zoningTypFilter.length > 0 : zoningTypFilter && zoningTypFilter !== "All") ||
        (municipalityFilter && municipalityFilter !== "All") ||
        (vacantFilter && vacantFilter !== "All") ||
        (listedSaleFilter && listedSaleFilter !== "All") ||
        (floodRiskFilter && floodRiskFilter !== "All");

    const useMapTiles = currentZoom >= 13.1 && !hasAnyFilterActive && !isParcelSearchActive && !adjacentMode;
    const defaultList = useMapTiles ? filteredParcels : filteredApiParcels;
    const favoriteSourceRows = filteredApiParcels.length > 0 ? filteredApiParcels : filteredParcels;

    const favoriteParcelIdsLowerSet = useMemo(
        () => new Set(favoriteParcelIdsLower),
        [favoriteParcelIdsLower]
    );

    const derivedFavoriteParcels = deriveFavoriteParcelsFromRows(favoriteParcelIds, favoriteSourceRows);
    const displayedParcels = showFavoritesOnly
        ? (favoriteParcels.length > 0
            ? favoriteParcels
            : derivedFavoriteParcels)
        : defaultList;

    const builderAttributeKeys = {
        price: ["totallandvalue", "price", "zestimate", "landvalue", "totalvalue", "total_land_value"],
        zestimate: ["zestimate", "price", "totallandvalue"],
        size: ["parcelsize", "parcel_size", "landsize", "land_size"],
        spread: ["spreadpercentage", "spreadPercent", "spread_percentage", "spread_percent"],
    };

    const builderFeatureNumber = (feature, keys) => {
        if (!feature) return null;
        const obj = feature.properties || feature;
        for (const key of keys) {
            const raw = obj[key];
            if (raw === undefined || raw === null || raw === "") continue;
            const num = Number(String(raw).replace(/[^0-9.-]/g, ""));
            if (Number.isFinite(num)) return num;
        }
        return null;
    };

    const builderFeatureText = (feature, keys) => {
        if (!feature) return "";
        const obj = feature.properties || feature;
        for (const key of keys) {
            const raw = obj[key];
            if (raw !== undefined && raw !== null && String(raw).trim() !== "") {
                return String(raw).trim();
            }
        }
        return "";
    };

    const builderSliderBounds = useMemo(() => {
        const bounds = (values) => ({
            min: values.length > 0 ? Math.min(...values) : undefined,
            max: values.length > 0 ? Math.max(...values) : undefined,
        });

        const builderValues = {
            price: [],
            zestimate: [],
            size: [],
            spread: [],
        };

        builderLayerFeatures.forEach((feature) => {
            const price = builderFeatureNumber(feature, builderAttributeKeys.price);
            if (price !== null) builderValues.price.push(price);

            const zestimate = builderFeatureNumber(feature, builderAttributeKeys.zestimate);
            if (zestimate !== null) builderValues.zestimate.push(zestimate);

            const size = builderFeatureNumber(feature, builderAttributeKeys.size);
            if (size !== null) builderValues.size.push(size);

            const spread = builderFeatureNumber(feature, builderAttributeKeys.spread);
            if (spread !== null) builderValues.spread.push(spread);
        });

        return {
            price: bounds(builderValues.price),
            zestimate: bounds(builderValues.zestimate),
            size: bounds(builderValues.size),
            spread: bounds(builderValues.spread),
        };
    }, [builderLayerFeatures]);

    const activeSliderMinPrice = builderLayerActive && builderSliderBounds.price.min !== undefined ? builderSliderBounds.price.min : sliderMinPrice;
    const activeSliderMaxPrice = builderLayerActive && builderSliderBounds.price.max !== undefined ? builderSliderBounds.price.max : sliderMaxPrice;
    const activeSliderMinZestimate = builderLayerActive && builderSliderBounds.zestimate.min !== undefined ? builderSliderBounds.zestimate.min : sliderMinZestimate;
    const activeSliderMaxZestimate = builderLayerActive && builderSliderBounds.zestimate.max !== undefined ? builderSliderBounds.zestimate.max : sliderMaxZestimate;
    const activeSliderMinSize = builderLayerActive && builderSliderBounds.size.min !== undefined ? builderSliderBounds.size.min : sliderMinSize;
    const activeSliderMaxSize = builderLayerActive && builderSliderBounds.size.max !== undefined ? builderSliderBounds.size.max : sliderMaxSize;
    const activeSliderMinSpread = builderLayerActive && builderSliderBounds.spread.min !== undefined ? builderSliderBounds.spread.min : sliderMinSpread;
    const activeSliderMaxSpread = builderLayerActive && builderSliderBounds.spread.max !== undefined ? builderSliderBounds.spread.max : sliderMaxSpread;

    useEffect(() => {
        if (!builderLayerActive) return;
        if (!Array.isArray(builderLayerFeatures) || builderLayerFeatures.length === 0) return;

        if (priceRangeDragComplete === 0 && activeSliderMinPrice !== undefined && activeSliderMaxPrice !== undefined) {
            setPriceRange([activeSliderMinPrice, activeSliderMaxPrice]);
        }
        if (zestimateRangeDragComplete === 0 && activeSliderMinZestimate !== undefined && activeSliderMaxZestimate !== undefined) {
            setZestimateRange([activeSliderMinZestimate, activeSliderMaxZestimate]);
        }
        if (sizeRangeDragComplete === 0 && activeSliderMinSize !== undefined && activeSliderMaxSize !== undefined) {
            setSizeRange([activeSliderMinSize, activeSliderMaxSize]);
        }
        if (spreadRangeDragComplete === 0 && activeSliderMinSpread !== undefined && activeSliderMaxSpread !== undefined) {
            setSpreadRange([activeSliderMinSpread, activeSliderMaxSpread]);
        }
    }, [
        builderLayerActive,
        builderLayerFeatures,
        activeSliderMinPrice,
        activeSliderMaxPrice,
        activeSliderMinZestimate,
        activeSliderMaxZestimate,
        activeSliderMinSize,
        activeSliderMaxSize,
        activeSliderMinSpread,
        activeSliderMaxSpread,
        priceRangeDragComplete,
        zestimateRangeDragComplete,
        sizeRangeDragComplete,
        spreadRangeDragComplete,
    ]);

    const builderFilterOptions = useMemo(() => {
        const zoneSet = new Set();
        const typeSet = new Set();
        const muniSet = new Set();
        const floodSet = new Set();

        builderLayerFeatures.forEach((feature) => {
            const props = feature.properties || feature || {};
            const add = (keys, set) => {
                keys.forEach((key) => {
                    const raw = props[key];
                    if (raw !== undefined && raw !== null) {
                        const value = String(raw).trim();
                        if (value !== "") {
                            set.add(value);
                        }
                    }
                });
            };

            add(["zonedcodelocal", "zone", "zoning", "zone_code", "zoning_code"], zoneSet);
            add(["zoningtyp", "zoning_type", "zoningType"], typeSet);
            add(["municipality", "city", "town", "cityname"], muniSet);
            add(["parcelcustomfemaflood", "FloodFemaRisk", "floodrisk", "flood_risk"], floodSet);
        });

        return {
            zoneCodes: Array.from(zoneSet).filter(Boolean).sort(),
            zoningTypOptions: Array.from(typeSet).filter(Boolean).sort(),
            municipalityOptions: ["All", ...Array.from(muniSet).filter(Boolean).sort()],
            floodOptions: ["All", ...Array.from(floodSet).filter(Boolean).sort()],
        };
    }, [builderLayerFeatures]);

    const activeZoneCodes = builderLayerActive && builderFilterOptions.zoneCodes.length > 0 ? builderFilterOptions.zoneCodes : zoneCodes;
    const activeZoningTypOptions = builderLayerActive && builderFilterOptions.zoningTypOptions.length > 0 ? builderFilterOptions.zoningTypOptions : zoningTypOptions;
    const activeMunicipalityOptions = builderLayerActive && builderFilterOptions.municipalityOptions.length > 1 ? builderFilterOptions.municipalityOptions : municipalityOptions;
    const activeFloodOptions = builderLayerActive && builderFilterOptions.floodOptions.length > 1 ? builderFilterOptions.floodOptions : floodOptions;

    const builderFilteredParcels = useMemo(() => {
        if (!builderLayerActive || !Array.isArray(builderLayerFeatures)) return builderLayerFeatures;

        const priceActive = priceRangeDragComplete !== 0 && isRangeFilterActive(priceRange, activeSliderMinPrice, activeSliderMaxPrice, 0.01);
        const zestimateActive = zestimateRangeDragComplete !== 0 && isRangeFilterActive(zestimateRange, activeSliderMinZestimate, activeSliderMaxZestimate, 0.1);
        const sizeActive = sizeRangeDragComplete !== 0 && isRangeFilterActive(sizeRange, activeSliderMinSize, activeSliderMaxSize, 0.01);
        const spreadActive = spreadRangeDragComplete !== 0 && isRangeFilterActive(spreadRange, activeSliderMinSpread, activeSliderMaxSpread, 0.1);

        const zoneFilterActive = Array.isArray(zoneCodeFilter)
            ? zoneCodeFilter.length > 0
            : zoneCodeFilter && zoneCodeFilter !== "All";
        const vacantFilterActive = vacantFilter && vacantFilter !== "All";
        const listedSaleFilterActive = listedSaleFilter && listedSaleFilter !== "All";
        const floodRiskFilterActive = floodRiskFilter && floodRiskFilter !== "All";
        const municipalityFilterActive = municipalityFilter && municipalityFilter !== "All";
        const zoningTypFilterActive = Array.isArray(zoningTypFilter)
            ? zoningTypFilter.length > 0
            : zoningTypFilter && zoningTypFilter !== "All";

        return builderLayerFeatures.filter((feature) => {
            const price = builderFeatureNumber(feature, builderAttributeKeys.price);
            if (priceActive && (price === null || price < priceRange[0] || price > priceRange[1])) return false;

            const zestimate = builderFeatureNumber(feature, builderAttributeKeys.zestimate);
            if (zestimateActive && (zestimate === null || zestimate < zestimateRange[0] || zestimate > zestimateRange[1])) return false;

            const size = builderFeatureNumber(feature, builderAttributeKeys.size);
            if (sizeActive && (size === null || size < sizeRange[0] || size > sizeRange[1])) return false;

            const spread = builderFeatureNumber(feature, builderAttributeKeys.spread);
            if (spreadActive && (spread === null || spread < spreadRange[0] || spread > spreadRange[1])) return false;

            if (vacantFilterActive) {
                const vacancyValue = builderFeatureText(feature, ["is_vaccant", "vacant", "vacant_status", "vacant_flag"]).toLowerCase();
                if (!(vacancyValue === "1" || vacancyValue === "yes" || vacancyValue === "true")) return false;
            }

            if (listedSaleFilterActive) {
                const listed = builderFeatureText(feature, ["listedsale", "listed_sale", "sale_status", "status"]).toLowerCase();
                if (listedSaleFilter.toLowerCase() === "yes") {
                    if (!["yes", "true", "listed", "1"].includes(listed)) return false;
                } else if (listedSaleFilter.toLowerCase() === "no") {
                    if (["yes", "true", "listed", "1"].includes(listed)) return false;
                }
            }

            if (floodRiskFilterActive) {
                const flood = builderFeatureText(feature, ["parcelcustomfemaflood", "FloodFemaRisk", "floodrisk", "flood_risk"]);
                if (!flood.includes(floodRiskFilter)) return false;
            }

            if (municipalityFilterActive) {
                const municipality = builderFeatureText(feature, ["municipality", "city", "town", "cityname"]).toLowerCase();
                if (municipality !== municipalityFilter.toLowerCase()) return false;
            }

            if (zoneFilterActive) {
                const zone = builderFeatureText(feature, ["zonedcodelocal", "zone", "zoning", "zone_code", "zoning_code"]).toLowerCase();
                const selected = Array.isArray(zoneCodeFilter) ? zoneCodeFilter : [zoneCodeFilter];
                if (!selected.some((code) => code && code.toString().toLowerCase() === zone)) return false;
            }

            if (zoningTypFilterActive) {
                const zoningType = builderFeatureText(feature, ["zoningtyp", "zoning_type", "zoningType"]).toLowerCase();
                const selectedTypes = Array.isArray(zoningTypFilter) ? zoningTypFilter : [zoningTypFilter];
                if (!selectedTypes.some((type) => type && type.toString().toLowerCase() === zoningType)) return false;
            }

            return true;
        });
    }, [
        builderLayerActive,
        builderLayerFeatures,
        priceRange,
        zestimateRange,
        sizeRange,
        spreadRange,
        zoneCodeFilter,
        vacantFilter,
        listedSaleFilter,
        floodRiskFilter,
        municipalityFilter,
        zoningTypFilter,
        activeSliderMinPrice,
        activeSliderMaxPrice,
        activeSliderMinZestimate,
        activeSliderMaxZestimate,
        activeSliderMinSize,
        activeSliderMaxSize,
        activeSliderMinSpread,
        activeSliderMaxSpread,
    ]);

    const builderListSource = builderLayerActive ? (drawnPolygonRef.current ? nearbyParcels : builderFilteredParcels) : null;
    const isBuilderListActive = builderLayerActive;
    const builderDisplayedParcels = builderLayerActive ? (drawnPolygonRef.current ? nearbyParcels : builderFilteredParcels) : displayedParcels;

    const effectiveTotalParcelCount = builderLayerActive
        ? (drawnPolygonRef.current ? nearbyParcels.length : builderFilteredParcels.length)
        : totalParcelCount;

    const builderParcelIds = useMemo(() => {
        if (!builderLayerActive || !Array.isArray(builderFilteredParcels)) return [];
        return builderFilteredParcels
            .map((feature) => {
                const props = feature?.properties || feature || {};
                return props.parcel || props.parcelid || props.geo_id || props.id;
            })
            .filter(Boolean)
            .map(String);
    }, [builderLayerActive, builderFilteredParcels]);

    const applyBuilderLayerFilter = (map, ids) => {
        if (!map) return;
        if (!map.getLayer("builder-parcel-fill") || !map.getLayer("builder-parcel-outline")) return;

        if (!ids || ids.length === 0) {
            const hideAll = ["==", ["get", "parcel"], ""];
            map.setFilter("builder-parcel-fill", hideAll);
            map.setFilter("builder-parcel-outline", hideAll);
            return;
        }

        const builderFilterExpr = [
            "any",
            ["in", ["to-string", ["get", "parcel"]], ["literal", ids]],
            ["in", ["to-string", ["get", "parcelid"]], ["literal", ids]],
            ["in", ["to-string", ["get", "geo_id"]], ["literal", ids]],
            ["in", ["to-string", ["get", "id"]], ["literal", ids]],
        ];

        map.setFilter("builder-parcel-fill", builderFilterExpr);
        map.setFilter("builder-parcel-outline", builderFilterExpr);
    };

    useEffect(() => {
        if (!mapRef.current || !mapLoaded) return;
        const map = mapRef.current;

        if (builderLayerActive) {
            applyBuilderLayerFilter(map, builderParcelIds);
            if (map.getLayer("builder-parcel-fill")) map.setLayoutProperty("builder-parcel-fill", "visibility", "visible");
            if (map.getLayer("builder-parcel-outline")) map.setLayoutProperty("builder-parcel-outline", "visibility", "visible");
        }
    }, [builderLayerActive, builderParcelIds, mapLoaded]);

    useEffect(() => {
        if (showFavoritesOnly && favoriteParcelIds.length > 0 && favoriteParcels.length === 0 && derivedFavoriteParcels.length > 0) {
            setFavoriteParcels(derivedFavoriteParcels);
        }
    }, [showFavoritesOnly, favoriteParcelIds.length, favoriteParcels.length, derivedFavoriteParcels]);

    // Debug logging for favorites
    if (showFavoritesOnly) {
        console.log("🌟 Favorites Debug:", {
            showFavoritesOnly,
            displayedParcelsLength: displayedParcels.length,
            favoriteCount: favoriteParcelIds.length,
            favoriteParcelIdsLowerSetSize: favoriteParcelIdsLowerSet.size,
            firstFewFavs: Array.from(favoriteParcelIdsLowerSet).slice(0, 3),
            firstFewDisplayedParcels: displayedParcels.slice(0, 3).map(p => getParcelIdentifier(p)),
        });
    }

    const handleClearAll2 = () => {
        try {
            drawRef.current?.changeMode?.("simple_select");
        } catch (e) { }

        try {
            drawRef.current?.deleteAll?.();
        } catch (e) {
            console.warn("Failed to clear drawn polygons", e);
        }

        setDrawnCoordinates(null); // This instantly unmounts/hides your sidebar pop-up card
        setDrawnArea(null);
        setDrawnParcelIds([]);
        setNearbyParcels([]);
        setNearbyError(null);
        setNearbyOffset(0);
        setNearbyTotalCount(0);
        setNearbyHasMore(false);
        setIsLoadingMoreNearby(false);
        nearbyOffsetRef.current = 0;
        nearbyHasMoreRef.current = false;
        nearbyLoadingRef.current = false;
        drawnPolygonRef.current = null;

        if (builderLayerActive && mapRef.current) {
            loadBuilderLayerFeatures(mapRef.current);
        }

    };

    const activeListForSelection = builderLayerActive ? builderDisplayedParcels : displayedParcels;
    const isAllSelected = activeListForSelection.length > 0 && activeListForSelection.every((item) => selectedRowIds.has(getRowKey(item)));

    return (
        <>
            {/* Header */}
            <Header
                activeTab={activeTab}
                handleTabChange={setActiveTab}
                showSearch={false}
                showFavorites={false}
                showLinks={true}
                isSubscribed={isSubscribed}
                isMenuOpen={isMenuOpen}
                toggleMenu={toggleMenu}
                handleLogout={handleLogout}
                // County selector props
                showCountySelector={true}
                selectedCounty={selectedCounty}
                // countiesList={countiesList}
                countiesList={['Chatham', 'Wake']}
                onCountyChange={onDropdownChange}

                onParcelSearch={searchParcelApi}
                clearParcelSearch={clearParcelSearch}
                setIsParcelSearchActive={setIsParcelSearchActive}
                resetParcelSearchSignal={resetParcelSearchSignal}
                isAdjacentMode={adjacentMode}
                onAdjacentToggle={(isAdjacent) => {
                    setShowFavoritesOnly(false);

                    if (isAdjacent) {
                        // setShowFavoritesOnly(false);
                        setSelectionMode(false);
                        setActiveTab("parcels");
                    }
                    setAdjacentMode(isAdjacent);
                    reloadParcelTiles(isAdjacent);
                    // Tell Header to reset parcel search UI
                    setResetParcelSearchSignal(prev => prev + 1);

                    // Clear list immediately so UI doesn't show stale results
                    //    setApiParcels([]);
                    //    setFilteredApiParcels([]);
                    // Reapply county filter to map AFTER tiles reload

                    const applyCountyToMap = () => {
                        const county = selectedCountyRef.current;
                        if (!mapRef.current || !county || county === "all") return;
                        try {
                            // ensure visible  set county filter
                            mapRef.current.setLayoutProperty("parcel-points", "visibility", "visible");
                            const filterExpr = ["==", ["downcase", ["get", "county"]], county];
                            mapRef.current.setFilter("parcel-points", filterExpr);
                            if (mapRef.current.getLayer("parcel-outline")) {
                                mapRef.current.setLayoutProperty("parcel-outline", "visibility", "visible");
                                mapRef.current.setFilter("parcel-outline", filterExpr);
                            }
                            // re-query rendered features and populate left panel reliably
                            setTimeout(() => {
                                try {
                                    // const feats = mapRef.current.queryRenderedFeatures({ layers: ["parcel-points"] }) || [];
                                    const feats = mapRef.current.queryRenderedFeatures({ layers: ["parcel-outline"] }) || [];
                                    const uniques = new Map();
                                    feats.forEach(f => {
                                        const p = f.properties || {};
                                        const id = p.parcel || p.parcelid || p.geo_id || p.id;
                                        if (id && !uniques.has(id)) uniques.set(id, p);
                                    });
                                    setFilteredParcels(Array.from(uniques.values()));
                                    console.log(`Applied county filter after tile swap; parcels: ${uniques.size}`);
                                } catch (e) { console.warn('queryRenderedFeatures failed', e); }
                            }, 150);
                        } catch (e) { console.warn('applyCountyToMap failed', e); }
                    };

                    if (mapRef.current) {
                        mapRef.current.once("sourcedata", applyCountyToMap);
                        mapRef.current.once("idle", applyCountyToMap);
                        // fallback
                        setTimeout(applyCountyToMap, 800);
                    } else {
                        applyCountyToMap();
                    }


                    if (selectedCounty && selectedCounty !== "all") {
                        fetchApiParcels(
                            selectedCounty,
                            priceRange[0],
                            priceRange[1],
                            zestimateRange[0],
                            zestimateRange[1],
                            sizeRange[0],
                            sizeRange[1],
                            spreadRange[0],
                            spreadRange[1],
                            zoneCodeFilter && zoneCodeFilter.length > 0 ? zoneCodeFilter : null,
                            vacantFilter !== "All" ? vacantFilter : null,
                            listedSaleFilter !== "All" ? listedSaleFilter : null,
                            floodRiskFilter !== "All" ? floodRiskFilter : null,
                            sortBy,
                            sortOrder,
                            municipalityFilter !== "All" ? municipalityFilter : null,
                            0,
                            null,  // is_best
                            zoningTypFilter && zoningTypFilter.length > 0 ? zoningTypFilter : null
                        );
                    }
                }}
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "100px",
                    zIndex: 2000,
                }}
            />

            {/* Parcel Filters - Always visible */}
            {selectedCounty !== "all" &&
                <div style={{
                    position: "fixed",
                    top: "130px",
                    // top: "100px",
                    left: 0,
                    right: 0,
                    // background: "#fff",
                    background: "#deefea !important",
                    zIndex: 1850,
                    // zIndex: 0,
                }}>
                    <ParcelFilters
                        // County selection
                        selectedCounty={selectedCounty}
                        countiesList={countiesList}
                        onCountyChange={onDropdownChange}

                        // Mode
                        isAdjacentMode={adjacentMode}

                        // Display info
                        currentZoom={currentZoom}
                        apiParcelsCount={apiParcels.length}
                        tileParcelsCount={filteredParcels.length}
                        effectiveTotalParcelCount={effectiveTotalParcelCount}

                        // Filter visibility
                        showPriceFilter={showPriceFilter}
                        setShowPriceFilter={setShowPriceFilter}
                        showSizeFilter={showSizeFilter}
                        setShowSizeFilter={setShowSizeFilter}
                        showFloodFilter={showFloodFilter}
                        setShowFloodFilter={setShowFloodFilter}
                        showZoneFilter={showZoneFilter}
                        setShowZoneFilter={setShowZoneFilter}
                        showZestimateFilter={showZestimateFilter}
                        setShowZestimateFilter={setShowZestimateFilter}
                        showSpreadFilter={showSpreadFilter}
                        setShowSpreadFilter={setShowSpreadFilter}

                        showMunicipalityFilter={showMunicipalityFilter}
                        setShowMunicipalityFilter={setShowMunicipalityFilter}

                        showAdjacentParcelFilter={showAdjacentParcelFilter}
                        setShowAdjacentParcelFilter={setShowAdjacentParcelFilter}
                        adjacentParcelFilter={adjacentParcelFilter}
                        setAdjacentParcelFilter={setAdjacentParcelFilter}

                        // Filter values
                        priceRange={priceRange}
                        setPriceRange={setPriceRange}
                        sizeRange={sizeRange}
                        setSizeRange={setSizeRange}
                        floodRiskFilter={floodRiskFilter}
                        setFloodRiskFilter={setFloodRiskFilter}
                        zoneCodeFilter={zoneCodeFilter}
                        setZoneCodeFilter={setZoneCodeFilter}
                        zestimateRange={zestimateRange}
                        setZestimateRange={setZestimateRange}
                        spreadRange={spreadRange}
                        setSpreadRange={setSpreadRange}
                        listedSaleFilter={listedSaleFilter}
                        setListedSaleFilter={setListedSaleFilter}
                        vacantFilter={vacantFilter}
                        setVacantFilter={setVacantFilter}
                        municipalityFilter={municipalityFilter}
                        setMunicipalityFilter={setMunicipalityFilter}
                        municipalityOptions={activeMunicipalityOptions}

                        // Slider bounds
                        sliderMinPrice={activeSliderMinPrice}
                        sliderMaxPrice={activeSliderMaxPrice}
                        sliderMinSize={activeSliderMinSize}
                        sliderMaxSize={activeSliderMaxSize}
                        sliderMinZestimate={activeSliderMinZestimate}
                        sliderMaxZestimate={activeSliderMaxZestimate}
                        sliderMinSpread={activeSliderMinSpread}
                        sliderMaxSpread={activeSliderMaxSpread}


                        // Filter options
                        floodOptions={activeFloodOptions}
                        zoneCodes={activeZoneCodes}
                        listedSaleOptions={listedSaleOptions}

                        // Zoning Type filter (only in parcel mode)
                        showZoningTypFilter={showZoningTypFilter}
                        setShowZoningTypFilter={setShowZoningTypFilter}
                        zoningTypFilter={zoningTypFilter}
                        setZoningTypFilter={setZoningTypFilter}
                        zoningTypOptions={activeZoningTypOptions}

                        // Helper functions
                        clearAllFilters={clearAllFilters}
                        formatCurrency={formatCurrency}
                        selectedCount={selectedRows.length}
                        selectionMode={selectionMode}
                        totalParcelCount={totalParcelCount}
                        favoriteCount={favoriteTotalCount != null ? favoriteTotalCount : favoriteParcelIds.length}
                        showFavoritesOnly={showFavoritesOnly}
                        setShowFavoritesOnly={(nextValue) => {
                            setSelectedRows([]);
                            setSelectionMode(false);
                            localStorage.removeItem("ncSelectedRows");
                            setShowFavoritesOnly(nextValue);
                        }}
                        onAddSelectedToFavorites={addSelectedRowsToFavorites}
                        onToggleSelectionMode={() => {
                            // When in favorites mode, just toggle selection mode without affecting favorites display
                            setSelectionMode((prev) => !prev);
                        }}
                        onExportToFavourite={exportSelectedRowsToFavourite}
                        // onExportFavorites={exportFavorites} // Removed - using bottom export flow
                        onClearSelection={clearSelectionAndCheckFilters} // Removed - using bottom export flow

                        // Callback for slider drag complete
                        onPriceRangeChangeComplete={handlePriceRangeChangeComplete}
                        onZestimateRangeChangeComplete={handleZestimateRangeChangeComplete}
                        onSizeRangeChangeComplete={handleSizeRangeChangeComplete}
                        onSpreadRangeChangeComplete={handleSpreadRangeChangeComplete}
                        priceRangeDragComplete={priceRangeDragComplete}
                        zestimateRangeDragComplete={zestimateRangeDragComplete}
                        sizeRangeDragComplete={sizeRangeDragComplete}
                        spreadRangeDragComplete={spreadRangeDragComplete}
                        isBuilderMode={builderLayerActive}
                    />
                </div>
            }

            {/* Left panel - API data (zoom < 13.1) or Tile data (zoom >= 13.1) */}
            {selectedCounty !== "all" && (() => {
                // Check if any filters are active - use actual values, not just touched flags
                const hasAnyFilterActive =
                    (vacantFilter && vacantFilter !== "All") ||
                    (zoneCodeFilter && zoneCodeFilter.length > 0) ||
                    (listedSaleFilter && listedSaleFilter !== "All") ||
                    (floodRiskFilter && floodRiskFilter !== "All") ||
                    // municipalityFilterTouched.current ||
                    (priceRange[0] !== sliderMinPrice || priceRange[1] !== sliderMaxPrice) ||
                    (zestimateRange[0] !== sliderMinZestimate || zestimateRange[1] !== sliderMaxZestimate) ||
                    (sizeRange[0] !== sliderMinSize || sizeRange[1] !== sliderMaxSize) ||
                    (spreadRange[0] !== sliderMinSpread || spreadRange[1] !== sliderMaxSpread) ||
                    (Array.isArray(zoningTypFilter) ? zoningTypFilter.length > 0 : zoningTypFilter && zoningTypFilter !== "All");

                const isPolygonSelectionActive = drawnPolygonRef.current && builderLayerActive;
                return (
                    <>
                        <div
                            style={{
                                position: "absolute",
                                top: "190px",
                                // top: "160px",
                                left: 0,
                                // width: "60%",
                                width: `${listWidth}%`,
                                bottom: 0,
                                background: "#fafafa",
                                borderRight: "1px solid #ddd",
                                overflow: "auto",
                                zIndex: 1500,
                            }}
                        >
                            {isLoadingParcel ? (
                                <div style={{ padding: "20px", textAlign: "center", color: "#666" }}>
                                    Loading parcels...
                                </div>
                                // ) : isLoadingApi && currentZoom < 13.1 ? (
                            ) : isLoadingApi ? (
                                <div style={{ padding: "20px", textAlign: "center", color: "#666" }}>
                                    Loading parcels...
                                </div>
                            ) : isPolygonSelectionActive && !showFavoritesOnly && !isParcelSearchActive && !adjacentMode ? (
                                isLoadingNearby ? (
                                    <div style={{ padding: "20px", textAlign: "center", color: "#666" }}>
                                        Loading parcels in drawn area...
                                    </div>
                                ) : nearbyParcels.length === 0 ? (
                                    <div style={{ padding: "20px", textAlign: "center", color: "#666" }}>
                                        No parcels found inside the drawn area.
                                    </div>
                                ) : (
                                    <>
                                        <div style={{
                                            padding: "10px 15px",
                                            background: "#f5f5f5",
                                            borderBottom: "1px solid #e0e0e0",
                                            fontSize: "14px",
                                            fontWeight: "500",
                                            color: "#333",
                                        }}>
                                            {`${nearbyParcels.length.toLocaleString()} Parcels inside drawn area`}
                                        </div>
                                        <PropertyList
                                            parcels={nearbyParcels}
                                            onRowClick={handleRowClick}
                                            selectedParcelId={selectedParcelId}
                                            setSelectedParcelId={setSelectedParcelId}
                                            sortKey={null}
                                            setSortKey={null}
                                            sortDir={null}
                                            setSortDir={null}
                                            currentZoom={currentZoom}
                                            rowOffset={0}
                                            isAdjacentMode={adjacentMode}
                                            selectionMode={selectionMode}
                                            selectedRowIds={selectedRowIds}
                                            toggleRowSelection={toggleParcelSelection}
                                            toggleSelectAll={() => toggleSelectAll(nearbyParcels)}
                                            isAllSelected={isAllSelected}
                                            showDownloadButton={showDownloadButton}
                                            onExportCsv={exportOnlySelectedRows}
                                            onExportPdf={exportSelectedRowsToPdf}
                                            onSaveToFavorites={addSelectedRowsToFavorites}
                                            onRemoveFromFavorites={removeSelectedRowsFromFavorites}
                                            removeAllFavorites={removeAllFavorites}
                                            showFavoritesOnly={showFavoritesOnly}
                                            favoriteParcelIds={favoriteParcelIds}
                                            onToggleFavorite={toggleFavoriteParcel}
                                        />
                                    </>
                                )
                            ) : isBuilderListActive && !showFavoritesOnly && !isParcelSearchActive && !adjacentMode ? (
                                isLoadingBuilderLayerFeatures ? (
                                    <div style={{ padding: "20px", textAlign: "center", color: "#666" }}>
                                        Loading builder projects...
                                    </div>
                                ) : builderDisplayedParcels.length === 0 ? (
                                    <div style={{ padding: "20px", textAlign: "center", color: "#666" }}>
                                        No Mega Parcles found for the current map tiles.
                                    </div>
                                ) : (
                                    <>
                                        <div style={{
                                            padding: "10px 15px",
                                            background: "#f5f5f5",
                                            borderBottom: "1px solid #e0e0e0",
                                            fontSize: "14px",
                                            fontWeight: "500",
                                            color: "#333",
                                        }}>
                                            {`${builderDisplayedParcels.length.toLocaleString()} Mega Parcles (Builder Layer)`}
                                        </div>
                                        <PropertyList
                                            parcels={builderDisplayedParcels}
                                            onRowClick={handleRowClick}
                                            selectedParcelId={selectedParcelId}
                                            setSelectedParcelId={setSelectedParcelId}
                                            sortKey={null}
                                            setSortKey={null}
                                            sortDir={null}
                                            setSortDir={null}
                                            currentZoom={currentZoom}
                                            rowOffset={0}
                                            isAdjacentMode={adjacentMode}
                                            selectionMode={selectionMode}
                                            selectedRowIds={selectedRowIds}
                                            toggleRowSelection={toggleParcelSelection}
                                            toggleSelectAll={() => toggleSelectAll(builderDisplayedParcels)}
                                            isAllSelected={isAllSelected}
                                            showDownloadButton={showDownloadButton}
                                            onExportCsv={exportOnlySelectedRows}
                                            onExportPdf={exportSelectedRowsToPdf}
                                            onSaveToFavorites={addSelectedRowsToFavorites}
                                            onRemoveFromFavorites={removeSelectedRowsFromFavorites}
                                            removeAllFavorites={removeAllFavorites}
                                            showFavoritesOnly={showFavoritesOnly}
                                            favoriteParcelIds={favoriteParcelIds}
                                            onToggleFavorite={toggleFavoriteParcel}
                                        />
                                    </>
                                )
                            ) : currentZoom >= 13.1 && !showFavoritesOnly && !hasAnyFilterActive && !isParcelSearchActive && !adjacentMode ? (
                                // Show tile server data ONLY when zoomed in AND no filters are active, but not when showing favorites

                                filteredParcels.length === 0 ? (
                                    <div style={{ padding: "20px", textAlign: "center", color: "#666" }}>
                                        No parcels visible at this zoom level
                                    </div>
                                ) : (
                                    <>
                                        {/* Property Count Display */}
                                        <div style={{
                                            padding: "10px 15px",
                                            background: "#f5f5f5",
                                            borderBottom: "1px solid #e0e0e0",
                                            fontSize: "14px",
                                            fontWeight: "500",
                                            color: "#333",
                                            display: "none"
                                        }}>
                                            {(() => {
                                                const useTiles = currentZoom >= 13.1 && !showFavoritesOnly && !hasAnyFilterActive && !adjacentMode;
                                                const displayCount = useTiles ? filteredParcels.length : totalParcelCount;
                                                const source = useTiles ? "(Map Tiles)" : "(Total)";
                                                return `${displayCount.toLocaleString()} Properties ${source}`;
                                            })()}
                                        </div>
                                        {console.log(`Passing ${filteredParcels.length} parcels to PropertyList`)}
                                        <PropertyList
                                            parcels={displayedParcels}
                                            onRowClick={handleRowClick}
                                            selectedParcelId={selectedParcelId}
                                            setSelectedParcelId={setSelectedParcelId}
                                            sortKey={null}
                                            setSortKey={null}
                                            sortDir={null}
                                            setSortDir={null}
                                            currentZoom={currentZoom}
                                            rowOffset={0}
                                            isAdjacentMode={adjacentMode}
                                            selectionMode={selectionMode}
                                            selectedRowIds={selectedRowIds}
                                            toggleRowSelection={toggleParcelSelection}
                                            toggleSelectAll={() => toggleSelectAll(displayedParcels)}
                                            isAllSelected={isAllSelected}
                                            showDownloadButton={showDownloadButton}
                                            onExportCsv={exportOnlySelectedRows}
                                            onExportPdf={exportSelectedRowsToPdf}
                                            onSaveToFavorites={addSelectedRowsToFavorites}
                                            onRemoveFromFavorites={removeSelectedRowsFromFavorites}
                                            removeAllFavorites={removeAllFavorites}
                                            showFavoritesOnly={showFavoritesOnly}
                                            favoriteParcelIds={favoriteParcelIds}
                                            onToggleFavorite={toggleFavoriteParcel}
                                        />
                                    </>
                                )
                                // ) : filteredApiParcels.length === 0 && !isLoadingApi ? (
                                //     // Show "no parcels" only if not loading AND data is empty
                                //     <div style={{ padding: "20px", textAlign: "center", color: "#666" }}>
                                //         No parcels found
                                //     </div>
                            ) : (
                                // Show API data when zoomed out (use filtered data)--before

                                // Show API data when zoomed out OR when any filter is active (regardless of zoom)
                                (showFavoritesOnly ? displayedParcels.length === 0 : filteredApiParcels.length === 0) ? (
                                    <div style={{ padding: "20px", textAlign: "center", color: "#666" }}>
                                        {showFavoritesOnly
                                            ? (favoriteParcelIds.length === 0 ? "No favorites saved" : "No favorites match the current filters")
                                            : (apiParcels.length === 0 ? "No parcels found" : "No parcels match the current filters")}
                                    </div>
                                ) : (
                                    <>
                                        {/* Property Count Display - Filtered API Data */}
                                        <div style={{
                                            padding: "10px 15px",
                                            background: "#f5f5f5",
                                            borderBottom: "1px solid #e0e0e0",
                                            fontSize: "14px",
                                            fontWeight: "500",
                                            color: "#333",
                                            display: "none"
                                        }}>
                                            {totalParcelCount.toLocaleString()} Properties (Total)
                                        </div>
                                        <PropertyList
                                            parcels={displayedParcels}
                                            onRowClick={handleRowClick}
                                            selectedParcelId={selectedParcelId}
                                            setSelectedParcelId={setSelectedParcelId}
                                            sortKey={sortBy}
                                            setSortKey={setSortBy}
                                            sortDir={sortOrder}
                                            setSortDir={setSortOrder}
                                            currentZoom={currentZoom}
                                            loadMore={showFavoritesOnly ? loadMoreFavorites : loadMoreParcels}
                                            hasMore={showFavoritesOnly ? favoriteHasMore : hasMoreData}
                                            isLoadingMore={showFavoritesOnly ? isLoadingFavorites : isLoadingMore}
                                            rowOffset={0}
                                            isAdjacentMode={adjacentMode}
                                            selectionMode={selectionMode}
                                            selectedRowIds={selectedRowIds}
                                            toggleRowSelection={toggleParcelSelection}
                                            toggleSelectAll={() => toggleSelectAll(displayedParcels)}
                                            isAllSelected={isAllSelected}
                                            showDownloadButton={showDownloadButton}
                                            onExportCsv={exportOnlySelectedRows}
                                            onExportPdf={exportSelectedRowsToPdf}
                                            onSaveToFavorites={addSelectedRowsToFavorites}
                                            onRemoveFromFavorites={removeSelectedRowsFromFavorites}
                                            removeAllFavorites={removeAllFavorites}
                                            showFavoritesOnly={showFavoritesOnly}
                                            favoriteParcelIds={favoriteParcelIds}
                                            onToggleFavorite={toggleFavoriteParcel}
                                        />
                                    </>
                                )
                            )}
                        </div>
                        <div
                            style={{
                                position: "absolute",
                                top: "190px",
                                // top: "160px",
                                left: `${listWidth}%`,
                                width: "6px",
                                bottom: 0,
                                cursor: "col-resize",
                                background: "#ddd",
                                zIndex: 1600,
                            }}
                            onMouseDown={(e) => {
                                const startX = e.clientX;

                                const onMouseMove = (moveEvent) => {
                                    const delta = moveEvent.clientX - startX;
                                    const newWidth =
                                        ((listWidth / 100) * window.innerWidth + delta) /
                                        window.innerWidth *
                                        100;

                                    if (newWidth > 20 && newWidth < 80) {
                                        setListWidth(newWidth);
                                    }
                                };

                                const onMouseUp = () => {
                                    window.removeEventListener("mousemove", onMouseMove);
                                    window.removeEventListener("mouseup", onMouseUp);
                                };

                                window.addEventListener("mousemove", onMouseMove);
                                window.addEventListener("mouseup", onMouseUp);
                            }}
                        />
                    </>
                );
            })()}
            {false && drawnCoordinates && (
                <div style={{
                    position: 'absolute',
                    top: '120px',
                    right: '10px',
                    zIndex: 1800,
                    background: '#fff',
                    borderRadius: '10px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
                    width: '320px',
                    maxHeight: '520px',
                    display: 'flex',
                    flexDirection: 'column',
                    fontFamily: 'Inter, sans-serif',
                    marginTop: '125px',
                    overflow: 'hidden',
                }}>
                    {/* Header */}
                    {/* <div style={{
            padding: '12px 16px',
            background: '#e1faee',
            borderBottom: '1px solid #c8f0df',
            flexShrink: 0,
        }}>
            <div style={{ fontWeight: 700, fontSize: '14px', color: '#1c8f7d' }}>
                Drawn Area
            </div>
            {drawnArea && (
                <div style={{ fontSize: '22px', fontWeight: 800, color: '#2c3e50', marginTop: '2px' }}>
                    {drawnArea}
                    <span style={{ fontSize: '13px', fontWeight: 500, color: '#555', marginLeft: '4px' }}>
                        acres
                    </span>
                </div>
            )}
        </div> */}

                    {/* Header with Close Icon */}
                    <div style={{
                        padding: '12px 16px',
                        background: '#e1faee',
                        borderBottom: '1px solid #c8f0df',
                        flexShrink: 0,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                    }}>
                        <div>
                            <div style={{ fontWeight: 700, fontSize: '14px', color: '#1c8f7d' }}>
                                Drawn Area
                            </div>
                            {drawnArea && (
                                <div style={{ fontSize: '22px', fontWeight: 800, color: '#2c3e50', marginTop: '2px' }}>
                                    {drawnArea}
                                    <span style={{ fontSize: '13px', fontWeight: 500, color: '#555', marginLeft: '4px' }}>
                                        acres
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Close / Clear Icon Button */}
                        <button
                            onClick={handleClearAll2}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                fontSize: '20px',
                                lineHeight: '1',
                                color: '#8da69a',
                                cursor: 'pointer',
                                padding: '4px',
                                marginRight: '-4px',
                                marginTop: '-4px',
                                transition: 'color 0.15s ease',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                            onMouseEnter={e => e.currentTarget.style.color = '#c0392b'}
                            onMouseLeave={e => e.currentTarget.style.color = '#8da69a'}
                            title="Clear and Close"
                        >
                            &times;
                        </button>
                    </div>

                    {/* Parcel list */}
                    <div style={{ overflowY: 'auto', flex: 1, padding: '8px 0' }}>
                        {isLoadingNearby && (
                            <div style={{ padding: '20px', textAlign: 'center', color: '#888', fontSize: '13px' }}>
                                Loading nearby parcels…
                            </div>
                        )}

                        {nearbyError && !isLoadingNearby && (
                            <div style={{ padding: '16px', color: '#c0392b', fontSize: '13px' }}>
                                {nearbyError}
                            </div>
                        )}

                        {!isLoadingNearby && !nearbyError && nearbyParcels.length === 0 && (
                            <div style={{ padding: '20px', textAlign: 'center', color: '#888', fontSize: '13px' }}>
                                No parcels found in this area.
                            </div>
                        )}

                        {!isLoadingNearby && nearbyParcels.map((parcel, idx) => {
                            const pid = parcel.parcel || parcel.parcelid || parcel.geo_id || parcel.id || `#${idx}`;
                            const address = parcel.addrline || parcel.addrline1 || parcel.address || '—';
                            const zone = parcel.zonedcodelocal || parcel.zoning || '—';
                            const size = parcel.parcelsize
                                ? `${parseFloat(parcel.parcelsize).toFixed(2)} ac`
                                : '—';
                            const price = parcel.zestimate
                                ? `$${parseFloat(parcel.zestimate).toLocaleString('en-US')}`
                                : '—';
                            const flood = parcel.parcelcustomfemaflood || '—';

                            const floodColor = {
                                'X': '#4CAF50',
                                'AE': '#FF9800',
                                'VE': '#E91E63',
                                'A': '#2196F3',
                            }[flood] || '#9fa2e7';

                            // return (
                            //     <div
                            //         key={pid}
                            //         onClick={() => handleRowClick(parcel)}
                            //         style={{
                            //             padding: '10px 16px',
                            //             borderBottom: '1px solid #f0f0f0',
                            //             cursor: 'pointer',
                            //             transition: 'background 0.15s',
                            //         }}
                            //         onMouseEnter={e => e.currentTarget.style.background = '#f5fdf9'}
                            //         onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                            //     >
                            //         {/* Parcel ID + flood badge */}
                            //         <div style={{
                            //             display: 'flex',
                            //             justifyContent: 'space-between',
                            //             alignItems: 'center',
                            //             marginBottom: '4px',
                            //         }}>
                            //             <span style={{
                            //                 fontWeight: 600,
                            //                 fontSize: '12px',
                            //                 color: '#1c8f7d',
                            //                 overflow: 'hidden',
                            //                 textOverflow: 'ellipsis',
                            //                 whiteSpace: 'nowrap',
                            //                 maxWidth: '200px',
                            //             }}>
                            //                 {pid}
                            //             </span>
                            //             <span style={{
                            //                 fontSize: '10px',
                            //                 fontWeight: 700,
                            //                 color: '#fff',
                            //                 background: floodColor,
                            //                 borderRadius: '4px',
                            //                 padding: '1px 7px',
                            //                 flexShrink: 0,
                            //             }}>
                            //                 {flood}
                            //             </span>
                            //         </div>

                            //         {/* Address */}
                            //         <div style={{ fontSize: '11px', color: '#555', marginBottom: '3px' }}>
                            //             {address}
                            //         </div>

                            //         {/* Detail row */}
                            //         <div style={{
                            //             display: 'flex',
                            //             gap: '10px',
                            //             fontSize: '11px',
                            //             color: '#777',
                            //         }}>
                            //             <span> {size}</span>
                            //             <span> {zone}</span>
                            //             <span>{price}</span>
                            //         </div>
                            //     </div>
                            // );

                            return (
                                <div
                                    key={pid}
                                    onClick={() => handleRowClick(parcel)}
                                    style={{
                                        padding: '12px 16px',
                                        borderBottom: '1px solid #f0f0f0',
                                        cursor: 'pointer',
                                        transition: 'background 0.15s',
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.background = '#f5fdf9'}
                                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                >
                                    {/* Top Row: Clean Parcel ID */}
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginBottom: '4px',
                                    }}>
                                        <span style={{
                                            fontWeight: 600,
                                            fontSize: '12px',
                                            color: '#1c8f7d',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            maxWidth: '100%',
                                        }}>
                                            Parcel: {pid}
                                        </span>
                                    </div>

                                    {/* Second Row: Address + Normal Flood Zone Text */}
                                    <div style={{
                                        fontSize: '11px',
                                        color: '#555',
                                        marginBottom: '6px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        flexWrap: 'wrap'
                                    }}>
                                        <span>{address}</span>
                                        <span style={{ color: '#ccc' }}>•</span>
                                        <span style={{ color: '#666' }}>
                                            <strong style={{ fontWeight: 600, color: '#555' }}>Flood Zone:</strong> {flood}
                                        </span>
                                    </div>

                                    {/* Third Row: Labeled Content */}
                                    <div style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: '12px',
                                        fontSize: '11px',
                                        color: '#777',
                                    }}>
                                        <span>
                                            <strong style={{ color: '#555' }}>Size:</strong> {size}
                                        </span>
                                        <span>
                                            <strong style={{ color: '#555' }}>Zone Type:</strong> {zone}
                                        </span>
                                        <span>
                                            <strong style={{ color: '#555' }}>Zestimate:</strong> {price}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Footer count */}
                    {!isLoadingNearby && nearbyParcels.length > 0 && (
                        <div style={{
                            padding: '8px 16px',
                            background: '#f9f9f9',
                            borderTop: '1px solid #eee',
                            fontSize: '12px',
                            color: '#888',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '10px',
                        }}>
                            <span>
                                {nearbyParcels.length}
                                {nearbyTotalCount ? ` of ${nearbyTotalCount}` : ''} parcel{nearbyTotalCount === 1 ? '' : 's'} found
                            </span>
                            {nearbyHasMore && (
                                <button
                                    type="button"
                                    onClick={() => nearbyLoadMoreRef.current?.()}
                                    disabled={isLoadingMoreNearby}
                                    style={{
                                        border: '1px solid #1c8f7d',
                                        background: isLoadingMoreNearby ? '#eef6f2' : '#ffffff',
                                        color: '#1c8f7d',
                                        borderRadius: '6px',
                                        padding: '4px 8px',
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        cursor: isLoadingMoreNearby ? 'wait' : 'pointer',
                                        flexShrink: 0,
                                    }}
                                >
                                    {isLoadingMoreNearby ? 'Loading...' : 'Load More'}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}


            {/* {drawnCoordinates && (
                <div style={{
                    position: 'absolute',
                    top: '120px',
                    right: '60px',
                    zIndex: 1000,
                    background: 'white',
                    padding: '10px',
                    borderRadius: '8px',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                    maxWidth: '300px',
                    maxHeight: '400px',
                    overflow: 'auto',
                    fontSize: '12px',
                    fontFamily: 'monospace', paddingTop: '25px',
                    marginTop: '125px',
                }}>

                    {drawnArea && (
                        <div style={{ marginBottom: '12px', fontSize: '14px', borderBottom: '1px solid #eee', paddingBottom: '8px' }}>
                            <strong style={{ fontFamily: 'sans-serif' }}>Calculated Area:</strong>
                            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#2c3e50', marginTop: '2px' }}>
                                {drawnArea} <span style={{ fontSize: '12px', fontWeight: 'normal' }}>Acres</span>
                            </div>
                        </div>
                    )}
                    <strong>Drawn Polygon Coordinates:</strong>
                    <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', marginTop: '5px' }}>{drawnCoordinates}</pre>

                    {drawnParcelIds.length > 0 && (
                        <div style={{ marginTop: '10px', borderTop: '1px solid #eee', paddingTop: '10px' }}>
                            <strong>Intersecting Parcels ({drawnParcelIds.length}):</strong>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '5px' }}>
                                {drawnParcelIds.map(pid => (
                                    <span key={pid} style={{ background: '#f0f0f0', padding: '2px 6px', borderRadius: '4px' }}>{pid}</span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )} */}

            {mapInitError && (
                <div style={{ padding: "16px", color: "red" }}>
                    Map failed to load. Please refresh.
                </div>
            )}
            {/* {selectedCounty !== "all" && (
                <div
                    style={{
                        position: 'absolute',
                        top: '620px',
                        right: '20px',
                        zIndex: 1000,
                     
                        boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                     
                        fontSize: '12px',
                        fontFamily: 'monospace', paddingTop: '25px',
                        marginTop: '155px',
                    }}
                >
                    <button
                        onClick={() => setShowFemaLayer(prev => !prev)}

                        style={{
                            position: 'absolute',
                            top: '20px',
                            right: '20px',
                            zIndex: 1000,
                            padding: '3px 14px',
                            borderRadius: '6px',
                            border: 'none',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            backgroundColor: showFemaLayer ? '#e1faee' : '#ffffff',
                            color: showFemaLayer ? '#1c8f7d' : '#333333',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                       
                        {showFemaLayer ? 'Hide Parcel' : 'Show Parcel'}
                    </button>
                </div>
            )} */}



            {/* <ErrorBoundary> */}
            {/* Map - Tile server data */}
            {/* <div style={{ display: "flex", flexDirection: "column", height: "100vh" }}> */}
            <div
                ref={mapContainerRef}
                style={{
                    position: "absolute",
                    // top: selectedCounty !== "all" ? "160px" : "130px",
                    top: selectedCounty !== "all" ? "190px" : "130px",
                    // left: selectedCounty !== "all" ? "60%" : 0,
                    left: selectedCounty !== "all" ? `${listWidth}%` : 0,
                    right: 0,
                    bottom: 0,
                    minHeight: 300,
                }}
            />
            {/* </div> */}
            {/* </ErrorBoundary> */}
        </>
    );
};

export default NCComponent;
